/**
 * Public entry point for `@sdxc/icons` — Lucide icons (https://lucide.dev) as
 * `remix/component` components, one tree-shakeable export per icon, matching
 * `lucide-react`'s package shape. Generated by `bun run generate`; do not edit by hand.
 *
 * @author [Sergio Xalambrí](https://sergiodxa.com)
 * @copyright Sergio Xalambrí 2026
 */

import { createLucideIcon } from "./create-lucide-icon.js";
import * as registry from "./registry.js";

export { createLucideIcon };
export type { IconNode, LucideProps } from "./create-lucide-icon.js";

export { Icon } from "./icon.js";
export type { IconProps } from "./icon.js";

export { iconExportNames, LUCIDE_VERSION } from "./icon-names.js";
export type { IconName } from "./icon-names.js";

/** Renders the "a-arrow-down" icon from https://lucide.dev/icons/a-arrow-down. */
export const AArrowDownIcon = /*#__PURE__*/ createLucideIcon("a-arrow-down", registry.aArrowDown);

/** Renders the "a-arrow-up" icon from https://lucide.dev/icons/a-arrow-up. */
export const AArrowUpIcon = /*#__PURE__*/ createLucideIcon("a-arrow-up", registry.aArrowUp);

/** Renders the "a-large-small" icon from https://lucide.dev/icons/a-large-small. */
export const ALargeSmallIcon = /*#__PURE__*/ createLucideIcon(
	"a-large-small",
	registry.aLargeSmall,
);

/** Renders the "accessibility" icon from https://lucide.dev/icons/accessibility. */
export const AccessibilityIcon = /*#__PURE__*/ createLucideIcon(
	"accessibility",
	registry.accessibility,
);

/** Renders the "activity" icon from https://lucide.dev/icons/activity. */
export const ActivityIcon = /*#__PURE__*/ createLucideIcon("activity", registry.activity);

/** Renders the "ad" icon from https://lucide.dev/icons/ad. */
export const AdIcon = /*#__PURE__*/ createLucideIcon("ad", registry.ad);

/** Renders the "air-vent" icon from https://lucide.dev/icons/air-vent. */
export const AirVentIcon = /*#__PURE__*/ createLucideIcon("air-vent", registry.airVent);

/** Renders the "airplay" icon from https://lucide.dev/icons/airplay. */
export const AirplayIcon = /*#__PURE__*/ createLucideIcon("airplay", registry.airplay);

/** Renders the "alarm-clock" icon from https://lucide.dev/icons/alarm-clock. */
export const AlarmClockIcon = /*#__PURE__*/ createLucideIcon("alarm-clock", registry.alarmClock);

/** Renders the "alarm-clock-check" icon from https://lucide.dev/icons/alarm-clock-check. */
export const AlarmClockCheckIcon = /*#__PURE__*/ createLucideIcon(
	"alarm-clock-check",
	registry.alarmClockCheck,
);

/** Renders the "alarm-clock-minus" icon from https://lucide.dev/icons/alarm-clock-minus. */
export const AlarmClockMinusIcon = /*#__PURE__*/ createLucideIcon(
	"alarm-clock-minus",
	registry.alarmClockMinus,
);

/** Renders the "alarm-clock-off" icon from https://lucide.dev/icons/alarm-clock-off. */
export const AlarmClockOffIcon = /*#__PURE__*/ createLucideIcon(
	"alarm-clock-off",
	registry.alarmClockOff,
);

/** Renders the "alarm-clock-plus" icon from https://lucide.dev/icons/alarm-clock-plus. */
export const AlarmClockPlusIcon = /*#__PURE__*/ createLucideIcon(
	"alarm-clock-plus",
	registry.alarmClockPlus,
);

/** Renders the "alarm-smoke" icon from https://lucide.dev/icons/alarm-smoke. */
export const AlarmSmokeIcon = /*#__PURE__*/ createLucideIcon("alarm-smoke", registry.alarmSmoke);

/** Renders the "align-center-horizontal" icon from https://lucide.dev/icons/align-center-horizontal. */
export const AlignCenterHorizontalIcon = /*#__PURE__*/ createLucideIcon(
	"align-center-horizontal",
	registry.alignCenterHorizontal,
);

/** Renders the "align-center-vertical" icon from https://lucide.dev/icons/align-center-vertical. */
export const AlignCenterVerticalIcon = /*#__PURE__*/ createLucideIcon(
	"align-center-vertical",
	registry.alignCenterVertical,
);

/** Renders the "align-end-horizontal" icon from https://lucide.dev/icons/align-end-horizontal. */
export const AlignEndHorizontalIcon = /*#__PURE__*/ createLucideIcon(
	"align-end-horizontal",
	registry.alignEndHorizontal,
);

/** Renders the "align-end-vertical" icon from https://lucide.dev/icons/align-end-vertical. */
export const AlignEndVerticalIcon = /*#__PURE__*/ createLucideIcon(
	"align-end-vertical",
	registry.alignEndVertical,
);

/** Renders the "align-horizontal-distribute-center" icon from https://lucide.dev/icons/align-horizontal-distribute-center. */
export const AlignHorizontalDistributeCenterIcon = /*#__PURE__*/ createLucideIcon(
	"align-horizontal-distribute-center",
	registry.alignHorizontalDistributeCenter,
);

/** Renders the "align-horizontal-distribute-end" icon from https://lucide.dev/icons/align-horizontal-distribute-end. */
export const AlignHorizontalDistributeEndIcon = /*#__PURE__*/ createLucideIcon(
	"align-horizontal-distribute-end",
	registry.alignHorizontalDistributeEnd,
);

/** Renders the "align-horizontal-distribute-start" icon from https://lucide.dev/icons/align-horizontal-distribute-start. */
export const AlignHorizontalDistributeStartIcon = /*#__PURE__*/ createLucideIcon(
	"align-horizontal-distribute-start",
	registry.alignHorizontalDistributeStart,
);

/** Renders the "align-horizontal-justify-center" icon from https://lucide.dev/icons/align-horizontal-justify-center. */
export const AlignHorizontalJustifyCenterIcon = /*#__PURE__*/ createLucideIcon(
	"align-horizontal-justify-center",
	registry.alignHorizontalJustifyCenter,
);

/** Renders the "align-horizontal-justify-end" icon from https://lucide.dev/icons/align-horizontal-justify-end. */
export const AlignHorizontalJustifyEndIcon = /*#__PURE__*/ createLucideIcon(
	"align-horizontal-justify-end",
	registry.alignHorizontalJustifyEnd,
);

/** Renders the "align-horizontal-justify-start" icon from https://lucide.dev/icons/align-horizontal-justify-start. */
export const AlignHorizontalJustifyStartIcon = /*#__PURE__*/ createLucideIcon(
	"align-horizontal-justify-start",
	registry.alignHorizontalJustifyStart,
);

/** Renders the "align-horizontal-space-around" icon from https://lucide.dev/icons/align-horizontal-space-around. */
export const AlignHorizontalSpaceAroundIcon = /*#__PURE__*/ createLucideIcon(
	"align-horizontal-space-around",
	registry.alignHorizontalSpaceAround,
);

/** Renders the "align-horizontal-space-between" icon from https://lucide.dev/icons/align-horizontal-space-between. */
export const AlignHorizontalSpaceBetweenIcon = /*#__PURE__*/ createLucideIcon(
	"align-horizontal-space-between",
	registry.alignHorizontalSpaceBetween,
);

/** Renders the "align-start-horizontal" icon from https://lucide.dev/icons/align-start-horizontal. */
export const AlignStartHorizontalIcon = /*#__PURE__*/ createLucideIcon(
	"align-start-horizontal",
	registry.alignStartHorizontal,
);

/** Renders the "align-start-vertical" icon from https://lucide.dev/icons/align-start-vertical. */
export const AlignStartVerticalIcon = /*#__PURE__*/ createLucideIcon(
	"align-start-vertical",
	registry.alignStartVertical,
);

/** Renders the "align-vertical-distribute-center" icon from https://lucide.dev/icons/align-vertical-distribute-center. */
export const AlignVerticalDistributeCenterIcon = /*#__PURE__*/ createLucideIcon(
	"align-vertical-distribute-center",
	registry.alignVerticalDistributeCenter,
);

/** Renders the "align-vertical-distribute-end" icon from https://lucide.dev/icons/align-vertical-distribute-end. */
export const AlignVerticalDistributeEndIcon = /*#__PURE__*/ createLucideIcon(
	"align-vertical-distribute-end",
	registry.alignVerticalDistributeEnd,
);

/** Renders the "align-vertical-distribute-start" icon from https://lucide.dev/icons/align-vertical-distribute-start. */
export const AlignVerticalDistributeStartIcon = /*#__PURE__*/ createLucideIcon(
	"align-vertical-distribute-start",
	registry.alignVerticalDistributeStart,
);

/** Renders the "align-vertical-justify-center" icon from https://lucide.dev/icons/align-vertical-justify-center. */
export const AlignVerticalJustifyCenterIcon = /*#__PURE__*/ createLucideIcon(
	"align-vertical-justify-center",
	registry.alignVerticalJustifyCenter,
);

/** Renders the "align-vertical-justify-end" icon from https://lucide.dev/icons/align-vertical-justify-end. */
export const AlignVerticalJustifyEndIcon = /*#__PURE__*/ createLucideIcon(
	"align-vertical-justify-end",
	registry.alignVerticalJustifyEnd,
);

/** Renders the "align-vertical-justify-start" icon from https://lucide.dev/icons/align-vertical-justify-start. */
export const AlignVerticalJustifyStartIcon = /*#__PURE__*/ createLucideIcon(
	"align-vertical-justify-start",
	registry.alignVerticalJustifyStart,
);

/** Renders the "align-vertical-space-around" icon from https://lucide.dev/icons/align-vertical-space-around. */
export const AlignVerticalSpaceAroundIcon = /*#__PURE__*/ createLucideIcon(
	"align-vertical-space-around",
	registry.alignVerticalSpaceAround,
);

/** Renders the "align-vertical-space-between" icon from https://lucide.dev/icons/align-vertical-space-between. */
export const AlignVerticalSpaceBetweenIcon = /*#__PURE__*/ createLucideIcon(
	"align-vertical-space-between",
	registry.alignVerticalSpaceBetween,
);

/** Renders the "ambulance" icon from https://lucide.dev/icons/ambulance. */
export const AmbulanceIcon = /*#__PURE__*/ createLucideIcon("ambulance", registry.ambulance);

/** Renders the "ampersand" icon from https://lucide.dev/icons/ampersand. */
export const AmpersandIcon = /*#__PURE__*/ createLucideIcon("ampersand", registry.ampersand);

/** Renders the "ampersands" icon from https://lucide.dev/icons/ampersands. */
export const AmpersandsIcon = /*#__PURE__*/ createLucideIcon("ampersands", registry.ampersands);

/** Renders the "amphora" icon from https://lucide.dev/icons/amphora. */
export const AmphoraIcon = /*#__PURE__*/ createLucideIcon("amphora", registry.amphora);

/** Renders the "anchor" icon from https://lucide.dev/icons/anchor. */
export const AnchorIcon = /*#__PURE__*/ createLucideIcon("anchor", registry.anchor);

/** Renders the "angle" icon from https://lucide.dev/icons/angle. */
export const AngleIcon = /*#__PURE__*/ createLucideIcon("angle", registry.angle);

/** Renders the "antenna" icon from https://lucide.dev/icons/antenna. */
export const AntennaIcon = /*#__PURE__*/ createLucideIcon("antenna", registry.antenna);

/** Renders the "anvil" icon from https://lucide.dev/icons/anvil. */
export const AnvilIcon = /*#__PURE__*/ createLucideIcon("anvil", registry.anvil);

/** Renders the "aperture" icon from https://lucide.dev/icons/aperture. */
export const ApertureIcon = /*#__PURE__*/ createLucideIcon("aperture", registry.aperture);

/** Renders the "app-window" icon from https://lucide.dev/icons/app-window. */
export const AppWindowIcon = /*#__PURE__*/ createLucideIcon("app-window", registry.appWindow);

/** Renders the "app-window-mac" icon from https://lucide.dev/icons/app-window-mac. */
export const AppWindowMacIcon = /*#__PURE__*/ createLucideIcon(
	"app-window-mac",
	registry.appWindowMac,
);

/** Renders the "apple" icon from https://lucide.dev/icons/apple. */
export const AppleIcon = /*#__PURE__*/ createLucideIcon("apple", registry.apple);

/** Renders the "archive" icon from https://lucide.dev/icons/archive. */
export const ArchiveIcon = /*#__PURE__*/ createLucideIcon("archive", registry.archive);

/** Renders the "archive-restore" icon from https://lucide.dev/icons/archive-restore. */
export const ArchiveRestoreIcon = /*#__PURE__*/ createLucideIcon(
	"archive-restore",
	registry.archiveRestore,
);

/** Renders the "archive-x" icon from https://lucide.dev/icons/archive-x. */
export const ArchiveXIcon = /*#__PURE__*/ createLucideIcon("archive-x", registry.archiveX);

/** Renders the "armchair" icon from https://lucide.dev/icons/armchair. */
export const ArmchairIcon = /*#__PURE__*/ createLucideIcon("armchair", registry.armchair);

/** Renders the "armenian-dram" icon from https://lucide.dev/icons/armenian-dram. */
export const ArmenianDramIcon = /*#__PURE__*/ createLucideIcon(
	"armenian-dram",
	registry.armenianDram,
);

/** Renders the "arrow-big-down" icon from https://lucide.dev/icons/arrow-big-down. */
export const ArrowBigDownIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-big-down",
	registry.arrowBigDown,
);

/** Renders the "arrow-big-down-dash" icon from https://lucide.dev/icons/arrow-big-down-dash. */
export const ArrowBigDownDashIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-big-down-dash",
	registry.arrowBigDownDash,
);

/** Renders the "arrow-big-left" icon from https://lucide.dev/icons/arrow-big-left. */
export const ArrowBigLeftIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-big-left",
	registry.arrowBigLeft,
);

/** Renders the "arrow-big-left-dash" icon from https://lucide.dev/icons/arrow-big-left-dash. */
export const ArrowBigLeftDashIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-big-left-dash",
	registry.arrowBigLeftDash,
);

/** Renders the "arrow-big-right" icon from https://lucide.dev/icons/arrow-big-right. */
export const ArrowBigRightIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-big-right",
	registry.arrowBigRight,
);

/** Renders the "arrow-big-right-dash" icon from https://lucide.dev/icons/arrow-big-right-dash. */
export const ArrowBigRightDashIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-big-right-dash",
	registry.arrowBigRightDash,
);

/** Renders the "arrow-big-up" icon from https://lucide.dev/icons/arrow-big-up. */
export const ArrowBigUpIcon = /*#__PURE__*/ createLucideIcon("arrow-big-up", registry.arrowBigUp);

/** Renders the "arrow-big-up-dash" icon from https://lucide.dev/icons/arrow-big-up-dash. */
export const ArrowBigUpDashIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-big-up-dash",
	registry.arrowBigUpDash,
);

/** Renders the "arrow-down" icon from https://lucide.dev/icons/arrow-down. */
export const ArrowDownIcon = /*#__PURE__*/ createLucideIcon("arrow-down", registry.arrowDown);

/** Renders the "arrow-down-0-1" icon from https://lucide.dev/icons/arrow-down-0-1. */
export const ArrowDown01Icon = /*#__PURE__*/ createLucideIcon(
	"arrow-down-0-1",
	registry.arrowDown01,
);

/** Renders the "arrow-down-1-0" icon from https://lucide.dev/icons/arrow-down-1-0. */
export const ArrowDown10Icon = /*#__PURE__*/ createLucideIcon(
	"arrow-down-1-0",
	registry.arrowDown10,
);

/** Renders the "arrow-down-a-z" icon from https://lucide.dev/icons/arrow-down-a-z. */
export const ArrowDownAZIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-down-a-z",
	registry.arrowDownAZ,
);

/** Renders the "arrow-down-from-line" icon from https://lucide.dev/icons/arrow-down-from-line. */
export const ArrowDownFromLineIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-down-from-line",
	registry.arrowDownFromLine,
);

/** Renders the "arrow-down-left" icon from https://lucide.dev/icons/arrow-down-left. */
export const ArrowDownLeftIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-down-left",
	registry.arrowDownLeft,
);

/** Renders the "arrow-down-narrow-wide" icon from https://lucide.dev/icons/arrow-down-narrow-wide. */
export const ArrowDownNarrowWideIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-down-narrow-wide",
	registry.arrowDownNarrowWide,
);

/** Renders the "arrow-down-right" icon from https://lucide.dev/icons/arrow-down-right. */
export const ArrowDownRightIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-down-right",
	registry.arrowDownRight,
);

/** Renders the "arrow-down-to-dot" icon from https://lucide.dev/icons/arrow-down-to-dot. */
export const ArrowDownToDotIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-down-to-dot",
	registry.arrowDownToDot,
);

/** Renders the "arrow-down-to-line" icon from https://lucide.dev/icons/arrow-down-to-line. */
export const ArrowDownToLineIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-down-to-line",
	registry.arrowDownToLine,
);

/** Renders the "arrow-down-up" icon from https://lucide.dev/icons/arrow-down-up. */
export const ArrowDownUpIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-down-up",
	registry.arrowDownUp,
);

/** Renders the "arrow-down-wide-narrow" icon from https://lucide.dev/icons/arrow-down-wide-narrow. */
export const ArrowDownWideNarrowIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-down-wide-narrow",
	registry.arrowDownWideNarrow,
);

/** Renders the "arrow-down-z-a" icon from https://lucide.dev/icons/arrow-down-z-a. */
export const ArrowDownZAIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-down-z-a",
	registry.arrowDownZA,
);

/** Renders the "arrow-left" icon from https://lucide.dev/icons/arrow-left. */
export const ArrowLeftIcon = /*#__PURE__*/ createLucideIcon("arrow-left", registry.arrowLeft);

/** Renders the "arrow-left-from-line" icon from https://lucide.dev/icons/arrow-left-from-line. */
export const ArrowLeftFromLineIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-left-from-line",
	registry.arrowLeftFromLine,
);

/** Renders the "arrow-left-right" icon from https://lucide.dev/icons/arrow-left-right. */
export const ArrowLeftRightIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-left-right",
	registry.arrowLeftRight,
);

/** Renders the "arrow-left-to-line" icon from https://lucide.dev/icons/arrow-left-to-line. */
export const ArrowLeftToLineIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-left-to-line",
	registry.arrowLeftToLine,
);

/** Renders the "arrow-right" icon from https://lucide.dev/icons/arrow-right. */
export const ArrowRightIcon = /*#__PURE__*/ createLucideIcon("arrow-right", registry.arrowRight);

/** Renders the "arrow-right-from-line" icon from https://lucide.dev/icons/arrow-right-from-line. */
export const ArrowRightFromLineIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-right-from-line",
	registry.arrowRightFromLine,
);

/** Renders the "arrow-right-left" icon from https://lucide.dev/icons/arrow-right-left. */
export const ArrowRightLeftIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-right-left",
	registry.arrowRightLeft,
);

/** Renders the "arrow-right-to-line" icon from https://lucide.dev/icons/arrow-right-to-line. */
export const ArrowRightToLineIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-right-to-line",
	registry.arrowRightToLine,
);

/** Renders the "arrow-up" icon from https://lucide.dev/icons/arrow-up. */
export const ArrowUpIcon = /*#__PURE__*/ createLucideIcon("arrow-up", registry.arrowUp);

/** Renders the "arrow-up-0-1" icon from https://lucide.dev/icons/arrow-up-0-1. */
export const ArrowUp01Icon = /*#__PURE__*/ createLucideIcon("arrow-up-0-1", registry.arrowUp01);

/** Renders the "arrow-up-1-0" icon from https://lucide.dev/icons/arrow-up-1-0. */
export const ArrowUp10Icon = /*#__PURE__*/ createLucideIcon("arrow-up-1-0", registry.arrowUp10);

/** Renders the "arrow-up-a-z" icon from https://lucide.dev/icons/arrow-up-a-z. */
export const ArrowUpAZIcon = /*#__PURE__*/ createLucideIcon("arrow-up-a-z", registry.arrowUpAZ);

/** Renders the "arrow-up-down" icon from https://lucide.dev/icons/arrow-up-down. */
export const ArrowUpDownIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-up-down",
	registry.arrowUpDown,
);

/** Renders the "arrow-up-from-dot" icon from https://lucide.dev/icons/arrow-up-from-dot. */
export const ArrowUpFromDotIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-up-from-dot",
	registry.arrowUpFromDot,
);

/** Renders the "arrow-up-from-line" icon from https://lucide.dev/icons/arrow-up-from-line. */
export const ArrowUpFromLineIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-up-from-line",
	registry.arrowUpFromLine,
);

/** Renders the "arrow-up-left" icon from https://lucide.dev/icons/arrow-up-left. */
export const ArrowUpLeftIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-up-left",
	registry.arrowUpLeft,
);

/** Renders the "arrow-up-narrow-wide" icon from https://lucide.dev/icons/arrow-up-narrow-wide. */
export const ArrowUpNarrowWideIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-up-narrow-wide",
	registry.arrowUpNarrowWide,
);

/** Renders the "arrow-up-right" icon from https://lucide.dev/icons/arrow-up-right. */
export const ArrowUpRightIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-up-right",
	registry.arrowUpRight,
);

/** Renders the "arrow-up-to-line" icon from https://lucide.dev/icons/arrow-up-to-line. */
export const ArrowUpToLineIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-up-to-line",
	registry.arrowUpToLine,
);

/** Renders the "arrow-up-wide-narrow" icon from https://lucide.dev/icons/arrow-up-wide-narrow. */
export const ArrowUpWideNarrowIcon = /*#__PURE__*/ createLucideIcon(
	"arrow-up-wide-narrow",
	registry.arrowUpWideNarrow,
);

/** Renders the "arrow-up-z-a" icon from https://lucide.dev/icons/arrow-up-z-a. */
export const ArrowUpZAIcon = /*#__PURE__*/ createLucideIcon("arrow-up-z-a", registry.arrowUpZA);

/** Renders the "arrows-up-from-line" icon from https://lucide.dev/icons/arrows-up-from-line. */
export const ArrowsUpFromLineIcon = /*#__PURE__*/ createLucideIcon(
	"arrows-up-from-line",
	registry.arrowsUpFromLine,
);

/** Renders the "asterisk" icon from https://lucide.dev/icons/asterisk. */
export const AsteriskIcon = /*#__PURE__*/ createLucideIcon("asterisk", registry.asterisk);

/** Renders the "astroid" icon from https://lucide.dev/icons/astroid. */
export const AstroidIcon = /*#__PURE__*/ createLucideIcon("astroid", registry.astroid);

/** Renders the "at-sign" icon from https://lucide.dev/icons/at-sign. */
export const AtSignIcon = /*#__PURE__*/ createLucideIcon("at-sign", registry.atSign);

/** Renders the "atom" icon from https://lucide.dev/icons/atom. */
export const AtomIcon = /*#__PURE__*/ createLucideIcon("atom", registry.atom);

/** Renders the "audio-lines" icon from https://lucide.dev/icons/audio-lines. */
export const AudioLinesIcon = /*#__PURE__*/ createLucideIcon("audio-lines", registry.audioLines);

/** Renders the "audio-lines-off" icon from https://lucide.dev/icons/audio-lines-off. */
export const AudioLinesOffIcon = /*#__PURE__*/ createLucideIcon(
	"audio-lines-off",
	registry.audioLinesOff,
);

/** Renders the "audio-lines-x" icon from https://lucide.dev/icons/audio-lines-x. */
export const AudioLinesXIcon = /*#__PURE__*/ createLucideIcon(
	"audio-lines-x",
	registry.audioLinesX,
);

/** Renders the "audio-waveform" icon from https://lucide.dev/icons/audio-waveform. */
export const AudioWaveformIcon = /*#__PURE__*/ createLucideIcon(
	"audio-waveform",
	registry.audioWaveform,
);

/** Renders the "avocado" icon from https://lucide.dev/icons/avocado. */
export const AvocadoIcon = /*#__PURE__*/ createLucideIcon("avocado", registry.avocado);

/** Renders the "award" icon from https://lucide.dev/icons/award. */
export const AwardIcon = /*#__PURE__*/ createLucideIcon("award", registry.award);

/** Renders the "axe" icon from https://lucide.dev/icons/axe. */
export const AxeIcon = /*#__PURE__*/ createLucideIcon("axe", registry.axe);

/** Renders the "axis-3d" icon from https://lucide.dev/icons/axis-3d. */
export const Axis3dIcon = /*#__PURE__*/ createLucideIcon("axis-3d", registry.axis3d);

/** Renders the "baby" icon from https://lucide.dev/icons/baby. */
export const BabyIcon = /*#__PURE__*/ createLucideIcon("baby", registry.baby);

/** Renders the "backpack" icon from https://lucide.dev/icons/backpack. */
export const BackpackIcon = /*#__PURE__*/ createLucideIcon("backpack", registry.backpack);

/** Renders the "bacon" icon from https://lucide.dev/icons/bacon. */
export const BaconIcon = /*#__PURE__*/ createLucideIcon("bacon", registry.bacon);

/** Renders the "badge" icon from https://lucide.dev/icons/badge. */
export const BadgeIcon = /*#__PURE__*/ createLucideIcon("badge", registry.badge);

/** Renders the "badge-alert" icon from https://lucide.dev/icons/badge-alert. */
export const BadgeAlertIcon = /*#__PURE__*/ createLucideIcon("badge-alert", registry.badgeAlert);

/** Renders the "badge-cent" icon from https://lucide.dev/icons/badge-cent. */
export const BadgeCentIcon = /*#__PURE__*/ createLucideIcon("badge-cent", registry.badgeCent);

/** Renders the "badge-check" icon from https://lucide.dev/icons/badge-check. */
export const BadgeCheckIcon = /*#__PURE__*/ createLucideIcon("badge-check", registry.badgeCheck);

/** Renders the "badge-dollar-sign" icon from https://lucide.dev/icons/badge-dollar-sign. */
export const BadgeDollarSignIcon = /*#__PURE__*/ createLucideIcon(
	"badge-dollar-sign",
	registry.badgeDollarSign,
);

/** Renders the "badge-euro" icon from https://lucide.dev/icons/badge-euro. */
export const BadgeEuroIcon = /*#__PURE__*/ createLucideIcon("badge-euro", registry.badgeEuro);

/** Renders the "badge-indian-rupee" icon from https://lucide.dev/icons/badge-indian-rupee. */
export const BadgeIndianRupeeIcon = /*#__PURE__*/ createLucideIcon(
	"badge-indian-rupee",
	registry.badgeIndianRupee,
);

/** Renders the "badge-info" icon from https://lucide.dev/icons/badge-info. */
export const BadgeInfoIcon = /*#__PURE__*/ createLucideIcon("badge-info", registry.badgeInfo);

/** Renders the "badge-japanese-yen" icon from https://lucide.dev/icons/badge-japanese-yen. */
export const BadgeJapaneseYenIcon = /*#__PURE__*/ createLucideIcon(
	"badge-japanese-yen",
	registry.badgeJapaneseYen,
);

/** Renders the "badge-minus" icon from https://lucide.dev/icons/badge-minus. */
export const BadgeMinusIcon = /*#__PURE__*/ createLucideIcon("badge-minus", registry.badgeMinus);

/** Renders the "badge-percent" icon from https://lucide.dev/icons/badge-percent. */
export const BadgePercentIcon = /*#__PURE__*/ createLucideIcon(
	"badge-percent",
	registry.badgePercent,
);

/** Renders the "badge-plus" icon from https://lucide.dev/icons/badge-plus. */
export const BadgePlusIcon = /*#__PURE__*/ createLucideIcon("badge-plus", registry.badgePlus);

/** Renders the "badge-pound-sterling" icon from https://lucide.dev/icons/badge-pound-sterling. */
export const BadgePoundSterlingIcon = /*#__PURE__*/ createLucideIcon(
	"badge-pound-sterling",
	registry.badgePoundSterling,
);

/** Renders the "badge-question-mark" icon from https://lucide.dev/icons/badge-question-mark. */
export const BadgeQuestionMarkIcon = /*#__PURE__*/ createLucideIcon(
	"badge-question-mark",
	registry.badgeQuestionMark,
);

/** Renders the "badge-russian-ruble" icon from https://lucide.dev/icons/badge-russian-ruble. */
export const BadgeRussianRubleIcon = /*#__PURE__*/ createLucideIcon(
	"badge-russian-ruble",
	registry.badgeRussianRuble,
);

/** Renders the "badge-swiss-franc" icon from https://lucide.dev/icons/badge-swiss-franc. */
export const BadgeSwissFrancIcon = /*#__PURE__*/ createLucideIcon(
	"badge-swiss-franc",
	registry.badgeSwissFranc,
);

/** Renders the "badge-turkish-lira" icon from https://lucide.dev/icons/badge-turkish-lira. */
export const BadgeTurkishLiraIcon = /*#__PURE__*/ createLucideIcon(
	"badge-turkish-lira",
	registry.badgeTurkishLira,
);

/** Renders the "badge-x" icon from https://lucide.dev/icons/badge-x. */
export const BadgeXIcon = /*#__PURE__*/ createLucideIcon("badge-x", registry.badgeX);

/** Renders the "baggage-claim" icon from https://lucide.dev/icons/baggage-claim. */
export const BaggageClaimIcon = /*#__PURE__*/ createLucideIcon(
	"baggage-claim",
	registry.baggageClaim,
);

/** Renders the "balloon" icon from https://lucide.dev/icons/balloon. */
export const BalloonIcon = /*#__PURE__*/ createLucideIcon("balloon", registry.balloon);

/** Renders the "ban" icon from https://lucide.dev/icons/ban. */
export const BanIcon = /*#__PURE__*/ createLucideIcon("ban", registry.ban);

/** Renders the "banana" icon from https://lucide.dev/icons/banana. */
export const BananaIcon = /*#__PURE__*/ createLucideIcon("banana", registry.banana);

/** Renders the "bandage" icon from https://lucide.dev/icons/bandage. */
export const BandageIcon = /*#__PURE__*/ createLucideIcon("bandage", registry.bandage);

/** Renders the "bangladeshi-taka" icon from https://lucide.dev/icons/bangladeshi-taka. */
export const BangladeshiTakaIcon = /*#__PURE__*/ createLucideIcon(
	"bangladeshi-taka",
	registry.bangladeshiTaka,
);

/** Renders the "banknote" icon from https://lucide.dev/icons/banknote. */
export const BanknoteIcon = /*#__PURE__*/ createLucideIcon("banknote", registry.banknote);

/** Renders the "banknote-arrow-down" icon from https://lucide.dev/icons/banknote-arrow-down. */
export const BanknoteArrowDownIcon = /*#__PURE__*/ createLucideIcon(
	"banknote-arrow-down",
	registry.banknoteArrowDown,
);

/** Renders the "banknote-arrow-up" icon from https://lucide.dev/icons/banknote-arrow-up. */
export const BanknoteArrowUpIcon = /*#__PURE__*/ createLucideIcon(
	"banknote-arrow-up",
	registry.banknoteArrowUp,
);

/** Renders the "banknote-check" icon from https://lucide.dev/icons/banknote-check. */
export const BanknoteCheckIcon = /*#__PURE__*/ createLucideIcon(
	"banknote-check",
	registry.banknoteCheck,
);

/** Renders the "banknote-x" icon from https://lucide.dev/icons/banknote-x. */
export const BanknoteXIcon = /*#__PURE__*/ createLucideIcon("banknote-x", registry.banknoteX);

/** Renders the "barcode" icon from https://lucide.dev/icons/barcode. */
export const BarcodeIcon = /*#__PURE__*/ createLucideIcon("barcode", registry.barcode);

/** Renders the "barrel" icon from https://lucide.dev/icons/barrel. */
export const BarrelIcon = /*#__PURE__*/ createLucideIcon("barrel", registry.barrel);

/** Renders the "baseline" icon from https://lucide.dev/icons/baseline. */
export const BaselineIcon = /*#__PURE__*/ createLucideIcon("baseline", registry.baseline);

/** Renders the "bath" icon from https://lucide.dev/icons/bath. */
export const BathIcon = /*#__PURE__*/ createLucideIcon("bath", registry.bath);

/** Renders the "battery" icon from https://lucide.dev/icons/battery. */
export const BatteryIcon = /*#__PURE__*/ createLucideIcon("battery", registry.battery);

/** Renders the "battery-charging" icon from https://lucide.dev/icons/battery-charging. */
export const BatteryChargingIcon = /*#__PURE__*/ createLucideIcon(
	"battery-charging",
	registry.batteryCharging,
);

/** Renders the "battery-full" icon from https://lucide.dev/icons/battery-full. */
export const BatteryFullIcon = /*#__PURE__*/ createLucideIcon("battery-full", registry.batteryFull);

/** Renders the "battery-low" icon from https://lucide.dev/icons/battery-low. */
export const BatteryLowIcon = /*#__PURE__*/ createLucideIcon("battery-low", registry.batteryLow);

/** Renders the "battery-medium" icon from https://lucide.dev/icons/battery-medium. */
export const BatteryMediumIcon = /*#__PURE__*/ createLucideIcon(
	"battery-medium",
	registry.batteryMedium,
);

/** Renders the "battery-plus" icon from https://lucide.dev/icons/battery-plus. */
export const BatteryPlusIcon = /*#__PURE__*/ createLucideIcon("battery-plus", registry.batteryPlus);

/** Renders the "battery-warning" icon from https://lucide.dev/icons/battery-warning. */
export const BatteryWarningIcon = /*#__PURE__*/ createLucideIcon(
	"battery-warning",
	registry.batteryWarning,
);

/** Renders the "beaker" icon from https://lucide.dev/icons/beaker. */
export const BeakerIcon = /*#__PURE__*/ createLucideIcon("beaker", registry.beaker);

/** Renders the "bean" icon from https://lucide.dev/icons/bean. */
export const BeanIcon = /*#__PURE__*/ createLucideIcon("bean", registry.bean);

/** Renders the "bean-off" icon from https://lucide.dev/icons/bean-off. */
export const BeanOffIcon = /*#__PURE__*/ createLucideIcon("bean-off", registry.beanOff);

/** Renders the "bed" icon from https://lucide.dev/icons/bed. */
export const BedIcon = /*#__PURE__*/ createLucideIcon("bed", registry.bed);

/** Renders the "bed-double" icon from https://lucide.dev/icons/bed-double. */
export const BedDoubleIcon = /*#__PURE__*/ createLucideIcon("bed-double", registry.bedDouble);

/** Renders the "bed-single" icon from https://lucide.dev/icons/bed-single. */
export const BedSingleIcon = /*#__PURE__*/ createLucideIcon("bed-single", registry.bedSingle);

/** Renders the "beef" icon from https://lucide.dev/icons/beef. */
export const BeefIcon = /*#__PURE__*/ createLucideIcon("beef", registry.beef);

/** Renders the "beef-off" icon from https://lucide.dev/icons/beef-off. */
export const BeefOffIcon = /*#__PURE__*/ createLucideIcon("beef-off", registry.beefOff);

/** Renders the "beer" icon from https://lucide.dev/icons/beer. */
export const BeerIcon = /*#__PURE__*/ createLucideIcon("beer", registry.beer);

/** Renders the "beer-off" icon from https://lucide.dev/icons/beer-off. */
export const BeerOffIcon = /*#__PURE__*/ createLucideIcon("beer-off", registry.beerOff);

/** Renders the "belarusian-ruble" icon from https://lucide.dev/icons/belarusian-ruble. */
export const BelarusianRubleIcon = /*#__PURE__*/ createLucideIcon(
	"belarusian-ruble",
	registry.belarusianRuble,
);

/** Renders the "bell" icon from https://lucide.dev/icons/bell. */
export const BellIcon = /*#__PURE__*/ createLucideIcon("bell", registry.bell);

/** Renders the "bell-check" icon from https://lucide.dev/icons/bell-check. */
export const BellCheckIcon = /*#__PURE__*/ createLucideIcon("bell-check", registry.bellCheck);

/** Renders the "bell-dot" icon from https://lucide.dev/icons/bell-dot. */
export const BellDotIcon = /*#__PURE__*/ createLucideIcon("bell-dot", registry.bellDot);

/** Renders the "bell-electric" icon from https://lucide.dev/icons/bell-electric. */
export const BellElectricIcon = /*#__PURE__*/ createLucideIcon(
	"bell-electric",
	registry.bellElectric,
);

/** Renders the "bell-minus" icon from https://lucide.dev/icons/bell-minus. */
export const BellMinusIcon = /*#__PURE__*/ createLucideIcon("bell-minus", registry.bellMinus);

/** Renders the "bell-off" icon from https://lucide.dev/icons/bell-off. */
export const BellOffIcon = /*#__PURE__*/ createLucideIcon("bell-off", registry.bellOff);

/** Renders the "bell-plus" icon from https://lucide.dev/icons/bell-plus. */
export const BellPlusIcon = /*#__PURE__*/ createLucideIcon("bell-plus", registry.bellPlus);

/** Renders the "bell-ring" icon from https://lucide.dev/icons/bell-ring. */
export const BellRingIcon = /*#__PURE__*/ createLucideIcon("bell-ring", registry.bellRing);

/** Renders the "between-horizontal-end" icon from https://lucide.dev/icons/between-horizontal-end. */
export const BetweenHorizontalEndIcon = /*#__PURE__*/ createLucideIcon(
	"between-horizontal-end",
	registry.betweenHorizontalEnd,
);

/** Renders the "between-horizontal-start" icon from https://lucide.dev/icons/between-horizontal-start. */
export const BetweenHorizontalStartIcon = /*#__PURE__*/ createLucideIcon(
	"between-horizontal-start",
	registry.betweenHorizontalStart,
);

/** Renders the "between-vertical-end" icon from https://lucide.dev/icons/between-vertical-end. */
export const BetweenVerticalEndIcon = /*#__PURE__*/ createLucideIcon(
	"between-vertical-end",
	registry.betweenVerticalEnd,
);

/** Renders the "between-vertical-start" icon from https://lucide.dev/icons/between-vertical-start. */
export const BetweenVerticalStartIcon = /*#__PURE__*/ createLucideIcon(
	"between-vertical-start",
	registry.betweenVerticalStart,
);

/** Renders the "biceps-flexed" icon from https://lucide.dev/icons/biceps-flexed. */
export const BicepsFlexedIcon = /*#__PURE__*/ createLucideIcon(
	"biceps-flexed",
	registry.bicepsFlexed,
);

/** Renders the "bike" icon from https://lucide.dev/icons/bike. */
export const BikeIcon = /*#__PURE__*/ createLucideIcon("bike", registry.bike);

/** Renders the "binary" icon from https://lucide.dev/icons/binary. */
export const BinaryIcon = /*#__PURE__*/ createLucideIcon("binary", registry.binary);

/** Renders the "binoculars" icon from https://lucide.dev/icons/binoculars. */
export const BinocularsIcon = /*#__PURE__*/ createLucideIcon("binoculars", registry.binoculars);

/** Renders the "biohazard" icon from https://lucide.dev/icons/biohazard. */
export const BiohazardIcon = /*#__PURE__*/ createLucideIcon("biohazard", registry.biohazard);

/** Renders the "bird" icon from https://lucide.dev/icons/bird. */
export const BirdIcon = /*#__PURE__*/ createLucideIcon("bird", registry.bird);

/** Renders the "birdhouse" icon from https://lucide.dev/icons/birdhouse. */
export const BirdhouseIcon = /*#__PURE__*/ createLucideIcon("birdhouse", registry.birdhouse);

/** Renders the "bitcoin" icon from https://lucide.dev/icons/bitcoin. */
export const BitcoinIcon = /*#__PURE__*/ createLucideIcon("bitcoin", registry.bitcoin);

/** Renders the "blend" icon from https://lucide.dev/icons/blend. */
export const BlendIcon = /*#__PURE__*/ createLucideIcon("blend", registry.blend);

/** Renders the "blender" icon from https://lucide.dev/icons/blender. */
export const BlenderIcon = /*#__PURE__*/ createLucideIcon("blender", registry.blender);

/** Renders the "blinds" icon from https://lucide.dev/icons/blinds. */
export const BlindsIcon = /*#__PURE__*/ createLucideIcon("blinds", registry.blinds);

/** Renders the "blocks" icon from https://lucide.dev/icons/blocks. */
export const BlocksIcon = /*#__PURE__*/ createLucideIcon("blocks", registry.blocks);

/** Renders the "bluetooth" icon from https://lucide.dev/icons/bluetooth. */
export const BluetoothIcon = /*#__PURE__*/ createLucideIcon("bluetooth", registry.bluetooth);

/** Renders the "bluetooth-connected" icon from https://lucide.dev/icons/bluetooth-connected. */
export const BluetoothConnectedIcon = /*#__PURE__*/ createLucideIcon(
	"bluetooth-connected",
	registry.bluetoothConnected,
);

/** Renders the "bluetooth-off" icon from https://lucide.dev/icons/bluetooth-off. */
export const BluetoothOffIcon = /*#__PURE__*/ createLucideIcon(
	"bluetooth-off",
	registry.bluetoothOff,
);

/** Renders the "bluetooth-searching" icon from https://lucide.dev/icons/bluetooth-searching. */
export const BluetoothSearchingIcon = /*#__PURE__*/ createLucideIcon(
	"bluetooth-searching",
	registry.bluetoothSearching,
);

/** Renders the "bold" icon from https://lucide.dev/icons/bold. */
export const BoldIcon = /*#__PURE__*/ createLucideIcon("bold", registry.bold);

/** Renders the "bolt" icon from https://lucide.dev/icons/bolt. */
export const BoltIcon = /*#__PURE__*/ createLucideIcon("bolt", registry.bolt);

/** Renders the "bomb" icon from https://lucide.dev/icons/bomb. */
export const BombIcon = /*#__PURE__*/ createLucideIcon("bomb", registry.bomb);

/** Renders the "bone" icon from https://lucide.dev/icons/bone. */
export const BoneIcon = /*#__PURE__*/ createLucideIcon("bone", registry.bone);

/** Renders the "bone-fracture" icon from https://lucide.dev/icons/bone-fracture. */
export const BoneFractureIcon = /*#__PURE__*/ createLucideIcon(
	"bone-fracture",
	registry.boneFracture,
);

/** Renders the "book" icon from https://lucide.dev/icons/book. */
export const BookIcon = /*#__PURE__*/ createLucideIcon("book", registry.book);

/** Renders the "book-a" icon from https://lucide.dev/icons/book-a. */
export const BookAIcon = /*#__PURE__*/ createLucideIcon("book-a", registry.bookA);

/** Renders the "book-alert" icon from https://lucide.dev/icons/book-alert. */
export const BookAlertIcon = /*#__PURE__*/ createLucideIcon("book-alert", registry.bookAlert);

/** Renders the "book-audio" icon from https://lucide.dev/icons/book-audio. */
export const BookAudioIcon = /*#__PURE__*/ createLucideIcon("book-audio", registry.bookAudio);

/** Renders the "book-bookmark" icon from https://lucide.dev/icons/book-bookmark. */
export const BookBookmarkIcon = /*#__PURE__*/ createLucideIcon(
	"book-bookmark",
	registry.bookBookmark,
);

/** Renders the "book-check" icon from https://lucide.dev/icons/book-check. */
export const BookCheckIcon = /*#__PURE__*/ createLucideIcon("book-check", registry.bookCheck);

/** Renders the "book-copy" icon from https://lucide.dev/icons/book-copy. */
export const BookCopyIcon = /*#__PURE__*/ createLucideIcon("book-copy", registry.bookCopy);

/** Renders the "book-dashed" icon from https://lucide.dev/icons/book-dashed. */
export const BookDashedIcon = /*#__PURE__*/ createLucideIcon("book-dashed", registry.bookDashed);

/** Renders the "book-down" icon from https://lucide.dev/icons/book-down. */
export const BookDownIcon = /*#__PURE__*/ createLucideIcon("book-down", registry.bookDown);

/** Renders the "book-headphones" icon from https://lucide.dev/icons/book-headphones. */
export const BookHeadphonesIcon = /*#__PURE__*/ createLucideIcon(
	"book-headphones",
	registry.bookHeadphones,
);

/** Renders the "book-heart" icon from https://lucide.dev/icons/book-heart. */
export const BookHeartIcon = /*#__PURE__*/ createLucideIcon("book-heart", registry.bookHeart);

/** Renders the "book-image" icon from https://lucide.dev/icons/book-image. */
export const BookImageIcon = /*#__PURE__*/ createLucideIcon("book-image", registry.bookImage);

/** Renders the "book-key" icon from https://lucide.dev/icons/book-key. */
export const BookKeyIcon = /*#__PURE__*/ createLucideIcon("book-key", registry.bookKey);

/** Renders the "book-lock" icon from https://lucide.dev/icons/book-lock. */
export const BookLockIcon = /*#__PURE__*/ createLucideIcon("book-lock", registry.bookLock);

/** Renders the "book-minus" icon from https://lucide.dev/icons/book-minus. */
export const BookMinusIcon = /*#__PURE__*/ createLucideIcon("book-minus", registry.bookMinus);

/** Renders the "book-open" icon from https://lucide.dev/icons/book-open. */
export const BookOpenIcon = /*#__PURE__*/ createLucideIcon("book-open", registry.bookOpen);

/** Renders the "book-open-check" icon from https://lucide.dev/icons/book-open-check. */
export const BookOpenCheckIcon = /*#__PURE__*/ createLucideIcon(
	"book-open-check",
	registry.bookOpenCheck,
);

/** Renders the "book-open-text" icon from https://lucide.dev/icons/book-open-text. */
export const BookOpenTextIcon = /*#__PURE__*/ createLucideIcon(
	"book-open-text",
	registry.bookOpenText,
);

/** Renders the "book-plus" icon from https://lucide.dev/icons/book-plus. */
export const BookPlusIcon = /*#__PURE__*/ createLucideIcon("book-plus", registry.bookPlus);

/** Renders the "book-search" icon from https://lucide.dev/icons/book-search. */
export const BookSearchIcon = /*#__PURE__*/ createLucideIcon("book-search", registry.bookSearch);

/** Renders the "book-text" icon from https://lucide.dev/icons/book-text. */
export const BookTextIcon = /*#__PURE__*/ createLucideIcon("book-text", registry.bookText);

/** Renders the "book-type" icon from https://lucide.dev/icons/book-type. */
export const BookTypeIcon = /*#__PURE__*/ createLucideIcon("book-type", registry.bookType);

/** Renders the "book-up" icon from https://lucide.dev/icons/book-up. */
export const BookUpIcon = /*#__PURE__*/ createLucideIcon("book-up", registry.bookUp);

/** Renders the "book-up-2" icon from https://lucide.dev/icons/book-up-2. */
export const BookUp2Icon = /*#__PURE__*/ createLucideIcon("book-up-2", registry.bookUp2);

/** Renders the "book-user" icon from https://lucide.dev/icons/book-user. */
export const BookUserIcon = /*#__PURE__*/ createLucideIcon("book-user", registry.bookUser);

/** Renders the "book-x" icon from https://lucide.dev/icons/book-x. */
export const BookXIcon = /*#__PURE__*/ createLucideIcon("book-x", registry.bookX);

/** Renders the "bookmark" icon from https://lucide.dev/icons/bookmark. */
export const BookmarkIcon = /*#__PURE__*/ createLucideIcon("bookmark", registry.bookmark);

/** Renders the "bookmark-check" icon from https://lucide.dev/icons/bookmark-check. */
export const BookmarkCheckIcon = /*#__PURE__*/ createLucideIcon(
	"bookmark-check",
	registry.bookmarkCheck,
);

/** Renders the "bookmark-minus" icon from https://lucide.dev/icons/bookmark-minus. */
export const BookmarkMinusIcon = /*#__PURE__*/ createLucideIcon(
	"bookmark-minus",
	registry.bookmarkMinus,
);

/** Renders the "bookmark-off" icon from https://lucide.dev/icons/bookmark-off. */
export const BookmarkOffIcon = /*#__PURE__*/ createLucideIcon("bookmark-off", registry.bookmarkOff);

/** Renders the "bookmark-plus" icon from https://lucide.dev/icons/bookmark-plus. */
export const BookmarkPlusIcon = /*#__PURE__*/ createLucideIcon(
	"bookmark-plus",
	registry.bookmarkPlus,
);

/** Renders the "bookmark-x" icon from https://lucide.dev/icons/bookmark-x. */
export const BookmarkXIcon = /*#__PURE__*/ createLucideIcon("bookmark-x", registry.bookmarkX);

/** Renders the "boom-box" icon from https://lucide.dev/icons/boom-box. */
export const BoomBoxIcon = /*#__PURE__*/ createLucideIcon("boom-box", registry.boomBox);

/** Renders the "bot" icon from https://lucide.dev/icons/bot. */
export const BotIcon = /*#__PURE__*/ createLucideIcon("bot", registry.bot);

/** Renders the "bot-message-square" icon from https://lucide.dev/icons/bot-message-square. */
export const BotMessageSquareIcon = /*#__PURE__*/ createLucideIcon(
	"bot-message-square",
	registry.botMessageSquare,
);

/** Renders the "bot-off" icon from https://lucide.dev/icons/bot-off. */
export const BotOffIcon = /*#__PURE__*/ createLucideIcon("bot-off", registry.botOff);

/** Renders the "bottle-wine" icon from https://lucide.dev/icons/bottle-wine. */
export const BottleWineIcon = /*#__PURE__*/ createLucideIcon("bottle-wine", registry.bottleWine);

/** Renders the "bow-arrow" icon from https://lucide.dev/icons/bow-arrow. */
export const BowArrowIcon = /*#__PURE__*/ createLucideIcon("bow-arrow", registry.bowArrow);

/** Renders the "box" icon from https://lucide.dev/icons/box. */
export const BoxIcon = /*#__PURE__*/ createLucideIcon("box", registry.box);

/** Renders the "boxes" icon from https://lucide.dev/icons/boxes. */
export const BoxesIcon = /*#__PURE__*/ createLucideIcon("boxes", registry.boxes);

/** Renders the "braces" icon from https://lucide.dev/icons/braces. */
export const BracesIcon = /*#__PURE__*/ createLucideIcon("braces", registry.braces);

/** Renders the "brackets" icon from https://lucide.dev/icons/brackets. */
export const BracketsIcon = /*#__PURE__*/ createLucideIcon("brackets", registry.brackets);

/** Renders the "brain" icon from https://lucide.dev/icons/brain. */
export const BrainIcon = /*#__PURE__*/ createLucideIcon("brain", registry.brain);

/** Renders the "brain-circuit" icon from https://lucide.dev/icons/brain-circuit. */
export const BrainCircuitIcon = /*#__PURE__*/ createLucideIcon(
	"brain-circuit",
	registry.brainCircuit,
);

/** Renders the "brain-cog" icon from https://lucide.dev/icons/brain-cog. */
export const BrainCogIcon = /*#__PURE__*/ createLucideIcon("brain-cog", registry.brainCog);

/** Renders the "brain-off" icon from https://lucide.dev/icons/brain-off. */
export const BrainOffIcon = /*#__PURE__*/ createLucideIcon("brain-off", registry.brainOff);

/** Renders the "brick-wall" icon from https://lucide.dev/icons/brick-wall. */
export const BrickWallIcon = /*#__PURE__*/ createLucideIcon("brick-wall", registry.brickWall);

/** Renders the "brick-wall-fire" icon from https://lucide.dev/icons/brick-wall-fire. */
export const BrickWallFireIcon = /*#__PURE__*/ createLucideIcon(
	"brick-wall-fire",
	registry.brickWallFire,
);

/** Renders the "brick-wall-shield" icon from https://lucide.dev/icons/brick-wall-shield. */
export const BrickWallShieldIcon = /*#__PURE__*/ createLucideIcon(
	"brick-wall-shield",
	registry.brickWallShield,
);

/** Renders the "bridge" icon from https://lucide.dev/icons/bridge. */
export const BridgeIcon = /*#__PURE__*/ createLucideIcon("bridge", registry.bridge);

/** Renders the "briefcase" icon from https://lucide.dev/icons/briefcase. */
export const BriefcaseIcon = /*#__PURE__*/ createLucideIcon("briefcase", registry.briefcase);

/** Renders the "briefcase-business" icon from https://lucide.dev/icons/briefcase-business. */
export const BriefcaseBusinessIcon = /*#__PURE__*/ createLucideIcon(
	"briefcase-business",
	registry.briefcaseBusiness,
);

/** Renders the "briefcase-conveyor-belt" icon from https://lucide.dev/icons/briefcase-conveyor-belt. */
export const BriefcaseConveyorBeltIcon = /*#__PURE__*/ createLucideIcon(
	"briefcase-conveyor-belt",
	registry.briefcaseConveyorBelt,
);

/** Renders the "briefcase-medical" icon from https://lucide.dev/icons/briefcase-medical. */
export const BriefcaseMedicalIcon = /*#__PURE__*/ createLucideIcon(
	"briefcase-medical",
	registry.briefcaseMedical,
);

/** Renders the "briefcase-plus" icon from https://lucide.dev/icons/briefcase-plus. */
export const BriefcasePlusIcon = /*#__PURE__*/ createLucideIcon(
	"briefcase-plus",
	registry.briefcasePlus,
);

/** Renders the "bring-to-front" icon from https://lucide.dev/icons/bring-to-front. */
export const BringToFrontIcon = /*#__PURE__*/ createLucideIcon(
	"bring-to-front",
	registry.bringToFront,
);

/** Renders the "broccoli" icon from https://lucide.dev/icons/broccoli. */
export const BroccoliIcon = /*#__PURE__*/ createLucideIcon("broccoli", registry.broccoli);

/** Renders the "broom" icon from https://lucide.dev/icons/broom. */
export const BroomIcon = /*#__PURE__*/ createLucideIcon("broom", registry.broom);

/** Renders the "broom-sparkles" icon from https://lucide.dev/icons/broom-sparkles. */
export const BroomSparklesIcon = /*#__PURE__*/ createLucideIcon(
	"broom-sparkles",
	registry.broomSparkles,
);

/** Renders the "brush" icon from https://lucide.dev/icons/brush. */
export const BrushIcon = /*#__PURE__*/ createLucideIcon("brush", registry.brush);

/** Renders the "brush-cleaning" icon from https://lucide.dev/icons/brush-cleaning. */
export const BrushCleaningIcon = /*#__PURE__*/ createLucideIcon(
	"brush-cleaning",
	registry.brushCleaning,
);

/** Renders the "bubbles" icon from https://lucide.dev/icons/bubbles. */
export const BubblesIcon = /*#__PURE__*/ createLucideIcon("bubbles", registry.bubbles);

/** Renders the "bug" icon from https://lucide.dev/icons/bug. */
export const BugIcon = /*#__PURE__*/ createLucideIcon("bug", registry.bug);

/** Renders the "bug-off" icon from https://lucide.dev/icons/bug-off. */
export const BugOffIcon = /*#__PURE__*/ createLucideIcon("bug-off", registry.bugOff);

/** Renders the "bug-play" icon from https://lucide.dev/icons/bug-play. */
export const BugPlayIcon = /*#__PURE__*/ createLucideIcon("bug-play", registry.bugPlay);

/** Renders the "building" icon from https://lucide.dev/icons/building. */
export const BuildingIcon = /*#__PURE__*/ createLucideIcon("building", registry.building);

/** Renders the "building-complex" icon from https://lucide.dev/icons/building-complex. */
export const BuildingComplexIcon = /*#__PURE__*/ createLucideIcon(
	"building-complex",
	registry.buildingComplex,
);

/** Renders the "building-complex-plus" icon from https://lucide.dev/icons/building-complex-plus. */
export const BuildingComplexPlusIcon = /*#__PURE__*/ createLucideIcon(
	"building-complex-plus",
	registry.buildingComplexPlus,
);

/** Renders the "bus" icon from https://lucide.dev/icons/bus. */
export const BusIcon = /*#__PURE__*/ createLucideIcon("bus", registry.bus);

/** Renders the "bus-front" icon from https://lucide.dev/icons/bus-front. */
export const BusFrontIcon = /*#__PURE__*/ createLucideIcon("bus-front", registry.busFront);

/** Renders the "cable" icon from https://lucide.dev/icons/cable. */
export const CableIcon = /*#__PURE__*/ createLucideIcon("cable", registry.cable);

/** Renders the "cable-car" icon from https://lucide.dev/icons/cable-car. */
export const CableCarIcon = /*#__PURE__*/ createLucideIcon("cable-car", registry.cableCar);

/** Renders the "cake" icon from https://lucide.dev/icons/cake. */
export const CakeIcon = /*#__PURE__*/ createLucideIcon("cake", registry.cake);

/** Renders the "cake-slice" icon from https://lucide.dev/icons/cake-slice. */
export const CakeSliceIcon = /*#__PURE__*/ createLucideIcon("cake-slice", registry.cakeSlice);

/** Renders the "calculator" icon from https://lucide.dev/icons/calculator. */
export const CalculatorIcon = /*#__PURE__*/ createLucideIcon("calculator", registry.calculator);

/** Renders the "calendar" icon from https://lucide.dev/icons/calendar. */
export const CalendarIcon = /*#__PURE__*/ createLucideIcon("calendar", registry.calendar);

/** Renders the "calendar-1" icon from https://lucide.dev/icons/calendar-1. */
export const Calendar1Icon = /*#__PURE__*/ createLucideIcon("calendar-1", registry.calendar1);

/** Renders the "calendar-arrow-down" icon from https://lucide.dev/icons/calendar-arrow-down. */
export const CalendarArrowDownIcon = /*#__PURE__*/ createLucideIcon(
	"calendar-arrow-down",
	registry.calendarArrowDown,
);

/** Renders the "calendar-arrow-up" icon from https://lucide.dev/icons/calendar-arrow-up. */
export const CalendarArrowUpIcon = /*#__PURE__*/ createLucideIcon(
	"calendar-arrow-up",
	registry.calendarArrowUp,
);

/** Renders the "calendar-check" icon from https://lucide.dev/icons/calendar-check. */
export const CalendarCheckIcon = /*#__PURE__*/ createLucideIcon(
	"calendar-check",
	registry.calendarCheck,
);

/** Renders the "calendar-check-2" icon from https://lucide.dev/icons/calendar-check-2. */
export const CalendarCheck2Icon = /*#__PURE__*/ createLucideIcon(
	"calendar-check-2",
	registry.calendarCheck2,
);

/** Renders the "calendar-chevrons-left" icon from https://lucide.dev/icons/calendar-chevrons-left. */
export const CalendarChevronsLeftIcon = /*#__PURE__*/ createLucideIcon(
	"calendar-chevrons-left",
	registry.calendarChevronsLeft,
);

/** Renders the "calendar-chevrons-right" icon from https://lucide.dev/icons/calendar-chevrons-right. */
export const CalendarChevronsRightIcon = /*#__PURE__*/ createLucideIcon(
	"calendar-chevrons-right",
	registry.calendarChevronsRight,
);

/** Renders the "calendar-clock" icon from https://lucide.dev/icons/calendar-clock. */
export const CalendarClockIcon = /*#__PURE__*/ createLucideIcon(
	"calendar-clock",
	registry.calendarClock,
);

/** Renders the "calendar-cog" icon from https://lucide.dev/icons/calendar-cog. */
export const CalendarCogIcon = /*#__PURE__*/ createLucideIcon("calendar-cog", registry.calendarCog);

/** Renders the "calendar-days" icon from https://lucide.dev/icons/calendar-days. */
export const CalendarDaysIcon = /*#__PURE__*/ createLucideIcon(
	"calendar-days",
	registry.calendarDays,
);

/** Renders the "calendar-fold" icon from https://lucide.dev/icons/calendar-fold. */
export const CalendarFoldIcon = /*#__PURE__*/ createLucideIcon(
	"calendar-fold",
	registry.calendarFold,
);

/** Renders the "calendar-heart" icon from https://lucide.dev/icons/calendar-heart. */
export const CalendarHeartIcon = /*#__PURE__*/ createLucideIcon(
	"calendar-heart",
	registry.calendarHeart,
);

/** Renders the "calendar-minus" icon from https://lucide.dev/icons/calendar-minus. */
export const CalendarMinusIcon = /*#__PURE__*/ createLucideIcon(
	"calendar-minus",
	registry.calendarMinus,
);

/** Renders the "calendar-minus-2" icon from https://lucide.dev/icons/calendar-minus-2. */
export const CalendarMinus2Icon = /*#__PURE__*/ createLucideIcon(
	"calendar-minus-2",
	registry.calendarMinus2,
);

/** Renders the "calendar-off" icon from https://lucide.dev/icons/calendar-off. */
export const CalendarOffIcon = /*#__PURE__*/ createLucideIcon("calendar-off", registry.calendarOff);

/** Renders the "calendar-plus" icon from https://lucide.dev/icons/calendar-plus. */
export const CalendarPlusIcon = /*#__PURE__*/ createLucideIcon(
	"calendar-plus",
	registry.calendarPlus,
);

/** Renders the "calendar-plus-2" icon from https://lucide.dev/icons/calendar-plus-2. */
export const CalendarPlus2Icon = /*#__PURE__*/ createLucideIcon(
	"calendar-plus-2",
	registry.calendarPlus2,
);

/** Renders the "calendar-range" icon from https://lucide.dev/icons/calendar-range. */
export const CalendarRangeIcon = /*#__PURE__*/ createLucideIcon(
	"calendar-range",
	registry.calendarRange,
);

/** Renders the "calendar-search" icon from https://lucide.dev/icons/calendar-search. */
export const CalendarSearchIcon = /*#__PURE__*/ createLucideIcon(
	"calendar-search",
	registry.calendarSearch,
);

/** Renders the "calendar-sync" icon from https://lucide.dev/icons/calendar-sync. */
export const CalendarSyncIcon = /*#__PURE__*/ createLucideIcon(
	"calendar-sync",
	registry.calendarSync,
);

/** Renders the "calendar-x" icon from https://lucide.dev/icons/calendar-x. */
export const CalendarXIcon = /*#__PURE__*/ createLucideIcon("calendar-x", registry.calendarX);

/** Renders the "calendar-x-2" icon from https://lucide.dev/icons/calendar-x-2. */
export const CalendarX2Icon = /*#__PURE__*/ createLucideIcon("calendar-x-2", registry.calendarX2);

/** Renders the "calendars" icon from https://lucide.dev/icons/calendars. */
export const CalendarsIcon = /*#__PURE__*/ createLucideIcon("calendars", registry.calendars);

/** Renders the "camera" icon from https://lucide.dev/icons/camera. */
export const CameraIcon = /*#__PURE__*/ createLucideIcon("camera", registry.camera);

/** Renders the "camera-off" icon from https://lucide.dev/icons/camera-off. */
export const CameraOffIcon = /*#__PURE__*/ createLucideIcon("camera-off", registry.cameraOff);

/** Renders the "can" icon from https://lucide.dev/icons/can. */
export const CanIcon = /*#__PURE__*/ createLucideIcon("can", registry.can);

/** Renders the "can-soda" icon from https://lucide.dev/icons/can-soda. */
export const CanSodaIcon = /*#__PURE__*/ createLucideIcon("can-soda", registry.canSoda);

/** Renders the "candy" icon from https://lucide.dev/icons/candy. */
export const CandyIcon = /*#__PURE__*/ createLucideIcon("candy", registry.candy);

/** Renders the "candy-cane" icon from https://lucide.dev/icons/candy-cane. */
export const CandyCaneIcon = /*#__PURE__*/ createLucideIcon("candy-cane", registry.candyCane);

/** Renders the "candy-off" icon from https://lucide.dev/icons/candy-off. */
export const CandyOffIcon = /*#__PURE__*/ createLucideIcon("candy-off", registry.candyOff);

/** Renders the "cannabis" icon from https://lucide.dev/icons/cannabis. */
export const CannabisIcon = /*#__PURE__*/ createLucideIcon("cannabis", registry.cannabis);

/** Renders the "cannabis-off" icon from https://lucide.dev/icons/cannabis-off. */
export const CannabisOffIcon = /*#__PURE__*/ createLucideIcon("cannabis-off", registry.cannabisOff);

/** Renders the "captions" icon from https://lucide.dev/icons/captions. */
export const CaptionsIcon = /*#__PURE__*/ createLucideIcon("captions", registry.captions);

/** Renders the "captions-off" icon from https://lucide.dev/icons/captions-off. */
export const CaptionsOffIcon = /*#__PURE__*/ createLucideIcon("captions-off", registry.captionsOff);

/** Renders the "car" icon from https://lucide.dev/icons/car. */
export const CarIcon = /*#__PURE__*/ createLucideIcon("car", registry.car);

/** Renders the "car-battery" icon from https://lucide.dev/icons/car-battery. */
export const CarBatteryIcon = /*#__PURE__*/ createLucideIcon("car-battery", registry.carBattery);

/** Renders the "car-front" icon from https://lucide.dev/icons/car-front. */
export const CarFrontIcon = /*#__PURE__*/ createLucideIcon("car-front", registry.carFront);

/** Renders the "car-taxi-front" icon from https://lucide.dev/icons/car-taxi-front. */
export const CarTaxiFrontIcon = /*#__PURE__*/ createLucideIcon(
	"car-taxi-front",
	registry.carTaxiFront,
);

/** Renders the "caravan" icon from https://lucide.dev/icons/caravan. */
export const CaravanIcon = /*#__PURE__*/ createLucideIcon("caravan", registry.caravan);

/** Renders the "card-sim" icon from https://lucide.dev/icons/card-sim. */
export const CardSimIcon = /*#__PURE__*/ createLucideIcon("card-sim", registry.cardSim);

/** Renders the "carrot" icon from https://lucide.dev/icons/carrot. */
export const CarrotIcon = /*#__PURE__*/ createLucideIcon("carrot", registry.carrot);

/** Renders the "carton" icon from https://lucide.dev/icons/carton. */
export const CartonIcon = /*#__PURE__*/ createLucideIcon("carton", registry.carton);

/** Renders the "carton-off" icon from https://lucide.dev/icons/carton-off. */
export const CartonOffIcon = /*#__PURE__*/ createLucideIcon("carton-off", registry.cartonOff);

/** Renders the "case-lower" icon from https://lucide.dev/icons/case-lower. */
export const CaseLowerIcon = /*#__PURE__*/ createLucideIcon("case-lower", registry.caseLower);

/** Renders the "case-sensitive" icon from https://lucide.dev/icons/case-sensitive. */
export const CaseSensitiveIcon = /*#__PURE__*/ createLucideIcon(
	"case-sensitive",
	registry.caseSensitive,
);

/** Renders the "case-upper" icon from https://lucide.dev/icons/case-upper. */
export const CaseUpperIcon = /*#__PURE__*/ createLucideIcon("case-upper", registry.caseUpper);

/** Renders the "cassette-tape" icon from https://lucide.dev/icons/cassette-tape. */
export const CassetteTapeIcon = /*#__PURE__*/ createLucideIcon(
	"cassette-tape",
	registry.cassetteTape,
);

/** Renders the "cast" icon from https://lucide.dev/icons/cast. */
export const CastIcon = /*#__PURE__*/ createLucideIcon("cast", registry.cast);

/** Renders the "castle" icon from https://lucide.dev/icons/castle. */
export const CastleIcon = /*#__PURE__*/ createLucideIcon("castle", registry.castle);

/** Renders the "cat" icon from https://lucide.dev/icons/cat. */
export const CatIcon = /*#__PURE__*/ createLucideIcon("cat", registry.cat);

/** Renders the "cctv" icon from https://lucide.dev/icons/cctv. */
export const CctvIcon = /*#__PURE__*/ createLucideIcon("cctv", registry.cctv);

/** Renders the "cctv-off" icon from https://lucide.dev/icons/cctv-off. */
export const CctvOffIcon = /*#__PURE__*/ createLucideIcon("cctv-off", registry.cctvOff);

/** Renders the "certificate" icon from https://lucide.dev/icons/certificate. */
export const CertificateIcon = /*#__PURE__*/ createLucideIcon("certificate", registry.certificate);

/** Renders the "chart-area" icon from https://lucide.dev/icons/chart-area. */
export const ChartAreaIcon = /*#__PURE__*/ createLucideIcon("chart-area", registry.chartArea);

/** Renders the "chart-bar" icon from https://lucide.dev/icons/chart-bar. */
export const ChartBarIcon = /*#__PURE__*/ createLucideIcon("chart-bar", registry.chartBar);

/** Renders the "chart-bar-big" icon from https://lucide.dev/icons/chart-bar-big. */
export const ChartBarBigIcon = /*#__PURE__*/ createLucideIcon(
	"chart-bar-big",
	registry.chartBarBig,
);

/** Renders the "chart-bar-decreasing" icon from https://lucide.dev/icons/chart-bar-decreasing. */
export const ChartBarDecreasingIcon = /*#__PURE__*/ createLucideIcon(
	"chart-bar-decreasing",
	registry.chartBarDecreasing,
);

/** Renders the "chart-bar-increasing" icon from https://lucide.dev/icons/chart-bar-increasing. */
export const ChartBarIncreasingIcon = /*#__PURE__*/ createLucideIcon(
	"chart-bar-increasing",
	registry.chartBarIncreasing,
);

/** Renders the "chart-bar-stacked" icon from https://lucide.dev/icons/chart-bar-stacked. */
export const ChartBarStackedIcon = /*#__PURE__*/ createLucideIcon(
	"chart-bar-stacked",
	registry.chartBarStacked,
);

/** Renders the "chart-candlestick" icon from https://lucide.dev/icons/chart-candlestick. */
export const ChartCandlestickIcon = /*#__PURE__*/ createLucideIcon(
	"chart-candlestick",
	registry.chartCandlestick,
);

/** Renders the "chart-column" icon from https://lucide.dev/icons/chart-column. */
export const ChartColumnIcon = /*#__PURE__*/ createLucideIcon("chart-column", registry.chartColumn);

/** Renders the "chart-column-big" icon from https://lucide.dev/icons/chart-column-big. */
export const ChartColumnBigIcon = /*#__PURE__*/ createLucideIcon(
	"chart-column-big",
	registry.chartColumnBig,
);

/** Renders the "chart-column-decreasing" icon from https://lucide.dev/icons/chart-column-decreasing. */
export const ChartColumnDecreasingIcon = /*#__PURE__*/ createLucideIcon(
	"chart-column-decreasing",
	registry.chartColumnDecreasing,
);

/** Renders the "chart-column-increasing" icon from https://lucide.dev/icons/chart-column-increasing. */
export const ChartColumnIncreasingIcon = /*#__PURE__*/ createLucideIcon(
	"chart-column-increasing",
	registry.chartColumnIncreasing,
);

/** Renders the "chart-column-stacked" icon from https://lucide.dev/icons/chart-column-stacked. */
export const ChartColumnStackedIcon = /*#__PURE__*/ createLucideIcon(
	"chart-column-stacked",
	registry.chartColumnStacked,
);

/** Renders the "chart-gantt" icon from https://lucide.dev/icons/chart-gantt. */
export const ChartGanttIcon = /*#__PURE__*/ createLucideIcon("chart-gantt", registry.chartGantt);

/** Renders the "chart-line" icon from https://lucide.dev/icons/chart-line. */
export const ChartLineIcon = /*#__PURE__*/ createLucideIcon("chart-line", registry.chartLine);

/** Renders the "chart-network" icon from https://lucide.dev/icons/chart-network. */
export const ChartNetworkIcon = /*#__PURE__*/ createLucideIcon(
	"chart-network",
	registry.chartNetwork,
);

/** Renders the "chart-no-axes-column" icon from https://lucide.dev/icons/chart-no-axes-column. */
export const ChartNoAxesColumnIcon = /*#__PURE__*/ createLucideIcon(
	"chart-no-axes-column",
	registry.chartNoAxesColumn,
);

/** Renders the "chart-no-axes-column-decreasing" icon from https://lucide.dev/icons/chart-no-axes-column-decreasing. */
export const ChartNoAxesColumnDecreasingIcon = /*#__PURE__*/ createLucideIcon(
	"chart-no-axes-column-decreasing",
	registry.chartNoAxesColumnDecreasing,
);

/** Renders the "chart-no-axes-column-increasing" icon from https://lucide.dev/icons/chart-no-axes-column-increasing. */
export const ChartNoAxesColumnIncreasingIcon = /*#__PURE__*/ createLucideIcon(
	"chart-no-axes-column-increasing",
	registry.chartNoAxesColumnIncreasing,
);

/** Renders the "chart-no-axes-combined" icon from https://lucide.dev/icons/chart-no-axes-combined. */
export const ChartNoAxesCombinedIcon = /*#__PURE__*/ createLucideIcon(
	"chart-no-axes-combined",
	registry.chartNoAxesCombined,
);

/** Renders the "chart-no-axes-gantt" icon from https://lucide.dev/icons/chart-no-axes-gantt. */
export const ChartNoAxesGanttIcon = /*#__PURE__*/ createLucideIcon(
	"chart-no-axes-gantt",
	registry.chartNoAxesGantt,
);

/** Renders the "chart-pie" icon from https://lucide.dev/icons/chart-pie. */
export const ChartPieIcon = /*#__PURE__*/ createLucideIcon("chart-pie", registry.chartPie);

/** Renders the "chart-scatter" icon from https://lucide.dev/icons/chart-scatter. */
export const ChartScatterIcon = /*#__PURE__*/ createLucideIcon(
	"chart-scatter",
	registry.chartScatter,
);

/** Renders the "chart-spline" icon from https://lucide.dev/icons/chart-spline. */
export const ChartSplineIcon = /*#__PURE__*/ createLucideIcon("chart-spline", registry.chartSpline);

/** Renders the "check" icon from https://lucide.dev/icons/check. */
export const CheckIcon = /*#__PURE__*/ createLucideIcon("check", registry.check);

/** Renders the "check-check" icon from https://lucide.dev/icons/check-check. */
export const CheckCheckIcon = /*#__PURE__*/ createLucideIcon("check-check", registry.checkCheck);

/** Renders the "check-line" icon from https://lucide.dev/icons/check-line. */
export const CheckLineIcon = /*#__PURE__*/ createLucideIcon("check-line", registry.checkLine);

/** Renders the "chef-hat" icon from https://lucide.dev/icons/chef-hat. */
export const ChefHatIcon = /*#__PURE__*/ createLucideIcon("chef-hat", registry.chefHat);

/** Renders the "cherry" icon from https://lucide.dev/icons/cherry. */
export const CherryIcon = /*#__PURE__*/ createLucideIcon("cherry", registry.cherry);

/** Renders the "chess-bishop" icon from https://lucide.dev/icons/chess-bishop. */
export const ChessBishopIcon = /*#__PURE__*/ createLucideIcon("chess-bishop", registry.chessBishop);

/** Renders the "chess-king" icon from https://lucide.dev/icons/chess-king. */
export const ChessKingIcon = /*#__PURE__*/ createLucideIcon("chess-king", registry.chessKing);

/** Renders the "chess-knight" icon from https://lucide.dev/icons/chess-knight. */
export const ChessKnightIcon = /*#__PURE__*/ createLucideIcon("chess-knight", registry.chessKnight);

/** Renders the "chess-pawn" icon from https://lucide.dev/icons/chess-pawn. */
export const ChessPawnIcon = /*#__PURE__*/ createLucideIcon("chess-pawn", registry.chessPawn);

/** Renders the "chess-queen" icon from https://lucide.dev/icons/chess-queen. */
export const ChessQueenIcon = /*#__PURE__*/ createLucideIcon("chess-queen", registry.chessQueen);

/** Renders the "chess-rook" icon from https://lucide.dev/icons/chess-rook. */
export const ChessRookIcon = /*#__PURE__*/ createLucideIcon("chess-rook", registry.chessRook);

/** Renders the "chevron-down" icon from https://lucide.dev/icons/chevron-down. */
export const ChevronDownIcon = /*#__PURE__*/ createLucideIcon("chevron-down", registry.chevronDown);

/** Renders the "chevron-first" icon from https://lucide.dev/icons/chevron-first. */
export const ChevronFirstIcon = /*#__PURE__*/ createLucideIcon(
	"chevron-first",
	registry.chevronFirst,
);

/** Renders the "chevron-last" icon from https://lucide.dev/icons/chevron-last. */
export const ChevronLastIcon = /*#__PURE__*/ createLucideIcon("chevron-last", registry.chevronLast);

/** Renders the "chevron-left" icon from https://lucide.dev/icons/chevron-left. */
export const ChevronLeftIcon = /*#__PURE__*/ createLucideIcon("chevron-left", registry.chevronLeft);

/** Renders the "chevron-right" icon from https://lucide.dev/icons/chevron-right. */
export const ChevronRightIcon = /*#__PURE__*/ createLucideIcon(
	"chevron-right",
	registry.chevronRight,
);

/** Renders the "chevron-up" icon from https://lucide.dev/icons/chevron-up. */
export const ChevronUpIcon = /*#__PURE__*/ createLucideIcon("chevron-up", registry.chevronUp);

/** Renders the "chevrons-down" icon from https://lucide.dev/icons/chevrons-down. */
export const ChevronsDownIcon = /*#__PURE__*/ createLucideIcon(
	"chevrons-down",
	registry.chevronsDown,
);

/** Renders the "chevrons-down-up" icon from https://lucide.dev/icons/chevrons-down-up. */
export const ChevronsDownUpIcon = /*#__PURE__*/ createLucideIcon(
	"chevrons-down-up",
	registry.chevronsDownUp,
);

/** Renders the "chevrons-left" icon from https://lucide.dev/icons/chevrons-left. */
export const ChevronsLeftIcon = /*#__PURE__*/ createLucideIcon(
	"chevrons-left",
	registry.chevronsLeft,
);

/** Renders the "chevrons-left-right" icon from https://lucide.dev/icons/chevrons-left-right. */
export const ChevronsLeftRightIcon = /*#__PURE__*/ createLucideIcon(
	"chevrons-left-right",
	registry.chevronsLeftRight,
);

/** Renders the "chevrons-left-right-ellipsis" icon from https://lucide.dev/icons/chevrons-left-right-ellipsis. */
export const ChevronsLeftRightEllipsisIcon = /*#__PURE__*/ createLucideIcon(
	"chevrons-left-right-ellipsis",
	registry.chevronsLeftRightEllipsis,
);

/** Renders the "chevrons-right" icon from https://lucide.dev/icons/chevrons-right. */
export const ChevronsRightIcon = /*#__PURE__*/ createLucideIcon(
	"chevrons-right",
	registry.chevronsRight,
);

/** Renders the "chevrons-right-left" icon from https://lucide.dev/icons/chevrons-right-left. */
export const ChevronsRightLeftIcon = /*#__PURE__*/ createLucideIcon(
	"chevrons-right-left",
	registry.chevronsRightLeft,
);

/** Renders the "chevrons-up" icon from https://lucide.dev/icons/chevrons-up. */
export const ChevronsUpIcon = /*#__PURE__*/ createLucideIcon("chevrons-up", registry.chevronsUp);

/** Renders the "chevrons-up-down" icon from https://lucide.dev/icons/chevrons-up-down. */
export const ChevronsUpDownIcon = /*#__PURE__*/ createLucideIcon(
	"chevrons-up-down",
	registry.chevronsUpDown,
);

/** Renders the "church" icon from https://lucide.dev/icons/church. */
export const ChurchIcon = /*#__PURE__*/ createLucideIcon("church", registry.church);

/** Renders the "cigarette" icon from https://lucide.dev/icons/cigarette. */
export const CigaretteIcon = /*#__PURE__*/ createLucideIcon("cigarette", registry.cigarette);

/** Renders the "cigarette-off" icon from https://lucide.dev/icons/cigarette-off. */
export const CigaretteOffIcon = /*#__PURE__*/ createLucideIcon(
	"cigarette-off",
	registry.cigaretteOff,
);

/** Renders the "circle" icon from https://lucide.dev/icons/circle. */
export const CircleIcon = /*#__PURE__*/ createLucideIcon("circle", registry.circle);

/** Renders the "circle-alert" icon from https://lucide.dev/icons/circle-alert. */
export const CircleAlertIcon = /*#__PURE__*/ createLucideIcon("circle-alert", registry.circleAlert);

/** Renders the "circle-arrow-down" icon from https://lucide.dev/icons/circle-arrow-down. */
export const CircleArrowDownIcon = /*#__PURE__*/ createLucideIcon(
	"circle-arrow-down",
	registry.circleArrowDown,
);

/** Renders the "circle-arrow-left" icon from https://lucide.dev/icons/circle-arrow-left. */
export const CircleArrowLeftIcon = /*#__PURE__*/ createLucideIcon(
	"circle-arrow-left",
	registry.circleArrowLeft,
);

/** Renders the "circle-arrow-out-down-left" icon from https://lucide.dev/icons/circle-arrow-out-down-left. */
export const CircleArrowOutDownLeftIcon = /*#__PURE__*/ createLucideIcon(
	"circle-arrow-out-down-left",
	registry.circleArrowOutDownLeft,
);

/** Renders the "circle-arrow-out-down-right" icon from https://lucide.dev/icons/circle-arrow-out-down-right. */
export const CircleArrowOutDownRightIcon = /*#__PURE__*/ createLucideIcon(
	"circle-arrow-out-down-right",
	registry.circleArrowOutDownRight,
);

/** Renders the "circle-arrow-out-up-left" icon from https://lucide.dev/icons/circle-arrow-out-up-left. */
export const CircleArrowOutUpLeftIcon = /*#__PURE__*/ createLucideIcon(
	"circle-arrow-out-up-left",
	registry.circleArrowOutUpLeft,
);

/** Renders the "circle-arrow-out-up-right" icon from https://lucide.dev/icons/circle-arrow-out-up-right. */
export const CircleArrowOutUpRightIcon = /*#__PURE__*/ createLucideIcon(
	"circle-arrow-out-up-right",
	registry.circleArrowOutUpRight,
);

/** Renders the "circle-arrow-right" icon from https://lucide.dev/icons/circle-arrow-right. */
export const CircleArrowRightIcon = /*#__PURE__*/ createLucideIcon(
	"circle-arrow-right",
	registry.circleArrowRight,
);

/** Renders the "circle-arrow-up" icon from https://lucide.dev/icons/circle-arrow-up. */
export const CircleArrowUpIcon = /*#__PURE__*/ createLucideIcon(
	"circle-arrow-up",
	registry.circleArrowUp,
);

/** Renders the "circle-check" icon from https://lucide.dev/icons/circle-check. */
export const CircleCheckIcon = /*#__PURE__*/ createLucideIcon("circle-check", registry.circleCheck);

/** Renders the "circle-check-big" icon from https://lucide.dev/icons/circle-check-big. */
export const CircleCheckBigIcon = /*#__PURE__*/ createLucideIcon(
	"circle-check-big",
	registry.circleCheckBig,
);

/** Renders the "circle-chevron-down" icon from https://lucide.dev/icons/circle-chevron-down. */
export const CircleChevronDownIcon = /*#__PURE__*/ createLucideIcon(
	"circle-chevron-down",
	registry.circleChevronDown,
);

/** Renders the "circle-chevron-left" icon from https://lucide.dev/icons/circle-chevron-left. */
export const CircleChevronLeftIcon = /*#__PURE__*/ createLucideIcon(
	"circle-chevron-left",
	registry.circleChevronLeft,
);

/** Renders the "circle-chevron-right" icon from https://lucide.dev/icons/circle-chevron-right. */
export const CircleChevronRightIcon = /*#__PURE__*/ createLucideIcon(
	"circle-chevron-right",
	registry.circleChevronRight,
);

/** Renders the "circle-chevron-up" icon from https://lucide.dev/icons/circle-chevron-up. */
export const CircleChevronUpIcon = /*#__PURE__*/ createLucideIcon(
	"circle-chevron-up",
	registry.circleChevronUp,
);

/** Renders the "circle-chevrons-down" icon from https://lucide.dev/icons/circle-chevrons-down. */
export const CircleChevronsDownIcon = /*#__PURE__*/ createLucideIcon(
	"circle-chevrons-down",
	registry.circleChevronsDown,
);

/** Renders the "circle-chevrons-left" icon from https://lucide.dev/icons/circle-chevrons-left. */
export const CircleChevronsLeftIcon = /*#__PURE__*/ createLucideIcon(
	"circle-chevrons-left",
	registry.circleChevronsLeft,
);

/** Renders the "circle-chevrons-right" icon from https://lucide.dev/icons/circle-chevrons-right. */
export const CircleChevronsRightIcon = /*#__PURE__*/ createLucideIcon(
	"circle-chevrons-right",
	registry.circleChevronsRight,
);

/** Renders the "circle-chevrons-up" icon from https://lucide.dev/icons/circle-chevrons-up. */
export const CircleChevronsUpIcon = /*#__PURE__*/ createLucideIcon(
	"circle-chevrons-up",
	registry.circleChevronsUp,
);

/** Renders the "circle-dashed" icon from https://lucide.dev/icons/circle-dashed. */
export const CircleDashedIcon = /*#__PURE__*/ createLucideIcon(
	"circle-dashed",
	registry.circleDashed,
);

/** Renders the "circle-dashed-check" icon from https://lucide.dev/icons/circle-dashed-check. */
export const CircleDashedCheckIcon = /*#__PURE__*/ createLucideIcon(
	"circle-dashed-check",
	registry.circleDashedCheck,
);

/** Renders the "circle-divide" icon from https://lucide.dev/icons/circle-divide. */
export const CircleDivideIcon = /*#__PURE__*/ createLucideIcon(
	"circle-divide",
	registry.circleDivide,
);

/** Renders the "circle-dollar-sign" icon from https://lucide.dev/icons/circle-dollar-sign. */
export const CircleDollarSignIcon = /*#__PURE__*/ createLucideIcon(
	"circle-dollar-sign",
	registry.circleDollarSign,
);

/** Renders the "circle-dot" icon from https://lucide.dev/icons/circle-dot. */
export const CircleDotIcon = /*#__PURE__*/ createLucideIcon("circle-dot", registry.circleDot);

/** Renders the "circle-dot-dashed" icon from https://lucide.dev/icons/circle-dot-dashed. */
export const CircleDotDashedIcon = /*#__PURE__*/ createLucideIcon(
	"circle-dot-dashed",
	registry.circleDotDashed,
);

/** Renders the "circle-ellipsis" icon from https://lucide.dev/icons/circle-ellipsis. */
export const CircleEllipsisIcon = /*#__PURE__*/ createLucideIcon(
	"circle-ellipsis",
	registry.circleEllipsis,
);

/** Renders the "circle-equal" icon from https://lucide.dev/icons/circle-equal. */
export const CircleEqualIcon = /*#__PURE__*/ createLucideIcon("circle-equal", registry.circleEqual);

/** Renders the "circle-euro" icon from https://lucide.dev/icons/circle-euro. */
export const CircleEuroIcon = /*#__PURE__*/ createLucideIcon("circle-euro", registry.circleEuro);

/** Renders the "circle-fading-arrow-up" icon from https://lucide.dev/icons/circle-fading-arrow-up. */
export const CircleFadingArrowUpIcon = /*#__PURE__*/ createLucideIcon(
	"circle-fading-arrow-up",
	registry.circleFadingArrowUp,
);

/** Renders the "circle-fading-play" icon from https://lucide.dev/icons/circle-fading-play. */
export const CircleFadingPlayIcon = /*#__PURE__*/ createLucideIcon(
	"circle-fading-play",
	registry.circleFadingPlay,
);

/** Renders the "circle-fading-plus" icon from https://lucide.dev/icons/circle-fading-plus. */
export const CircleFadingPlusIcon = /*#__PURE__*/ createLucideIcon(
	"circle-fading-plus",
	registry.circleFadingPlus,
);

/** Renders the "circle-gauge" icon from https://lucide.dev/icons/circle-gauge. */
export const CircleGaugeIcon = /*#__PURE__*/ createLucideIcon("circle-gauge", registry.circleGauge);

/** Renders the "circle-minus" icon from https://lucide.dev/icons/circle-minus. */
export const CircleMinusIcon = /*#__PURE__*/ createLucideIcon("circle-minus", registry.circleMinus);

/** Renders the "circle-off" icon from https://lucide.dev/icons/circle-off. */
export const CircleOffIcon = /*#__PURE__*/ createLucideIcon("circle-off", registry.circleOff);

/** Renders the "circle-parking" icon from https://lucide.dev/icons/circle-parking. */
export const CircleParkingIcon = /*#__PURE__*/ createLucideIcon(
	"circle-parking",
	registry.circleParking,
);

/** Renders the "circle-parking-off" icon from https://lucide.dev/icons/circle-parking-off. */
export const CircleParkingOffIcon = /*#__PURE__*/ createLucideIcon(
	"circle-parking-off",
	registry.circleParkingOff,
);

/** Renders the "circle-pause" icon from https://lucide.dev/icons/circle-pause. */
export const CirclePauseIcon = /*#__PURE__*/ createLucideIcon("circle-pause", registry.circlePause);

/** Renders the "circle-percent" icon from https://lucide.dev/icons/circle-percent. */
export const CirclePercentIcon = /*#__PURE__*/ createLucideIcon(
	"circle-percent",
	registry.circlePercent,
);

/** Renders the "circle-pile" icon from https://lucide.dev/icons/circle-pile. */
export const CirclePileIcon = /*#__PURE__*/ createLucideIcon("circle-pile", registry.circlePile);

/** Renders the "circle-play" icon from https://lucide.dev/icons/circle-play. */
export const CirclePlayIcon = /*#__PURE__*/ createLucideIcon("circle-play", registry.circlePlay);

/** Renders the "circle-plus" icon from https://lucide.dev/icons/circle-plus. */
export const CirclePlusIcon = /*#__PURE__*/ createLucideIcon("circle-plus", registry.circlePlus);

/** Renders the "circle-pound-sterling" icon from https://lucide.dev/icons/circle-pound-sterling. */
export const CirclePoundSterlingIcon = /*#__PURE__*/ createLucideIcon(
	"circle-pound-sterling",
	registry.circlePoundSterling,
);

/** Renders the "circle-power" icon from https://lucide.dev/icons/circle-power. */
export const CirclePowerIcon = /*#__PURE__*/ createLucideIcon("circle-power", registry.circlePower);

/** Renders the "circle-question-mark" icon from https://lucide.dev/icons/circle-question-mark. */
export const CircleQuestionMarkIcon = /*#__PURE__*/ createLucideIcon(
	"circle-question-mark",
	registry.circleQuestionMark,
);

/** Renders the "circle-slash" icon from https://lucide.dev/icons/circle-slash. */
export const CircleSlashIcon = /*#__PURE__*/ createLucideIcon("circle-slash", registry.circleSlash);

/** Renders the "circle-slash-2" icon from https://lucide.dev/icons/circle-slash-2. */
export const CircleSlash2Icon = /*#__PURE__*/ createLucideIcon(
	"circle-slash-2",
	registry.circleSlash2,
);

/** Renders the "circle-small" icon from https://lucide.dev/icons/circle-small. */
export const CircleSmallIcon = /*#__PURE__*/ createLucideIcon("circle-small", registry.circleSmall);

/** Renders the "circle-star" icon from https://lucide.dev/icons/circle-star. */
export const CircleStarIcon = /*#__PURE__*/ createLucideIcon("circle-star", registry.circleStar);

/** Renders the "circle-stop" icon from https://lucide.dev/icons/circle-stop. */
export const CircleStopIcon = /*#__PURE__*/ createLucideIcon("circle-stop", registry.circleStop);

/** Renders the "circle-user" icon from https://lucide.dev/icons/circle-user. */
export const CircleUserIcon = /*#__PURE__*/ createLucideIcon("circle-user", registry.circleUser);

/** Renders the "circle-user-round" icon from https://lucide.dev/icons/circle-user-round. */
export const CircleUserRoundIcon = /*#__PURE__*/ createLucideIcon(
	"circle-user-round",
	registry.circleUserRound,
);

/** Renders the "circle-x" icon from https://lucide.dev/icons/circle-x. */
export const CircleXIcon = /*#__PURE__*/ createLucideIcon("circle-x", registry.circleX);

/** Renders the "circuit-board" icon from https://lucide.dev/icons/circuit-board. */
export const CircuitBoardIcon = /*#__PURE__*/ createLucideIcon(
	"circuit-board",
	registry.circuitBoard,
);

/** Renders the "citrus" icon from https://lucide.dev/icons/citrus. */
export const CitrusIcon = /*#__PURE__*/ createLucideIcon("citrus", registry.citrus);

/** Renders the "clapperboard" icon from https://lucide.dev/icons/clapperboard. */
export const ClapperboardIcon = /*#__PURE__*/ createLucideIcon(
	"clapperboard",
	registry.clapperboard,
);

/** Renders the "clef-alto" icon from https://lucide.dev/icons/clef-alto. */
export const ClefAltoIcon = /*#__PURE__*/ createLucideIcon("clef-alto", registry.clefAlto);

/** Renders the "clef-bass" icon from https://lucide.dev/icons/clef-bass. */
export const ClefBassIcon = /*#__PURE__*/ createLucideIcon("clef-bass", registry.clefBass);

/** Renders the "clef-treble" icon from https://lucide.dev/icons/clef-treble. */
export const ClefTrebleIcon = /*#__PURE__*/ createLucideIcon("clef-treble", registry.clefTreble);

/** Renders the "clipboard" icon from https://lucide.dev/icons/clipboard. */
export const ClipboardIcon = /*#__PURE__*/ createLucideIcon("clipboard", registry.clipboard);

/** Renders the "clipboard-check" icon from https://lucide.dev/icons/clipboard-check. */
export const ClipboardCheckIcon = /*#__PURE__*/ createLucideIcon(
	"clipboard-check",
	registry.clipboardCheck,
);

/** Renders the "clipboard-clock" icon from https://lucide.dev/icons/clipboard-clock. */
export const ClipboardClockIcon = /*#__PURE__*/ createLucideIcon(
	"clipboard-clock",
	registry.clipboardClock,
);

/** Renders the "clipboard-copy" icon from https://lucide.dev/icons/clipboard-copy. */
export const ClipboardCopyIcon = /*#__PURE__*/ createLucideIcon(
	"clipboard-copy",
	registry.clipboardCopy,
);

/** Renders the "clipboard-list" icon from https://lucide.dev/icons/clipboard-list. */
export const ClipboardListIcon = /*#__PURE__*/ createLucideIcon(
	"clipboard-list",
	registry.clipboardList,
);

/** Renders the "clipboard-minus" icon from https://lucide.dev/icons/clipboard-minus. */
export const ClipboardMinusIcon = /*#__PURE__*/ createLucideIcon(
	"clipboard-minus",
	registry.clipboardMinus,
);

/** Renders the "clipboard-paste" icon from https://lucide.dev/icons/clipboard-paste. */
export const ClipboardPasteIcon = /*#__PURE__*/ createLucideIcon(
	"clipboard-paste",
	registry.clipboardPaste,
);

/** Renders the "clipboard-pen" icon from https://lucide.dev/icons/clipboard-pen. */
export const ClipboardPenIcon = /*#__PURE__*/ createLucideIcon(
	"clipboard-pen",
	registry.clipboardPen,
);

/** Renders the "clipboard-pen-line" icon from https://lucide.dev/icons/clipboard-pen-line. */
export const ClipboardPenLineIcon = /*#__PURE__*/ createLucideIcon(
	"clipboard-pen-line",
	registry.clipboardPenLine,
);

/** Renders the "clipboard-plus" icon from https://lucide.dev/icons/clipboard-plus. */
export const ClipboardPlusIcon = /*#__PURE__*/ createLucideIcon(
	"clipboard-plus",
	registry.clipboardPlus,
);

/** Renders the "clipboard-type" icon from https://lucide.dev/icons/clipboard-type. */
export const ClipboardTypeIcon = /*#__PURE__*/ createLucideIcon(
	"clipboard-type",
	registry.clipboardType,
);

/** Renders the "clipboard-x" icon from https://lucide.dev/icons/clipboard-x. */
export const ClipboardXIcon = /*#__PURE__*/ createLucideIcon("clipboard-x", registry.clipboardX);

/** Renders the "clock" icon from https://lucide.dev/icons/clock. */
export const ClockIcon = /*#__PURE__*/ createLucideIcon("clock", registry.clock);

/** Renders the "clock-1" icon from https://lucide.dev/icons/clock-1. */
export const Clock1Icon = /*#__PURE__*/ createLucideIcon("clock-1", registry.clock1);

/** Renders the "clock-10" icon from https://lucide.dev/icons/clock-10. */
export const Clock10Icon = /*#__PURE__*/ createLucideIcon("clock-10", registry.clock10);

/** Renders the "clock-11" icon from https://lucide.dev/icons/clock-11. */
export const Clock11Icon = /*#__PURE__*/ createLucideIcon("clock-11", registry.clock11);

/** Renders the "clock-12" icon from https://lucide.dev/icons/clock-12. */
export const Clock12Icon = /*#__PURE__*/ createLucideIcon("clock-12", registry.clock12);

/** Renders the "clock-2" icon from https://lucide.dev/icons/clock-2. */
export const Clock2Icon = /*#__PURE__*/ createLucideIcon("clock-2", registry.clock2);

/** Renders the "clock-3" icon from https://lucide.dev/icons/clock-3. */
export const Clock3Icon = /*#__PURE__*/ createLucideIcon("clock-3", registry.clock3);

/** Renders the "clock-4" icon from https://lucide.dev/icons/clock-4. */
export const Clock4Icon = /*#__PURE__*/ createLucideIcon("clock-4", registry.clock4);

/** Renders the "clock-5" icon from https://lucide.dev/icons/clock-5. */
export const Clock5Icon = /*#__PURE__*/ createLucideIcon("clock-5", registry.clock5);

/** Renders the "clock-6" icon from https://lucide.dev/icons/clock-6. */
export const Clock6Icon = /*#__PURE__*/ createLucideIcon("clock-6", registry.clock6);

/** Renders the "clock-7" icon from https://lucide.dev/icons/clock-7. */
export const Clock7Icon = /*#__PURE__*/ createLucideIcon("clock-7", registry.clock7);

/** Renders the "clock-8" icon from https://lucide.dev/icons/clock-8. */
export const Clock8Icon = /*#__PURE__*/ createLucideIcon("clock-8", registry.clock8);

/** Renders the "clock-9" icon from https://lucide.dev/icons/clock-9. */
export const Clock9Icon = /*#__PURE__*/ createLucideIcon("clock-9", registry.clock9);

/** Renders the "clock-alert" icon from https://lucide.dev/icons/clock-alert. */
export const ClockAlertIcon = /*#__PURE__*/ createLucideIcon("clock-alert", registry.clockAlert);

/** Renders the "clock-arrow-down" icon from https://lucide.dev/icons/clock-arrow-down. */
export const ClockArrowDownIcon = /*#__PURE__*/ createLucideIcon(
	"clock-arrow-down",
	registry.clockArrowDown,
);

/** Renders the "clock-arrow-left" icon from https://lucide.dev/icons/clock-arrow-left. */
export const ClockArrowLeftIcon = /*#__PURE__*/ createLucideIcon(
	"clock-arrow-left",
	registry.clockArrowLeft,
);

/** Renders the "clock-arrow-right" icon from https://lucide.dev/icons/clock-arrow-right. */
export const ClockArrowRightIcon = /*#__PURE__*/ createLucideIcon(
	"clock-arrow-right",
	registry.clockArrowRight,
);

/** Renders the "clock-arrow-up" icon from https://lucide.dev/icons/clock-arrow-up. */
export const ClockArrowUpIcon = /*#__PURE__*/ createLucideIcon(
	"clock-arrow-up",
	registry.clockArrowUp,
);

/** Renders the "clock-check" icon from https://lucide.dev/icons/clock-check. */
export const ClockCheckIcon = /*#__PURE__*/ createLucideIcon("clock-check", registry.clockCheck);

/** Renders the "clock-fading" icon from https://lucide.dev/icons/clock-fading. */
export const ClockFadingIcon = /*#__PURE__*/ createLucideIcon("clock-fading", registry.clockFading);

/** Renders the "clock-plus" icon from https://lucide.dev/icons/clock-plus. */
export const ClockPlusIcon = /*#__PURE__*/ createLucideIcon("clock-plus", registry.clockPlus);

/** Renders the "closed-caption" icon from https://lucide.dev/icons/closed-caption. */
export const ClosedCaptionIcon = /*#__PURE__*/ createLucideIcon(
	"closed-caption",
	registry.closedCaption,
);

/** Renders the "cloud" icon from https://lucide.dev/icons/cloud. */
export const CloudIcon = /*#__PURE__*/ createLucideIcon("cloud", registry.cloud);

/** Renders the "cloud-alert" icon from https://lucide.dev/icons/cloud-alert. */
export const CloudAlertIcon = /*#__PURE__*/ createLucideIcon("cloud-alert", registry.cloudAlert);

/** Renders the "cloud-backup" icon from https://lucide.dev/icons/cloud-backup. */
export const CloudBackupIcon = /*#__PURE__*/ createLucideIcon("cloud-backup", registry.cloudBackup);

/** Renders the "cloud-check" icon from https://lucide.dev/icons/cloud-check. */
export const CloudCheckIcon = /*#__PURE__*/ createLucideIcon("cloud-check", registry.cloudCheck);

/** Renders the "cloud-cog" icon from https://lucide.dev/icons/cloud-cog. */
export const CloudCogIcon = /*#__PURE__*/ createLucideIcon("cloud-cog", registry.cloudCog);

/** Renders the "cloud-download" icon from https://lucide.dev/icons/cloud-download. */
export const CloudDownloadIcon = /*#__PURE__*/ createLucideIcon(
	"cloud-download",
	registry.cloudDownload,
);

/** Renders the "cloud-drizzle" icon from https://lucide.dev/icons/cloud-drizzle. */
export const CloudDrizzleIcon = /*#__PURE__*/ createLucideIcon(
	"cloud-drizzle",
	registry.cloudDrizzle,
);

/** Renders the "cloud-fog" icon from https://lucide.dev/icons/cloud-fog. */
export const CloudFogIcon = /*#__PURE__*/ createLucideIcon("cloud-fog", registry.cloudFog);

/** Renders the "cloud-hail" icon from https://lucide.dev/icons/cloud-hail. */
export const CloudHailIcon = /*#__PURE__*/ createLucideIcon("cloud-hail", registry.cloudHail);

/** Renders the "cloud-lightning" icon from https://lucide.dev/icons/cloud-lightning. */
export const CloudLightningIcon = /*#__PURE__*/ createLucideIcon(
	"cloud-lightning",
	registry.cloudLightning,
);

/** Renders the "cloud-moon" icon from https://lucide.dev/icons/cloud-moon. */
export const CloudMoonIcon = /*#__PURE__*/ createLucideIcon("cloud-moon", registry.cloudMoon);

/** Renders the "cloud-moon-rain" icon from https://lucide.dev/icons/cloud-moon-rain. */
export const CloudMoonRainIcon = /*#__PURE__*/ createLucideIcon(
	"cloud-moon-rain",
	registry.cloudMoonRain,
);

/** Renders the "cloud-off" icon from https://lucide.dev/icons/cloud-off. */
export const CloudOffIcon = /*#__PURE__*/ createLucideIcon("cloud-off", registry.cloudOff);

/** Renders the "cloud-rain" icon from https://lucide.dev/icons/cloud-rain. */
export const CloudRainIcon = /*#__PURE__*/ createLucideIcon("cloud-rain", registry.cloudRain);

/** Renders the "cloud-rain-wind" icon from https://lucide.dev/icons/cloud-rain-wind. */
export const CloudRainWindIcon = /*#__PURE__*/ createLucideIcon(
	"cloud-rain-wind",
	registry.cloudRainWind,
);

/** Renders the "cloud-snow" icon from https://lucide.dev/icons/cloud-snow. */
export const CloudSnowIcon = /*#__PURE__*/ createLucideIcon("cloud-snow", registry.cloudSnow);

/** Renders the "cloud-sun" icon from https://lucide.dev/icons/cloud-sun. */
export const CloudSunIcon = /*#__PURE__*/ createLucideIcon("cloud-sun", registry.cloudSun);

/** Renders the "cloud-sun-rain" icon from https://lucide.dev/icons/cloud-sun-rain. */
export const CloudSunRainIcon = /*#__PURE__*/ createLucideIcon(
	"cloud-sun-rain",
	registry.cloudSunRain,
);

/** Renders the "cloud-sync" icon from https://lucide.dev/icons/cloud-sync. */
export const CloudSyncIcon = /*#__PURE__*/ createLucideIcon("cloud-sync", registry.cloudSync);

/** Renders the "cloud-upload" icon from https://lucide.dev/icons/cloud-upload. */
export const CloudUploadIcon = /*#__PURE__*/ createLucideIcon("cloud-upload", registry.cloudUpload);

/** Renders the "cloudy" icon from https://lucide.dev/icons/cloudy. */
export const CloudyIcon = /*#__PURE__*/ createLucideIcon("cloudy", registry.cloudy);

/** Renders the "clover" icon from https://lucide.dev/icons/clover. */
export const CloverIcon = /*#__PURE__*/ createLucideIcon("clover", registry.clover);

/** Renders the "club" icon from https://lucide.dev/icons/club. */
export const ClubIcon = /*#__PURE__*/ createLucideIcon("club", registry.club);

/** Renders the "code" icon from https://lucide.dev/icons/code. */
export const CodeIcon = /*#__PURE__*/ createLucideIcon("code", registry.code);

/** Renders the "code-xml" icon from https://lucide.dev/icons/code-xml. */
export const CodeXmlIcon = /*#__PURE__*/ createLucideIcon("code-xml", registry.codeXml);

/** Renders the "coffee" icon from https://lucide.dev/icons/coffee. */
export const CoffeeIcon = /*#__PURE__*/ createLucideIcon("coffee", registry.coffee);

/** Renders the "cog" icon from https://lucide.dev/icons/cog. */
export const CogIcon = /*#__PURE__*/ createLucideIcon("cog", registry.cog);

/** Renders the "coins" icon from https://lucide.dev/icons/coins. */
export const CoinsIcon = /*#__PURE__*/ createLucideIcon("coins", registry.coins);

/** Renders the "columns-2" icon from https://lucide.dev/icons/columns-2. */
export const Columns2Icon = /*#__PURE__*/ createLucideIcon("columns-2", registry.columns2);

/** Renders the "columns-3" icon from https://lucide.dev/icons/columns-3. */
export const Columns3Icon = /*#__PURE__*/ createLucideIcon("columns-3", registry.columns3);

/** Renders the "columns-3-cog" icon from https://lucide.dev/icons/columns-3-cog. */
export const Columns3CogIcon = /*#__PURE__*/ createLucideIcon(
	"columns-3-cog",
	registry.columns3Cog,
);

/** Renders the "columns-4" icon from https://lucide.dev/icons/columns-4. */
export const Columns4Icon = /*#__PURE__*/ createLucideIcon("columns-4", registry.columns4);

/** Renders the "combine" icon from https://lucide.dev/icons/combine. */
export const CombineIcon = /*#__PURE__*/ createLucideIcon("combine", registry.combine);

/** Renders the "command" icon from https://lucide.dev/icons/command. */
export const CommandIcon = /*#__PURE__*/ createLucideIcon("command", registry.command);

/** Renders the "compass" icon from https://lucide.dev/icons/compass. */
export const CompassIcon = /*#__PURE__*/ createLucideIcon("compass", registry.compass);

/** Renders the "component" icon from https://lucide.dev/icons/component. */
export const ComponentIcon = /*#__PURE__*/ createLucideIcon("component", registry.component);

/** Renders the "computer" icon from https://lucide.dev/icons/computer. */
export const ComputerIcon = /*#__PURE__*/ createLucideIcon("computer", registry.computer);

/** Renders the "concierge-bell" icon from https://lucide.dev/icons/concierge-bell. */
export const ConciergeBellIcon = /*#__PURE__*/ createLucideIcon(
	"concierge-bell",
	registry.conciergeBell,
);

/** Renders the "cone" icon from https://lucide.dev/icons/cone. */
export const ConeIcon = /*#__PURE__*/ createLucideIcon("cone", registry.cone);

/** Renders the "construction" icon from https://lucide.dev/icons/construction. */
export const ConstructionIcon = /*#__PURE__*/ createLucideIcon(
	"construction",
	registry.construction,
);

/** Renders the "contact" icon from https://lucide.dev/icons/contact. */
export const ContactIcon = /*#__PURE__*/ createLucideIcon("contact", registry.contact);

/** Renders the "contact-round" icon from https://lucide.dev/icons/contact-round. */
export const ContactRoundIcon = /*#__PURE__*/ createLucideIcon(
	"contact-round",
	registry.contactRound,
);

/** Renders the "container" icon from https://lucide.dev/icons/container. */
export const ContainerIcon = /*#__PURE__*/ createLucideIcon("container", registry.container);

/** Renders the "contrast" icon from https://lucide.dev/icons/contrast. */
export const ContrastIcon = /*#__PURE__*/ createLucideIcon("contrast", registry.contrast);

/** Renders the "cookie" icon from https://lucide.dev/icons/cookie. */
export const CookieIcon = /*#__PURE__*/ createLucideIcon("cookie", registry.cookie);

/** Renders the "cooking-pot" icon from https://lucide.dev/icons/cooking-pot. */
export const CookingPotIcon = /*#__PURE__*/ createLucideIcon("cooking-pot", registry.cookingPot);

/** Renders the "copy" icon from https://lucide.dev/icons/copy. */
export const CopyIcon = /*#__PURE__*/ createLucideIcon("copy", registry.copy);

/** Renders the "copy-check" icon from https://lucide.dev/icons/copy-check. */
export const CopyCheckIcon = /*#__PURE__*/ createLucideIcon("copy-check", registry.copyCheck);

/** Renders the "copy-minus" icon from https://lucide.dev/icons/copy-minus. */
export const CopyMinusIcon = /*#__PURE__*/ createLucideIcon("copy-minus", registry.copyMinus);

/** Renders the "copy-plus" icon from https://lucide.dev/icons/copy-plus. */
export const CopyPlusIcon = /*#__PURE__*/ createLucideIcon("copy-plus", registry.copyPlus);

/** Renders the "copy-slash" icon from https://lucide.dev/icons/copy-slash. */
export const CopySlashIcon = /*#__PURE__*/ createLucideIcon("copy-slash", registry.copySlash);

/** Renders the "copy-x" icon from https://lucide.dev/icons/copy-x. */
export const CopyXIcon = /*#__PURE__*/ createLucideIcon("copy-x", registry.copyX);

/** Renders the "copyleft" icon from https://lucide.dev/icons/copyleft. */
export const CopyleftIcon = /*#__PURE__*/ createLucideIcon("copyleft", registry.copyleft);

/** Renders the "copyright" icon from https://lucide.dev/icons/copyright. */
export const CopyrightIcon = /*#__PURE__*/ createLucideIcon("copyright", registry.copyright);

/** Renders the "corner-down-left" icon from https://lucide.dev/icons/corner-down-left. */
export const CornerDownLeftIcon = /*#__PURE__*/ createLucideIcon(
	"corner-down-left",
	registry.cornerDownLeft,
);

/** Renders the "corner-down-right" icon from https://lucide.dev/icons/corner-down-right. */
export const CornerDownRightIcon = /*#__PURE__*/ createLucideIcon(
	"corner-down-right",
	registry.cornerDownRight,
);

/** Renders the "corner-left-down" icon from https://lucide.dev/icons/corner-left-down. */
export const CornerLeftDownIcon = /*#__PURE__*/ createLucideIcon(
	"corner-left-down",
	registry.cornerLeftDown,
);

/** Renders the "corner-left-up" icon from https://lucide.dev/icons/corner-left-up. */
export const CornerLeftUpIcon = /*#__PURE__*/ createLucideIcon(
	"corner-left-up",
	registry.cornerLeftUp,
);

/** Renders the "corner-right-down" icon from https://lucide.dev/icons/corner-right-down. */
export const CornerRightDownIcon = /*#__PURE__*/ createLucideIcon(
	"corner-right-down",
	registry.cornerRightDown,
);

/** Renders the "corner-right-up" icon from https://lucide.dev/icons/corner-right-up. */
export const CornerRightUpIcon = /*#__PURE__*/ createLucideIcon(
	"corner-right-up",
	registry.cornerRightUp,
);

/** Renders the "corner-up-left" icon from https://lucide.dev/icons/corner-up-left. */
export const CornerUpLeftIcon = /*#__PURE__*/ createLucideIcon(
	"corner-up-left",
	registry.cornerUpLeft,
);

/** Renders the "corner-up-right" icon from https://lucide.dev/icons/corner-up-right. */
export const CornerUpRightIcon = /*#__PURE__*/ createLucideIcon(
	"corner-up-right",
	registry.cornerUpRight,
);

/** Renders the "cpu" icon from https://lucide.dev/icons/cpu. */
export const CpuIcon = /*#__PURE__*/ createLucideIcon("cpu", registry.cpu);

/** Renders the "cpu-ai" icon from https://lucide.dev/icons/cpu-ai. */
export const CpuAiIcon = /*#__PURE__*/ createLucideIcon("cpu-ai", registry.cpuAi);

/** Renders the "creative-commons" icon from https://lucide.dev/icons/creative-commons. */
export const CreativeCommonsIcon = /*#__PURE__*/ createLucideIcon(
	"creative-commons",
	registry.creativeCommons,
);

/** Renders the "credit-card" icon from https://lucide.dev/icons/credit-card. */
export const CreditCardIcon = /*#__PURE__*/ createLucideIcon("credit-card", registry.creditCard);

/** Renders the "credit-card-check" icon from https://lucide.dev/icons/credit-card-check. */
export const CreditCardCheckIcon = /*#__PURE__*/ createLucideIcon(
	"credit-card-check",
	registry.creditCardCheck,
);

/** Renders the "credit-card-minus" icon from https://lucide.dev/icons/credit-card-minus. */
export const CreditCardMinusIcon = /*#__PURE__*/ createLucideIcon(
	"credit-card-minus",
	registry.creditCardMinus,
);

/** Renders the "credit-card-plus" icon from https://lucide.dev/icons/credit-card-plus. */
export const CreditCardPlusIcon = /*#__PURE__*/ createLucideIcon(
	"credit-card-plus",
	registry.creditCardPlus,
);

/** Renders the "credit-card-reader" icon from https://lucide.dev/icons/credit-card-reader. */
export const CreditCardReaderIcon = /*#__PURE__*/ createLucideIcon(
	"credit-card-reader",
	registry.creditCardReader,
);

/** Renders the "credit-card-x" icon from https://lucide.dev/icons/credit-card-x. */
export const CreditCardXIcon = /*#__PURE__*/ createLucideIcon(
	"credit-card-x",
	registry.creditCardX,
);

/** Renders the "croissant" icon from https://lucide.dev/icons/croissant. */
export const CroissantIcon = /*#__PURE__*/ createLucideIcon("croissant", registry.croissant);

/** Renders the "crop" icon from https://lucide.dev/icons/crop. */
export const CropIcon = /*#__PURE__*/ createLucideIcon("crop", registry.crop);

/** Renders the "cross" icon from https://lucide.dev/icons/cross. */
export const CrossIcon = /*#__PURE__*/ createLucideIcon("cross", registry.cross);

/** Renders the "crosshair" icon from https://lucide.dev/icons/crosshair. */
export const CrosshairIcon = /*#__PURE__*/ createLucideIcon("crosshair", registry.crosshair);

/** Renders the "crown" icon from https://lucide.dev/icons/crown. */
export const CrownIcon = /*#__PURE__*/ createLucideIcon("crown", registry.crown);

/** Renders the "cuboid" icon from https://lucide.dev/icons/cuboid. */
export const CuboidIcon = /*#__PURE__*/ createLucideIcon("cuboid", registry.cuboid);

/** Renders the "cup-soda" icon from https://lucide.dev/icons/cup-soda. */
export const CupSodaIcon = /*#__PURE__*/ createLucideIcon("cup-soda", registry.cupSoda);

/** Renders the "cupcake" icon from https://lucide.dev/icons/cupcake. */
export const CupcakeIcon = /*#__PURE__*/ createLucideIcon("cupcake", registry.cupcake);

/** Renders the "currency" icon from https://lucide.dev/icons/currency. */
export const CurrencyIcon = /*#__PURE__*/ createLucideIcon("currency", registry.currency);

/** Renders the "cylinder" icon from https://lucide.dev/icons/cylinder. */
export const CylinderIcon = /*#__PURE__*/ createLucideIcon("cylinder", registry.cylinder);

/** Renders the "dam" icon from https://lucide.dev/icons/dam. */
export const DamIcon = /*#__PURE__*/ createLucideIcon("dam", registry.dam);

/** Renders the "database" icon from https://lucide.dev/icons/database. */
export const DatabaseIcon = /*#__PURE__*/ createLucideIcon("database", registry.database);

/** Renders the "database-arrow-down" icon from https://lucide.dev/icons/database-arrow-down. */
export const DatabaseArrowDownIcon = /*#__PURE__*/ createLucideIcon(
	"database-arrow-down",
	registry.databaseArrowDown,
);

/** Renders the "database-arrow-up" icon from https://lucide.dev/icons/database-arrow-up. */
export const DatabaseArrowUpIcon = /*#__PURE__*/ createLucideIcon(
	"database-arrow-up",
	registry.databaseArrowUp,
);

/** Renders the "database-backup" icon from https://lucide.dev/icons/database-backup. */
export const DatabaseBackupIcon = /*#__PURE__*/ createLucideIcon(
	"database-backup",
	registry.databaseBackup,
);

/** Renders the "database-check" icon from https://lucide.dev/icons/database-check. */
export const DatabaseCheckIcon = /*#__PURE__*/ createLucideIcon(
	"database-check",
	registry.databaseCheck,
);

/** Renders the "database-minus" icon from https://lucide.dev/icons/database-minus. */
export const DatabaseMinusIcon = /*#__PURE__*/ createLucideIcon(
	"database-minus",
	registry.databaseMinus,
);

/** Renders the "database-plus" icon from https://lucide.dev/icons/database-plus. */
export const DatabasePlusIcon = /*#__PURE__*/ createLucideIcon(
	"database-plus",
	registry.databasePlus,
);

/** Renders the "database-search" icon from https://lucide.dev/icons/database-search. */
export const DatabaseSearchIcon = /*#__PURE__*/ createLucideIcon(
	"database-search",
	registry.databaseSearch,
);

/** Renders the "database-x" icon from https://lucide.dev/icons/database-x. */
export const DatabaseXIcon = /*#__PURE__*/ createLucideIcon("database-x", registry.databaseX);

/** Renders the "database-zap" icon from https://lucide.dev/icons/database-zap. */
export const DatabaseZapIcon = /*#__PURE__*/ createLucideIcon("database-zap", registry.databaseZap);

/** Renders the "decimals-arrow-left" icon from https://lucide.dev/icons/decimals-arrow-left. */
export const DecimalsArrowLeftIcon = /*#__PURE__*/ createLucideIcon(
	"decimals-arrow-left",
	registry.decimalsArrowLeft,
);

/** Renders the "decimals-arrow-right" icon from https://lucide.dev/icons/decimals-arrow-right. */
export const DecimalsArrowRightIcon = /*#__PURE__*/ createLucideIcon(
	"decimals-arrow-right",
	registry.decimalsArrowRight,
);

/** Renders the "delete" icon from https://lucide.dev/icons/delete. */
export const DeleteIcon = /*#__PURE__*/ createLucideIcon("delete", registry.deleteNode);

/** Renders the "dessert" icon from https://lucide.dev/icons/dessert. */
export const DessertIcon = /*#__PURE__*/ createLucideIcon("dessert", registry.dessert);

/** Renders the "diameter" icon from https://lucide.dev/icons/diameter. */
export const DiameterIcon = /*#__PURE__*/ createLucideIcon("diameter", registry.diameter);

/** Renders the "diamond" icon from https://lucide.dev/icons/diamond. */
export const DiamondIcon = /*#__PURE__*/ createLucideIcon("diamond", registry.diamond);

/** Renders the "diamond-minus" icon from https://lucide.dev/icons/diamond-minus. */
export const DiamondMinusIcon = /*#__PURE__*/ createLucideIcon(
	"diamond-minus",
	registry.diamondMinus,
);

/** Renders the "diamond-percent" icon from https://lucide.dev/icons/diamond-percent. */
export const DiamondPercentIcon = /*#__PURE__*/ createLucideIcon(
	"diamond-percent",
	registry.diamondPercent,
);

/** Renders the "diamond-plus" icon from https://lucide.dev/icons/diamond-plus. */
export const DiamondPlusIcon = /*#__PURE__*/ createLucideIcon("diamond-plus", registry.diamondPlus);

/** Renders the "dice-1" icon from https://lucide.dev/icons/dice-1. */
export const Dice1Icon = /*#__PURE__*/ createLucideIcon("dice-1", registry.dice1);

/** Renders the "dice-2" icon from https://lucide.dev/icons/dice-2. */
export const Dice2Icon = /*#__PURE__*/ createLucideIcon("dice-2", registry.dice2);

/** Renders the "dice-3" icon from https://lucide.dev/icons/dice-3. */
export const Dice3Icon = /*#__PURE__*/ createLucideIcon("dice-3", registry.dice3);

/** Renders the "dice-4" icon from https://lucide.dev/icons/dice-4. */
export const Dice4Icon = /*#__PURE__*/ createLucideIcon("dice-4", registry.dice4);

/** Renders the "dice-5" icon from https://lucide.dev/icons/dice-5. */
export const Dice5Icon = /*#__PURE__*/ createLucideIcon("dice-5", registry.dice5);

/** Renders the "dice-6" icon from https://lucide.dev/icons/dice-6. */
export const Dice6Icon = /*#__PURE__*/ createLucideIcon("dice-6", registry.dice6);

/** Renders the "dices" icon from https://lucide.dev/icons/dices. */
export const DicesIcon = /*#__PURE__*/ createLucideIcon("dices", registry.dices);

/** Renders the "diff" icon from https://lucide.dev/icons/diff. */
export const DiffIcon = /*#__PURE__*/ createLucideIcon("diff", registry.diff);

/** Renders the "disc" icon from https://lucide.dev/icons/disc. */
export const DiscIcon = /*#__PURE__*/ createLucideIcon("disc", registry.disc);

/** Renders the "disc-2" icon from https://lucide.dev/icons/disc-2. */
export const Disc2Icon = /*#__PURE__*/ createLucideIcon("disc-2", registry.disc2);

/** Renders the "disc-3" icon from https://lucide.dev/icons/disc-3. */
export const Disc3Icon = /*#__PURE__*/ createLucideIcon("disc-3", registry.disc3);

/** Renders the "disc-album" icon from https://lucide.dev/icons/disc-album. */
export const DiscAlbumIcon = /*#__PURE__*/ createLucideIcon("disc-album", registry.discAlbum);

/** Renders the "divide" icon from https://lucide.dev/icons/divide. */
export const DivideIcon = /*#__PURE__*/ createLucideIcon("divide", registry.divide);

/** Renders the "dna" icon from https://lucide.dev/icons/dna. */
export const DnaIcon = /*#__PURE__*/ createLucideIcon("dna", registry.dna);

/** Renders the "dna-off" icon from https://lucide.dev/icons/dna-off. */
export const DnaOffIcon = /*#__PURE__*/ createLucideIcon("dna-off", registry.dnaOff);

/** Renders the "dock" icon from https://lucide.dev/icons/dock. */
export const DockIcon = /*#__PURE__*/ createLucideIcon("dock", registry.dock);

/** Renders the "dog" icon from https://lucide.dev/icons/dog. */
export const DogIcon = /*#__PURE__*/ createLucideIcon("dog", registry.dog);

/** Renders the "dollar-sign" icon from https://lucide.dev/icons/dollar-sign. */
export const DollarSignIcon = /*#__PURE__*/ createLucideIcon("dollar-sign", registry.dollarSign);

/** Renders the "dome" icon from https://lucide.dev/icons/dome. */
export const DomeIcon = /*#__PURE__*/ createLucideIcon("dome", registry.dome);

/** Renders the "donut" icon from https://lucide.dev/icons/donut. */
export const DonutIcon = /*#__PURE__*/ createLucideIcon("donut", registry.donut);

/** Renders the "door-closed" icon from https://lucide.dev/icons/door-closed. */
export const DoorClosedIcon = /*#__PURE__*/ createLucideIcon("door-closed", registry.doorClosed);

/** Renders the "door-closed-cog" icon from https://lucide.dev/icons/door-closed-cog. */
export const DoorClosedCogIcon = /*#__PURE__*/ createLucideIcon(
	"door-closed-cog",
	registry.doorClosedCog,
);

/** Renders the "door-closed-locked" icon from https://lucide.dev/icons/door-closed-locked. */
export const DoorClosedLockedIcon = /*#__PURE__*/ createLucideIcon(
	"door-closed-locked",
	registry.doorClosedLocked,
);

/** Renders the "door-closed-package" icon from https://lucide.dev/icons/door-closed-package. */
export const DoorClosedPackageIcon = /*#__PURE__*/ createLucideIcon(
	"door-closed-package",
	registry.doorClosedPackage,
);

/** Renders the "door-open" icon from https://lucide.dev/icons/door-open. */
export const DoorOpenIcon = /*#__PURE__*/ createLucideIcon("door-open", registry.doorOpen);

/** Renders the "door-stairwell" icon from https://lucide.dev/icons/door-stairwell. */
export const DoorStairwellIcon = /*#__PURE__*/ createLucideIcon(
	"door-stairwell",
	registry.doorStairwell,
);

/** Renders the "dot" icon from https://lucide.dev/icons/dot. */
export const DotIcon = /*#__PURE__*/ createLucideIcon("dot", registry.dot);

/** Renders the "download" icon from https://lucide.dev/icons/download. */
export const DownloadIcon = /*#__PURE__*/ createLucideIcon("download", registry.download);

/** Renders the "drafting-compass" icon from https://lucide.dev/icons/drafting-compass. */
export const DraftingCompassIcon = /*#__PURE__*/ createLucideIcon(
	"drafting-compass",
	registry.draftingCompass,
);

/** Renders the "drama" icon from https://lucide.dev/icons/drama. */
export const DramaIcon = /*#__PURE__*/ createLucideIcon("drama", registry.drama);

/** Renders the "drill" icon from https://lucide.dev/icons/drill. */
export const DrillIcon = /*#__PURE__*/ createLucideIcon("drill", registry.drill);

/** Renders the "drone" icon from https://lucide.dev/icons/drone. */
export const DroneIcon = /*#__PURE__*/ createLucideIcon("drone", registry.drone);

/** Renders the "droplet" icon from https://lucide.dev/icons/droplet. */
export const DropletIcon = /*#__PURE__*/ createLucideIcon("droplet", registry.droplet);

/** Renders the "droplet-off" icon from https://lucide.dev/icons/droplet-off. */
export const DropletOffIcon = /*#__PURE__*/ createLucideIcon("droplet-off", registry.dropletOff);

/** Renders the "droplets" icon from https://lucide.dev/icons/droplets. */
export const DropletsIcon = /*#__PURE__*/ createLucideIcon("droplets", registry.droplets);

/** Renders the "drum" icon from https://lucide.dev/icons/drum. */
export const DrumIcon = /*#__PURE__*/ createLucideIcon("drum", registry.drum);

/** Renders the "drumstick" icon from https://lucide.dev/icons/drumstick. */
export const DrumstickIcon = /*#__PURE__*/ createLucideIcon("drumstick", registry.drumstick);

/** Renders the "dumbbell" icon from https://lucide.dev/icons/dumbbell. */
export const DumbbellIcon = /*#__PURE__*/ createLucideIcon("dumbbell", registry.dumbbell);

/** Renders the "ear" icon from https://lucide.dev/icons/ear. */
export const EarIcon = /*#__PURE__*/ createLucideIcon("ear", registry.ear);

/** Renders the "ear-off" icon from https://lucide.dev/icons/ear-off. */
export const EarOffIcon = /*#__PURE__*/ createLucideIcon("ear-off", registry.earOff);

/** Renders the "earth" icon from https://lucide.dev/icons/earth. */
export const EarthIcon = /*#__PURE__*/ createLucideIcon("earth", registry.earth);

/** Renders the "earth-lock" icon from https://lucide.dev/icons/earth-lock. */
export const EarthLockIcon = /*#__PURE__*/ createLucideIcon("earth-lock", registry.earthLock);

/** Renders the "eclipse" icon from https://lucide.dev/icons/eclipse. */
export const EclipseIcon = /*#__PURE__*/ createLucideIcon("eclipse", registry.eclipse);

/** Renders the "egg" icon from https://lucide.dev/icons/egg. */
export const EggIcon = /*#__PURE__*/ createLucideIcon("egg", registry.egg);

/** Renders the "egg-fried" icon from https://lucide.dev/icons/egg-fried. */
export const EggFriedIcon = /*#__PURE__*/ createLucideIcon("egg-fried", registry.eggFried);

/** Renders the "egg-off" icon from https://lucide.dev/icons/egg-off. */
export const EggOffIcon = /*#__PURE__*/ createLucideIcon("egg-off", registry.eggOff);

/** Renders the "eighth-note" icon from https://lucide.dev/icons/eighth-note. */
export const EighthNoteIcon = /*#__PURE__*/ createLucideIcon("eighth-note", registry.eighthNote);

/** Renders the "eighth-notes" icon from https://lucide.dev/icons/eighth-notes. */
export const EighthNotesIcon = /*#__PURE__*/ createLucideIcon("eighth-notes", registry.eighthNotes);

/** Renders the "eject" icon from https://lucide.dev/icons/eject. */
export const EjectIcon = /*#__PURE__*/ createLucideIcon("eject", registry.eject);

/** Renders the "ellipse" icon from https://lucide.dev/icons/ellipse. */
export const EllipseIcon = /*#__PURE__*/ createLucideIcon("ellipse", registry.ellipse);

/** Renders the "ellipsis" icon from https://lucide.dev/icons/ellipsis. */
export const EllipsisIcon = /*#__PURE__*/ createLucideIcon("ellipsis", registry.ellipsis);

/** Renders the "ellipsis-vertical" icon from https://lucide.dev/icons/ellipsis-vertical. */
export const EllipsisVerticalIcon = /*#__PURE__*/ createLucideIcon(
	"ellipsis-vertical",
	registry.ellipsisVertical,
);

/** Renders the "engine" icon from https://lucide.dev/icons/engine. */
export const EngineIcon = /*#__PURE__*/ createLucideIcon("engine", registry.engine);

/** Renders the "equal" icon from https://lucide.dev/icons/equal. */
export const EqualIcon = /*#__PURE__*/ createLucideIcon("equal", registry.equal);

/** Renders the "equal-approximately" icon from https://lucide.dev/icons/equal-approximately. */
export const EqualApproximatelyIcon = /*#__PURE__*/ createLucideIcon(
	"equal-approximately",
	registry.equalApproximately,
);

/** Renders the "equal-approximately-not" icon from https://lucide.dev/icons/equal-approximately-not. */
export const EqualApproximatelyNotIcon = /*#__PURE__*/ createLucideIcon(
	"equal-approximately-not",
	registry.equalApproximatelyNot,
);

/** Renders the "equal-not" icon from https://lucide.dev/icons/equal-not. */
export const EqualNotIcon = /*#__PURE__*/ createLucideIcon("equal-not", registry.equalNot);

/** Renders the "eraser" icon from https://lucide.dev/icons/eraser. */
export const EraserIcon = /*#__PURE__*/ createLucideIcon("eraser", registry.eraser);

/** Renders the "ethernet-port" icon from https://lucide.dev/icons/ethernet-port. */
export const EthernetPortIcon = /*#__PURE__*/ createLucideIcon(
	"ethernet-port",
	registry.ethernetPort,
);

/** Renders the "euro" icon from https://lucide.dev/icons/euro. */
export const EuroIcon = /*#__PURE__*/ createLucideIcon("euro", registry.euro);

/** Renders the "ev-charger" icon from https://lucide.dev/icons/ev-charger. */
export const EvChargerIcon = /*#__PURE__*/ createLucideIcon("ev-charger", registry.evCharger);

/** Renders the "expand" icon from https://lucide.dev/icons/expand. */
export const ExpandIcon = /*#__PURE__*/ createLucideIcon("expand", registry.expand);

/** Renders the "external-link" icon from https://lucide.dev/icons/external-link. */
export const ExternalLinkIcon = /*#__PURE__*/ createLucideIcon(
	"external-link",
	registry.externalLink,
);

/** Renders the "eye" icon from https://lucide.dev/icons/eye. */
export const EyeIcon = /*#__PURE__*/ createLucideIcon("eye", registry.eye);

/** Renders the "eye-closed" icon from https://lucide.dev/icons/eye-closed. */
export const EyeClosedIcon = /*#__PURE__*/ createLucideIcon("eye-closed", registry.eyeClosed);

/** Renders the "eye-dashed" icon from https://lucide.dev/icons/eye-dashed. */
export const EyeDashedIcon = /*#__PURE__*/ createLucideIcon("eye-dashed", registry.eyeDashed);

/** Renders the "eye-off" icon from https://lucide.dev/icons/eye-off. */
export const EyeOffIcon = /*#__PURE__*/ createLucideIcon("eye-off", registry.eyeOff);

/** Renders the "face-angry" icon from https://lucide.dev/icons/face-angry. */
export const FaceAngryIcon = /*#__PURE__*/ createLucideIcon("face-angry", registry.faceAngry);

/** Renders the "face-expressionless" icon from https://lucide.dev/icons/face-expressionless. */
export const FaceExpressionlessIcon = /*#__PURE__*/ createLucideIcon(
	"face-expressionless",
	registry.faceExpressionless,
);

/** Renders the "face-grinning" icon from https://lucide.dev/icons/face-grinning. */
export const FaceGrinningIcon = /*#__PURE__*/ createLucideIcon(
	"face-grinning",
	registry.faceGrinning,
);

/** Renders the "face-neutral" icon from https://lucide.dev/icons/face-neutral. */
export const FaceNeutralIcon = /*#__PURE__*/ createLucideIcon("face-neutral", registry.faceNeutral);

/** Renders the "face-slightly-frowning" icon from https://lucide.dev/icons/face-slightly-frowning. */
export const FaceSlightlyFrowningIcon = /*#__PURE__*/ createLucideIcon(
	"face-slightly-frowning",
	registry.faceSlightlyFrowning,
);

/** Renders the "face-slightly-smiling" icon from https://lucide.dev/icons/face-slightly-smiling. */
export const FaceSlightlySmilingIcon = /*#__PURE__*/ createLucideIcon(
	"face-slightly-smiling",
	registry.faceSlightlySmiling,
);

/** Renders the "face-slightly-smiling-plus" icon from https://lucide.dev/icons/face-slightly-smiling-plus. */
export const FaceSlightlySmilingPlusIcon = /*#__PURE__*/ createLucideIcon(
	"face-slightly-smiling-plus",
	registry.faceSlightlySmilingPlus,
);

/** Renders the "factory" icon from https://lucide.dev/icons/factory. */
export const FactoryIcon = /*#__PURE__*/ createLucideIcon("factory", registry.factory);

/** Renders the "fan" icon from https://lucide.dev/icons/fan. */
export const FanIcon = /*#__PURE__*/ createLucideIcon("fan", registry.fan);

/** Renders the "fast-forward" icon from https://lucide.dev/icons/fast-forward. */
export const FastForwardIcon = /*#__PURE__*/ createLucideIcon("fast-forward", registry.fastForward);

/** Renders the "faucet" icon from https://lucide.dev/icons/faucet. */
export const FaucetIcon = /*#__PURE__*/ createLucideIcon("faucet", registry.faucet);

/** Renders the "feather" icon from https://lucide.dev/icons/feather. */
export const FeatherIcon = /*#__PURE__*/ createLucideIcon("feather", registry.feather);

/** Renders the "fence" icon from https://lucide.dev/icons/fence. */
export const FenceIcon = /*#__PURE__*/ createLucideIcon("fence", registry.fence);

/** Renders the "ferris-wheel" icon from https://lucide.dev/icons/ferris-wheel. */
export const FerrisWheelIcon = /*#__PURE__*/ createLucideIcon("ferris-wheel", registry.ferrisWheel);

/** Renders the "file" icon from https://lucide.dev/icons/file. */
export const FileIcon = /*#__PURE__*/ createLucideIcon("file", registry.file);

/** Renders the "file-archive" icon from https://lucide.dev/icons/file-archive. */
export const FileArchiveIcon = /*#__PURE__*/ createLucideIcon("file-archive", registry.fileArchive);

/** Renders the "file-axis-3d" icon from https://lucide.dev/icons/file-axis-3d. */
export const FileAxis3dIcon = /*#__PURE__*/ createLucideIcon("file-axis-3d", registry.fileAxis3d);

/** Renders the "file-badge" icon from https://lucide.dev/icons/file-badge. */
export const FileBadgeIcon = /*#__PURE__*/ createLucideIcon("file-badge", registry.fileBadge);

/** Renders the "file-box" icon from https://lucide.dev/icons/file-box. */
export const FileBoxIcon = /*#__PURE__*/ createLucideIcon("file-box", registry.fileBox);

/** Renders the "file-braces" icon from https://lucide.dev/icons/file-braces. */
export const FileBracesIcon = /*#__PURE__*/ createLucideIcon("file-braces", registry.fileBraces);

/** Renders the "file-braces-corner" icon from https://lucide.dev/icons/file-braces-corner. */
export const FileBracesCornerIcon = /*#__PURE__*/ createLucideIcon(
	"file-braces-corner",
	registry.fileBracesCorner,
);

/** Renders the "file-chart-column" icon from https://lucide.dev/icons/file-chart-column. */
export const FileChartColumnIcon = /*#__PURE__*/ createLucideIcon(
	"file-chart-column",
	registry.fileChartColumn,
);

/** Renders the "file-chart-column-increasing" icon from https://lucide.dev/icons/file-chart-column-increasing. */
export const FileChartColumnIncreasingIcon = /*#__PURE__*/ createLucideIcon(
	"file-chart-column-increasing",
	registry.fileChartColumnIncreasing,
);

/** Renders the "file-chart-line" icon from https://lucide.dev/icons/file-chart-line. */
export const FileChartLineIcon = /*#__PURE__*/ createLucideIcon(
	"file-chart-line",
	registry.fileChartLine,
);

/** Renders the "file-chart-pie" icon from https://lucide.dev/icons/file-chart-pie. */
export const FileChartPieIcon = /*#__PURE__*/ createLucideIcon(
	"file-chart-pie",
	registry.fileChartPie,
);

/** Renders the "file-check" icon from https://lucide.dev/icons/file-check. */
export const FileCheckIcon = /*#__PURE__*/ createLucideIcon("file-check", registry.fileCheck);

/** Renders the "file-check-corner" icon from https://lucide.dev/icons/file-check-corner. */
export const FileCheckCornerIcon = /*#__PURE__*/ createLucideIcon(
	"file-check-corner",
	registry.fileCheckCorner,
);

/** Renders the "file-clock" icon from https://lucide.dev/icons/file-clock. */
export const FileClockIcon = /*#__PURE__*/ createLucideIcon("file-clock", registry.fileClock);

/** Renders the "file-code" icon from https://lucide.dev/icons/file-code. */
export const FileCodeIcon = /*#__PURE__*/ createLucideIcon("file-code", registry.fileCode);

/** Renders the "file-code-corner" icon from https://lucide.dev/icons/file-code-corner. */
export const FileCodeCornerIcon = /*#__PURE__*/ createLucideIcon(
	"file-code-corner",
	registry.fileCodeCorner,
);

/** Renders the "file-cog" icon from https://lucide.dev/icons/file-cog. */
export const FileCogIcon = /*#__PURE__*/ createLucideIcon("file-cog", registry.fileCog);

/** Renders the "file-diff" icon from https://lucide.dev/icons/file-diff. */
export const FileDiffIcon = /*#__PURE__*/ createLucideIcon("file-diff", registry.fileDiff);

/** Renders the "file-digit" icon from https://lucide.dev/icons/file-digit. */
export const FileDigitIcon = /*#__PURE__*/ createLucideIcon("file-digit", registry.fileDigit);

/** Renders the "file-down" icon from https://lucide.dev/icons/file-down. */
export const FileDownIcon = /*#__PURE__*/ createLucideIcon("file-down", registry.fileDown);

/** Renders the "file-exclamation-point" icon from https://lucide.dev/icons/file-exclamation-point. */
export const FileExclamationPointIcon = /*#__PURE__*/ createLucideIcon(
	"file-exclamation-point",
	registry.fileExclamationPoint,
);

/** Renders the "file-headphone" icon from https://lucide.dev/icons/file-headphone. */
export const FileHeadphoneIcon = /*#__PURE__*/ createLucideIcon(
	"file-headphone",
	registry.fileHeadphone,
);

/** Renders the "file-heart" icon from https://lucide.dev/icons/file-heart. */
export const FileHeartIcon = /*#__PURE__*/ createLucideIcon("file-heart", registry.fileHeart);

/** Renders the "file-image" icon from https://lucide.dev/icons/file-image. */
export const FileImageIcon = /*#__PURE__*/ createLucideIcon("file-image", registry.fileImage);

/** Renders the "file-input" icon from https://lucide.dev/icons/file-input. */
export const FileInputIcon = /*#__PURE__*/ createLucideIcon("file-input", registry.fileInput);

/** Renders the "file-key" icon from https://lucide.dev/icons/file-key. */
export const FileKeyIcon = /*#__PURE__*/ createLucideIcon("file-key", registry.fileKey);

/** Renders the "file-lock" icon from https://lucide.dev/icons/file-lock. */
export const FileLockIcon = /*#__PURE__*/ createLucideIcon("file-lock", registry.fileLock);

/** Renders the "file-minus" icon from https://lucide.dev/icons/file-minus. */
export const FileMinusIcon = /*#__PURE__*/ createLucideIcon("file-minus", registry.fileMinus);

/** Renders the "file-minus-corner" icon from https://lucide.dev/icons/file-minus-corner. */
export const FileMinusCornerIcon = /*#__PURE__*/ createLucideIcon(
	"file-minus-corner",
	registry.fileMinusCorner,
);

/** Renders the "file-music" icon from https://lucide.dev/icons/file-music. */
export const FileMusicIcon = /*#__PURE__*/ createLucideIcon("file-music", registry.fileMusic);

/** Renders the "file-output" icon from https://lucide.dev/icons/file-output. */
export const FileOutputIcon = /*#__PURE__*/ createLucideIcon("file-output", registry.fileOutput);

/** Renders the "file-pen" icon from https://lucide.dev/icons/file-pen. */
export const FilePenIcon = /*#__PURE__*/ createLucideIcon("file-pen", registry.filePen);

/** Renders the "file-pen-line" icon from https://lucide.dev/icons/file-pen-line. */
export const FilePenLineIcon = /*#__PURE__*/ createLucideIcon(
	"file-pen-line",
	registry.filePenLine,
);

/** Renders the "file-play" icon from https://lucide.dev/icons/file-play. */
export const FilePlayIcon = /*#__PURE__*/ createLucideIcon("file-play", registry.filePlay);

/** Renders the "file-plus" icon from https://lucide.dev/icons/file-plus. */
export const FilePlusIcon = /*#__PURE__*/ createLucideIcon("file-plus", registry.filePlus);

/** Renders the "file-plus-corner" icon from https://lucide.dev/icons/file-plus-corner. */
export const FilePlusCornerIcon = /*#__PURE__*/ createLucideIcon(
	"file-plus-corner",
	registry.filePlusCorner,
);

/** Renders the "file-question-mark" icon from https://lucide.dev/icons/file-question-mark. */
export const FileQuestionMarkIcon = /*#__PURE__*/ createLucideIcon(
	"file-question-mark",
	registry.fileQuestionMark,
);

/** Renders the "file-scan" icon from https://lucide.dev/icons/file-scan. */
export const FileScanIcon = /*#__PURE__*/ createLucideIcon("file-scan", registry.fileScan);

/** Renders the "file-search" icon from https://lucide.dev/icons/file-search. */
export const FileSearchIcon = /*#__PURE__*/ createLucideIcon("file-search", registry.fileSearch);

/** Renders the "file-search-corner" icon from https://lucide.dev/icons/file-search-corner. */
export const FileSearchCornerIcon = /*#__PURE__*/ createLucideIcon(
	"file-search-corner",
	registry.fileSearchCorner,
);

/** Renders the "file-signal" icon from https://lucide.dev/icons/file-signal. */
export const FileSignalIcon = /*#__PURE__*/ createLucideIcon("file-signal", registry.fileSignal);

/** Renders the "file-sliders" icon from https://lucide.dev/icons/file-sliders. */
export const FileSlidersIcon = /*#__PURE__*/ createLucideIcon("file-sliders", registry.fileSliders);

/** Renders the "file-spreadsheet" icon from https://lucide.dev/icons/file-spreadsheet. */
export const FileSpreadsheetIcon = /*#__PURE__*/ createLucideIcon(
	"file-spreadsheet",
	registry.fileSpreadsheet,
);

/** Renders the "file-stack" icon from https://lucide.dev/icons/file-stack. */
export const FileStackIcon = /*#__PURE__*/ createLucideIcon("file-stack", registry.fileStack);

/** Renders the "file-symlink" icon from https://lucide.dev/icons/file-symlink. */
export const FileSymlinkIcon = /*#__PURE__*/ createLucideIcon("file-symlink", registry.fileSymlink);

/** Renders the "file-terminal" icon from https://lucide.dev/icons/file-terminal. */
export const FileTerminalIcon = /*#__PURE__*/ createLucideIcon(
	"file-terminal",
	registry.fileTerminal,
);

/** Renders the "file-text" icon from https://lucide.dev/icons/file-text. */
export const FileTextIcon = /*#__PURE__*/ createLucideIcon("file-text", registry.fileText);

/** Renders the "file-type" icon from https://lucide.dev/icons/file-type. */
export const FileTypeIcon = /*#__PURE__*/ createLucideIcon("file-type", registry.fileType);

/** Renders the "file-type-corner" icon from https://lucide.dev/icons/file-type-corner. */
export const FileTypeCornerIcon = /*#__PURE__*/ createLucideIcon(
	"file-type-corner",
	registry.fileTypeCorner,
);

/** Renders the "file-up" icon from https://lucide.dev/icons/file-up. */
export const FileUpIcon = /*#__PURE__*/ createLucideIcon("file-up", registry.fileUp);

/** Renders the "file-user" icon from https://lucide.dev/icons/file-user. */
export const FileUserIcon = /*#__PURE__*/ createLucideIcon("file-user", registry.fileUser);

/** Renders the "file-video-camera" icon from https://lucide.dev/icons/file-video-camera. */
export const FileVideoCameraIcon = /*#__PURE__*/ createLucideIcon(
	"file-video-camera",
	registry.fileVideoCamera,
);

/** Renders the "file-volume" icon from https://lucide.dev/icons/file-volume. */
export const FileVolumeIcon = /*#__PURE__*/ createLucideIcon("file-volume", registry.fileVolume);

/** Renders the "file-x" icon from https://lucide.dev/icons/file-x. */
export const FileXIcon = /*#__PURE__*/ createLucideIcon("file-x", registry.fileX);

/** Renders the "file-x-corner" icon from https://lucide.dev/icons/file-x-corner. */
export const FileXCornerIcon = /*#__PURE__*/ createLucideIcon(
	"file-x-corner",
	registry.fileXCorner,
);

/** Renders the "files" icon from https://lucide.dev/icons/files. */
export const FilesIcon = /*#__PURE__*/ createLucideIcon("files", registry.files);

/** Renders the "film" icon from https://lucide.dev/icons/film. */
export const FilmIcon = /*#__PURE__*/ createLucideIcon("film", registry.film);

/** Renders the "fingerprint-pattern" icon from https://lucide.dev/icons/fingerprint-pattern. */
export const FingerprintPatternIcon = /*#__PURE__*/ createLucideIcon(
	"fingerprint-pattern",
	registry.fingerprintPattern,
);

/** Renders the "fire-extinguisher" icon from https://lucide.dev/icons/fire-extinguisher. */
export const FireExtinguisherIcon = /*#__PURE__*/ createLucideIcon(
	"fire-extinguisher",
	registry.fireExtinguisher,
);

/** Renders the "fish" icon from https://lucide.dev/icons/fish. */
export const FishIcon = /*#__PURE__*/ createLucideIcon("fish", registry.fish);

/** Renders the "fish-off" icon from https://lucide.dev/icons/fish-off. */
export const FishOffIcon = /*#__PURE__*/ createLucideIcon("fish-off", registry.fishOff);

/** Renders the "fish-symbol" icon from https://lucide.dev/icons/fish-symbol. */
export const FishSymbolIcon = /*#__PURE__*/ createLucideIcon("fish-symbol", registry.fishSymbol);

/** Renders the "fishing-hook" icon from https://lucide.dev/icons/fishing-hook. */
export const FishingHookIcon = /*#__PURE__*/ createLucideIcon("fishing-hook", registry.fishingHook);

/** Renders the "fishing-rod" icon from https://lucide.dev/icons/fishing-rod. */
export const FishingRodIcon = /*#__PURE__*/ createLucideIcon("fishing-rod", registry.fishingRod);

/** Renders the "flag" icon from https://lucide.dev/icons/flag. */
export const FlagIcon = /*#__PURE__*/ createLucideIcon("flag", registry.flag);

/** Renders the "flag-off" icon from https://lucide.dev/icons/flag-off. */
export const FlagOffIcon = /*#__PURE__*/ createLucideIcon("flag-off", registry.flagOff);

/** Renders the "flag-triangle-left" icon from https://lucide.dev/icons/flag-triangle-left. */
export const FlagTriangleLeftIcon = /*#__PURE__*/ createLucideIcon(
	"flag-triangle-left",
	registry.flagTriangleLeft,
);

/** Renders the "flag-triangle-right" icon from https://lucide.dev/icons/flag-triangle-right. */
export const FlagTriangleRightIcon = /*#__PURE__*/ createLucideIcon(
	"flag-triangle-right",
	registry.flagTriangleRight,
);

/** Renders the "flame" icon from https://lucide.dev/icons/flame. */
export const FlameIcon = /*#__PURE__*/ createLucideIcon("flame", registry.flame);

/** Renders the "flame-kindling" icon from https://lucide.dev/icons/flame-kindling. */
export const FlameKindlingIcon = /*#__PURE__*/ createLucideIcon(
	"flame-kindling",
	registry.flameKindling,
);

/** Renders the "flashlight" icon from https://lucide.dev/icons/flashlight. */
export const FlashlightIcon = /*#__PURE__*/ createLucideIcon("flashlight", registry.flashlight);

/** Renders the "flashlight-off" icon from https://lucide.dev/icons/flashlight-off. */
export const FlashlightOffIcon = /*#__PURE__*/ createLucideIcon(
	"flashlight-off",
	registry.flashlightOff,
);

/** Renders the "flask-conical" icon from https://lucide.dev/icons/flask-conical. */
export const FlaskConicalIcon = /*#__PURE__*/ createLucideIcon(
	"flask-conical",
	registry.flaskConical,
);

/** Renders the "flask-conical-off" icon from https://lucide.dev/icons/flask-conical-off. */
export const FlaskConicalOffIcon = /*#__PURE__*/ createLucideIcon(
	"flask-conical-off",
	registry.flaskConicalOff,
);

/** Renders the "flask-round" icon from https://lucide.dev/icons/flask-round. */
export const FlaskRoundIcon = /*#__PURE__*/ createLucideIcon("flask-round", registry.flaskRound);

/** Renders the "flower" icon from https://lucide.dev/icons/flower. */
export const FlowerIcon = /*#__PURE__*/ createLucideIcon("flower", registry.flower);

/** Renders the "flower-2" icon from https://lucide.dev/icons/flower-2. */
export const Flower2Icon = /*#__PURE__*/ createLucideIcon("flower-2", registry.flower2);

/** Renders the "focus" icon from https://lucide.dev/icons/focus. */
export const FocusIcon = /*#__PURE__*/ createLucideIcon("focus", registry.focus);

/** Renders the "fold-horizontal" icon from https://lucide.dev/icons/fold-horizontal. */
export const FoldHorizontalIcon = /*#__PURE__*/ createLucideIcon(
	"fold-horizontal",
	registry.foldHorizontal,
);

/** Renders the "fold-vertical" icon from https://lucide.dev/icons/fold-vertical. */
export const FoldVerticalIcon = /*#__PURE__*/ createLucideIcon(
	"fold-vertical",
	registry.foldVertical,
);

/** Renders the "folder" icon from https://lucide.dev/icons/folder. */
export const FolderIcon = /*#__PURE__*/ createLucideIcon("folder", registry.folder);

/** Renders the "folder-archive" icon from https://lucide.dev/icons/folder-archive. */
export const FolderArchiveIcon = /*#__PURE__*/ createLucideIcon(
	"folder-archive",
	registry.folderArchive,
);

/** Renders the "folder-bookmark" icon from https://lucide.dev/icons/folder-bookmark. */
export const FolderBookmarkIcon = /*#__PURE__*/ createLucideIcon(
	"folder-bookmark",
	registry.folderBookmark,
);

/** Renders the "folder-check" icon from https://lucide.dev/icons/folder-check. */
export const FolderCheckIcon = /*#__PURE__*/ createLucideIcon("folder-check", registry.folderCheck);

/** Renders the "folder-clock" icon from https://lucide.dev/icons/folder-clock. */
export const FolderClockIcon = /*#__PURE__*/ createLucideIcon("folder-clock", registry.folderClock);

/** Renders the "folder-closed" icon from https://lucide.dev/icons/folder-closed. */
export const FolderClosedIcon = /*#__PURE__*/ createLucideIcon(
	"folder-closed",
	registry.folderClosed,
);

/** Renders the "folder-code" icon from https://lucide.dev/icons/folder-code. */
export const FolderCodeIcon = /*#__PURE__*/ createLucideIcon("folder-code", registry.folderCode);

/** Renders the "folder-cog" icon from https://lucide.dev/icons/folder-cog. */
export const FolderCogIcon = /*#__PURE__*/ createLucideIcon("folder-cog", registry.folderCog);

/** Renders the "folder-dot" icon from https://lucide.dev/icons/folder-dot. */
export const FolderDotIcon = /*#__PURE__*/ createLucideIcon("folder-dot", registry.folderDot);

/** Renders the "folder-down" icon from https://lucide.dev/icons/folder-down. */
export const FolderDownIcon = /*#__PURE__*/ createLucideIcon("folder-down", registry.folderDown);

/** Renders the "folder-git" icon from https://lucide.dev/icons/folder-git. */
export const FolderGitIcon = /*#__PURE__*/ createLucideIcon("folder-git", registry.folderGit);

/** Renders the "folder-git-2" icon from https://lucide.dev/icons/folder-git-2. */
export const FolderGit2Icon = /*#__PURE__*/ createLucideIcon("folder-git-2", registry.folderGit2);

/** Renders the "folder-heart" icon from https://lucide.dev/icons/folder-heart. */
export const FolderHeartIcon = /*#__PURE__*/ createLucideIcon("folder-heart", registry.folderHeart);

/** Renders the "folder-input" icon from https://lucide.dev/icons/folder-input. */
export const FolderInputIcon = /*#__PURE__*/ createLucideIcon("folder-input", registry.folderInput);

/** Renders the "folder-kanban" icon from https://lucide.dev/icons/folder-kanban. */
export const FolderKanbanIcon = /*#__PURE__*/ createLucideIcon(
	"folder-kanban",
	registry.folderKanban,
);

/** Renders the "folder-key" icon from https://lucide.dev/icons/folder-key. */
export const FolderKeyIcon = /*#__PURE__*/ createLucideIcon("folder-key", registry.folderKey);

/** Renders the "folder-lock" icon from https://lucide.dev/icons/folder-lock. */
export const FolderLockIcon = /*#__PURE__*/ createLucideIcon("folder-lock", registry.folderLock);

/** Renders the "folder-minus" icon from https://lucide.dev/icons/folder-minus. */
export const FolderMinusIcon = /*#__PURE__*/ createLucideIcon("folder-minus", registry.folderMinus);

/** Renders the "folder-open" icon from https://lucide.dev/icons/folder-open. */
export const FolderOpenIcon = /*#__PURE__*/ createLucideIcon("folder-open", registry.folderOpen);

/** Renders the "folder-open-dot" icon from https://lucide.dev/icons/folder-open-dot. */
export const FolderOpenDotIcon = /*#__PURE__*/ createLucideIcon(
	"folder-open-dot",
	registry.folderOpenDot,
);

/** Renders the "folder-output" icon from https://lucide.dev/icons/folder-output. */
export const FolderOutputIcon = /*#__PURE__*/ createLucideIcon(
	"folder-output",
	registry.folderOutput,
);

/** Renders the "folder-pen" icon from https://lucide.dev/icons/folder-pen. */
export const FolderPenIcon = /*#__PURE__*/ createLucideIcon("folder-pen", registry.folderPen);

/** Renders the "folder-plus" icon from https://lucide.dev/icons/folder-plus. */
export const FolderPlusIcon = /*#__PURE__*/ createLucideIcon("folder-plus", registry.folderPlus);

/** Renders the "folder-root" icon from https://lucide.dev/icons/folder-root. */
export const FolderRootIcon = /*#__PURE__*/ createLucideIcon("folder-root", registry.folderRoot);

/** Renders the "folder-search" icon from https://lucide.dev/icons/folder-search. */
export const FolderSearchIcon = /*#__PURE__*/ createLucideIcon(
	"folder-search",
	registry.folderSearch,
);

/** Renders the "folder-search-2" icon from https://lucide.dev/icons/folder-search-2. */
export const FolderSearch2Icon = /*#__PURE__*/ createLucideIcon(
	"folder-search-2",
	registry.folderSearch2,
);

/** Renders the "folder-symlink" icon from https://lucide.dev/icons/folder-symlink. */
export const FolderSymlinkIcon = /*#__PURE__*/ createLucideIcon(
	"folder-symlink",
	registry.folderSymlink,
);

/** Renders the "folder-sync" icon from https://lucide.dev/icons/folder-sync. */
export const FolderSyncIcon = /*#__PURE__*/ createLucideIcon("folder-sync", registry.folderSync);

/** Renders the "folder-tree" icon from https://lucide.dev/icons/folder-tree. */
export const FolderTreeIcon = /*#__PURE__*/ createLucideIcon("folder-tree", registry.folderTree);

/** Renders the "folder-up" icon from https://lucide.dev/icons/folder-up. */
export const FolderUpIcon = /*#__PURE__*/ createLucideIcon("folder-up", registry.folderUp);

/** Renders the "folder-user" icon from https://lucide.dev/icons/folder-user. */
export const FolderUserIcon = /*#__PURE__*/ createLucideIcon("folder-user", registry.folderUser);

/** Renders the "folder-users" icon from https://lucide.dev/icons/folder-users. */
export const FolderUsersIcon = /*#__PURE__*/ createLucideIcon("folder-users", registry.folderUsers);

/** Renders the "folder-x" icon from https://lucide.dev/icons/folder-x. */
export const FolderXIcon = /*#__PURE__*/ createLucideIcon("folder-x", registry.folderX);

/** Renders the "folders" icon from https://lucide.dev/icons/folders. */
export const FoldersIcon = /*#__PURE__*/ createLucideIcon("folders", registry.folders);

/** Renders the "footprints" icon from https://lucide.dev/icons/footprints. */
export const FootprintsIcon = /*#__PURE__*/ createLucideIcon("footprints", registry.footprints);

/** Renders the "forklift" icon from https://lucide.dev/icons/forklift. */
export const ForkliftIcon = /*#__PURE__*/ createLucideIcon("forklift", registry.forklift);

/** Renders the "form" icon from https://lucide.dev/icons/form. */
export const FormIcon = /*#__PURE__*/ createLucideIcon("form", registry.form);

/** Renders the "forward" icon from https://lucide.dev/icons/forward. */
export const ForwardIcon = /*#__PURE__*/ createLucideIcon("forward", registry.forward);

/** Renders the "frame" icon from https://lucide.dev/icons/frame. */
export const FrameIcon = /*#__PURE__*/ createLucideIcon("frame", registry.frame);

/** Renders the "fuel" icon from https://lucide.dev/icons/fuel. */
export const FuelIcon = /*#__PURE__*/ createLucideIcon("fuel", registry.fuel);

/** Renders the "fullscreen" icon from https://lucide.dev/icons/fullscreen. */
export const FullscreenIcon = /*#__PURE__*/ createLucideIcon("fullscreen", registry.fullscreen);

/** Renders the "funnel" icon from https://lucide.dev/icons/funnel. */
export const FunnelIcon = /*#__PURE__*/ createLucideIcon("funnel", registry.funnel);

/** Renders the "funnel-plus" icon from https://lucide.dev/icons/funnel-plus. */
export const FunnelPlusIcon = /*#__PURE__*/ createLucideIcon("funnel-plus", registry.funnelPlus);

/** Renders the "funnel-x" icon from https://lucide.dev/icons/funnel-x. */
export const FunnelXIcon = /*#__PURE__*/ createLucideIcon("funnel-x", registry.funnelX);

/** Renders the "galaxy" icon from https://lucide.dev/icons/galaxy. */
export const GalaxyIcon = /*#__PURE__*/ createLucideIcon("galaxy", registry.galaxy);

/** Renders the "gallery-horizontal" icon from https://lucide.dev/icons/gallery-horizontal. */
export const GalleryHorizontalIcon = /*#__PURE__*/ createLucideIcon(
	"gallery-horizontal",
	registry.galleryHorizontal,
);

/** Renders the "gallery-horizontal-end" icon from https://lucide.dev/icons/gallery-horizontal-end. */
export const GalleryHorizontalEndIcon = /*#__PURE__*/ createLucideIcon(
	"gallery-horizontal-end",
	registry.galleryHorizontalEnd,
);

/** Renders the "gallery-thumbnails" icon from https://lucide.dev/icons/gallery-thumbnails. */
export const GalleryThumbnailsIcon = /*#__PURE__*/ createLucideIcon(
	"gallery-thumbnails",
	registry.galleryThumbnails,
);

/** Renders the "gallery-vertical" icon from https://lucide.dev/icons/gallery-vertical. */
export const GalleryVerticalIcon = /*#__PURE__*/ createLucideIcon(
	"gallery-vertical",
	registry.galleryVertical,
);

/** Renders the "gallery-vertical-end" icon from https://lucide.dev/icons/gallery-vertical-end. */
export const GalleryVerticalEndIcon = /*#__PURE__*/ createLucideIcon(
	"gallery-vertical-end",
	registry.galleryVerticalEnd,
);

/** Renders the "gamepad" icon from https://lucide.dev/icons/gamepad. */
export const GamepadIcon = /*#__PURE__*/ createLucideIcon("gamepad", registry.gamepad);

/** Renders the "gamepad-2" icon from https://lucide.dev/icons/gamepad-2. */
export const Gamepad2Icon = /*#__PURE__*/ createLucideIcon("gamepad-2", registry.gamepad2);

/** Renders the "gamepad-directional" icon from https://lucide.dev/icons/gamepad-directional. */
export const GamepadDirectionalIcon = /*#__PURE__*/ createLucideIcon(
	"gamepad-directional",
	registry.gamepadDirectional,
);

/** Renders the "gap-horizontal" icon from https://lucide.dev/icons/gap-horizontal. */
export const GapHorizontalIcon = /*#__PURE__*/ createLucideIcon(
	"gap-horizontal",
	registry.gapHorizontal,
);

/** Renders the "gap-vertical" icon from https://lucide.dev/icons/gap-vertical. */
export const GapVerticalIcon = /*#__PURE__*/ createLucideIcon("gap-vertical", registry.gapVertical);

/** Renders the "gauge" icon from https://lucide.dev/icons/gauge. */
export const GaugeIcon = /*#__PURE__*/ createLucideIcon("gauge", registry.gauge);

/** Renders the "gavel" icon from https://lucide.dev/icons/gavel. */
export const GavelIcon = /*#__PURE__*/ createLucideIcon("gavel", registry.gavel);

/** Renders the "gem" icon from https://lucide.dev/icons/gem. */
export const GemIcon = /*#__PURE__*/ createLucideIcon("gem", registry.gem);

/** Renders the "georgian-lari" icon from https://lucide.dev/icons/georgian-lari. */
export const GeorgianLariIcon = /*#__PURE__*/ createLucideIcon(
	"georgian-lari",
	registry.georgianLari,
);

/** Renders the "germ" icon from https://lucide.dev/icons/germ. */
export const GermIcon = /*#__PURE__*/ createLucideIcon("germ", registry.germ);

/** Renders the "germ-off" icon from https://lucide.dev/icons/germ-off. */
export const GermOffIcon = /*#__PURE__*/ createLucideIcon("germ-off", registry.germOff);

/** Renders the "ghost" icon from https://lucide.dev/icons/ghost. */
export const GhostIcon = /*#__PURE__*/ createLucideIcon("ghost", registry.ghost);

/** Renders the "gift" icon from https://lucide.dev/icons/gift. */
export const GiftIcon = /*#__PURE__*/ createLucideIcon("gift", registry.gift);

/** Renders the "git-branch" icon from https://lucide.dev/icons/git-branch. */
export const GitBranchIcon = /*#__PURE__*/ createLucideIcon("git-branch", registry.gitBranch);

/** Renders the "git-branch-minus" icon from https://lucide.dev/icons/git-branch-minus. */
export const GitBranchMinusIcon = /*#__PURE__*/ createLucideIcon(
	"git-branch-minus",
	registry.gitBranchMinus,
);

/** Renders the "git-branch-plus" icon from https://lucide.dev/icons/git-branch-plus. */
export const GitBranchPlusIcon = /*#__PURE__*/ createLucideIcon(
	"git-branch-plus",
	registry.gitBranchPlus,
);

/** Renders the "git-commit-horizontal" icon from https://lucide.dev/icons/git-commit-horizontal. */
export const GitCommitHorizontalIcon = /*#__PURE__*/ createLucideIcon(
	"git-commit-horizontal",
	registry.gitCommitHorizontal,
);

/** Renders the "git-commit-vertical" icon from https://lucide.dev/icons/git-commit-vertical. */
export const GitCommitVerticalIcon = /*#__PURE__*/ createLucideIcon(
	"git-commit-vertical",
	registry.gitCommitVertical,
);

/** Renders the "git-compare" icon from https://lucide.dev/icons/git-compare. */
export const GitCompareIcon = /*#__PURE__*/ createLucideIcon("git-compare", registry.gitCompare);

/** Renders the "git-compare-arrows" icon from https://lucide.dev/icons/git-compare-arrows. */
export const GitCompareArrowsIcon = /*#__PURE__*/ createLucideIcon(
	"git-compare-arrows",
	registry.gitCompareArrows,
);

/** Renders the "git-fork" icon from https://lucide.dev/icons/git-fork. */
export const GitForkIcon = /*#__PURE__*/ createLucideIcon("git-fork", registry.gitFork);

/** Renders the "git-graph" icon from https://lucide.dev/icons/git-graph. */
export const GitGraphIcon = /*#__PURE__*/ createLucideIcon("git-graph", registry.gitGraph);

/** Renders the "git-merge" icon from https://lucide.dev/icons/git-merge. */
export const GitMergeIcon = /*#__PURE__*/ createLucideIcon("git-merge", registry.gitMerge);

/** Renders the "git-merge-conflict" icon from https://lucide.dev/icons/git-merge-conflict. */
export const GitMergeConflictIcon = /*#__PURE__*/ createLucideIcon(
	"git-merge-conflict",
	registry.gitMergeConflict,
);

/** Renders the "git-pull-request" icon from https://lucide.dev/icons/git-pull-request. */
export const GitPullRequestIcon = /*#__PURE__*/ createLucideIcon(
	"git-pull-request",
	registry.gitPullRequest,
);

/** Renders the "git-pull-request-arrow" icon from https://lucide.dev/icons/git-pull-request-arrow. */
export const GitPullRequestArrowIcon = /*#__PURE__*/ createLucideIcon(
	"git-pull-request-arrow",
	registry.gitPullRequestArrow,
);

/** Renders the "git-pull-request-closed" icon from https://lucide.dev/icons/git-pull-request-closed. */
export const GitPullRequestClosedIcon = /*#__PURE__*/ createLucideIcon(
	"git-pull-request-closed",
	registry.gitPullRequestClosed,
);

/** Renders the "git-pull-request-create" icon from https://lucide.dev/icons/git-pull-request-create. */
export const GitPullRequestCreateIcon = /*#__PURE__*/ createLucideIcon(
	"git-pull-request-create",
	registry.gitPullRequestCreate,
);

/** Renders the "git-pull-request-create-arrow" icon from https://lucide.dev/icons/git-pull-request-create-arrow. */
export const GitPullRequestCreateArrowIcon = /*#__PURE__*/ createLucideIcon(
	"git-pull-request-create-arrow",
	registry.gitPullRequestCreateArrow,
);

/** Renders the "git-pull-request-draft" icon from https://lucide.dev/icons/git-pull-request-draft. */
export const GitPullRequestDraftIcon = /*#__PURE__*/ createLucideIcon(
	"git-pull-request-draft",
	registry.gitPullRequestDraft,
);

/** Renders the "glass-water" icon from https://lucide.dev/icons/glass-water. */
export const GlassWaterIcon = /*#__PURE__*/ createLucideIcon("glass-water", registry.glassWater);

/** Renders the "glasses" icon from https://lucide.dev/icons/glasses. */
export const GlassesIcon = /*#__PURE__*/ createLucideIcon("glasses", registry.glasses);

/** Renders the "globe" icon from https://lucide.dev/icons/globe. */
export const GlobeIcon = /*#__PURE__*/ createLucideIcon("globe", registry.globe);

/** Renders the "globe-check" icon from https://lucide.dev/icons/globe-check. */
export const GlobeCheckIcon = /*#__PURE__*/ createLucideIcon("globe-check", registry.globeCheck);

/** Renders the "globe-code" icon from https://lucide.dev/icons/globe-code. */
export const GlobeCodeIcon = /*#__PURE__*/ createLucideIcon("globe-code", registry.globeCode);

/** Renders the "globe-lock" icon from https://lucide.dev/icons/globe-lock. */
export const GlobeLockIcon = /*#__PURE__*/ createLucideIcon("globe-lock", registry.globeLock);

/** Renders the "globe-off" icon from https://lucide.dev/icons/globe-off. */
export const GlobeOffIcon = /*#__PURE__*/ createLucideIcon("globe-off", registry.globeOff);

/** Renders the "globe-x" icon from https://lucide.dev/icons/globe-x. */
export const GlobeXIcon = /*#__PURE__*/ createLucideIcon("globe-x", registry.globeX);

/** Renders the "goal" icon from https://lucide.dev/icons/goal. */
export const GoalIcon = /*#__PURE__*/ createLucideIcon("goal", registry.goal);

/** Renders the "gpu" icon from https://lucide.dev/icons/gpu. */
export const GpuIcon = /*#__PURE__*/ createLucideIcon("gpu", registry.gpu);

/** Renders the "graduation-cap" icon from https://lucide.dev/icons/graduation-cap. */
export const GraduationCapIcon = /*#__PURE__*/ createLucideIcon(
	"graduation-cap",
	registry.graduationCap,
);

/** Renders the "grape" icon from https://lucide.dev/icons/grape. */
export const GrapeIcon = /*#__PURE__*/ createLucideIcon("grape", registry.grape);

/** Renders the "graph-hub" icon from https://lucide.dev/icons/graph-hub. */
export const GraphHubIcon = /*#__PURE__*/ createLucideIcon("graph-hub", registry.graphHub);

/** Renders the "graph-star" icon from https://lucide.dev/icons/graph-star. */
export const GraphStarIcon = /*#__PURE__*/ createLucideIcon("graph-star", registry.graphStar);

/** Renders the "grid-2x2" icon from https://lucide.dev/icons/grid-2x2. */
export const Grid2x2Icon = /*#__PURE__*/ createLucideIcon("grid-2x2", registry.grid2x2);

/** Renders the "grid-2x2-check" icon from https://lucide.dev/icons/grid-2x2-check. */
export const Grid2x2CheckIcon = /*#__PURE__*/ createLucideIcon(
	"grid-2x2-check",
	registry.grid2x2Check,
);

/** Renders the "grid-2x2-plus" icon from https://lucide.dev/icons/grid-2x2-plus. */
export const Grid2x2PlusIcon = /*#__PURE__*/ createLucideIcon(
	"grid-2x2-plus",
	registry.grid2x2Plus,
);

/** Renders the "grid-2x2-x" icon from https://lucide.dev/icons/grid-2x2-x. */
export const Grid2x2XIcon = /*#__PURE__*/ createLucideIcon("grid-2x2-x", registry.grid2x2X);

/** Renders the "grid-3x2" icon from https://lucide.dev/icons/grid-3x2. */
export const Grid3x2Icon = /*#__PURE__*/ createLucideIcon("grid-3x2", registry.grid3x2);

/** Renders the "grid-3x3" icon from https://lucide.dev/icons/grid-3x3. */
export const Grid3x3Icon = /*#__PURE__*/ createLucideIcon("grid-3x3", registry.grid3x3);

/** Renders the "grip" icon from https://lucide.dev/icons/grip. */
export const GripIcon = /*#__PURE__*/ createLucideIcon("grip", registry.grip);

/** Renders the "grip-horizontal" icon from https://lucide.dev/icons/grip-horizontal. */
export const GripHorizontalIcon = /*#__PURE__*/ createLucideIcon(
	"grip-horizontal",
	registry.gripHorizontal,
);

/** Renders the "grip-vertical" icon from https://lucide.dev/icons/grip-vertical. */
export const GripVerticalIcon = /*#__PURE__*/ createLucideIcon(
	"grip-vertical",
	registry.gripVertical,
);

/** Renders the "groceries" icon from https://lucide.dev/icons/groceries. */
export const GroceriesIcon = /*#__PURE__*/ createLucideIcon("groceries", registry.groceries);

/** Renders the "group" icon from https://lucide.dev/icons/group. */
export const GroupIcon = /*#__PURE__*/ createLucideIcon("group", registry.group);

/** Renders the "guitar" icon from https://lucide.dev/icons/guitar. */
export const GuitarIcon = /*#__PURE__*/ createLucideIcon("guitar", registry.guitar);

/** Renders the "guitar-electric" icon from https://lucide.dev/icons/guitar-electric. */
export const GuitarElectricIcon = /*#__PURE__*/ createLucideIcon(
	"guitar-electric",
	registry.guitarElectric,
);

/** Renders the "half-note" icon from https://lucide.dev/icons/half-note. */
export const HalfNoteIcon = /*#__PURE__*/ createLucideIcon("half-note", registry.halfNote);

/** Renders the "ham" icon from https://lucide.dev/icons/ham. */
export const HamIcon = /*#__PURE__*/ createLucideIcon("ham", registry.ham);

/** Renders the "hamburger" icon from https://lucide.dev/icons/hamburger. */
export const HamburgerIcon = /*#__PURE__*/ createLucideIcon("hamburger", registry.hamburger);

/** Renders the "hammer" icon from https://lucide.dev/icons/hammer. */
export const HammerIcon = /*#__PURE__*/ createLucideIcon("hammer", registry.hammer);

/** Renders the "hand" icon from https://lucide.dev/icons/hand. */
export const HandIcon = /*#__PURE__*/ createLucideIcon("hand", registry.hand);

/** Renders the "hand-coins" icon from https://lucide.dev/icons/hand-coins. */
export const HandCoinsIcon = /*#__PURE__*/ createLucideIcon("hand-coins", registry.handCoins);

/** Renders the "hand-fist" icon from https://lucide.dev/icons/hand-fist. */
export const HandFistIcon = /*#__PURE__*/ createLucideIcon("hand-fist", registry.handFist);

/** Renders the "hand-grab" icon from https://lucide.dev/icons/hand-grab. */
export const HandGrabIcon = /*#__PURE__*/ createLucideIcon("hand-grab", registry.handGrab);

/** Renders the "hand-heart" icon from https://lucide.dev/icons/hand-heart. */
export const HandHeartIcon = /*#__PURE__*/ createLucideIcon("hand-heart", registry.handHeart);

/** Renders the "hand-helping" icon from https://lucide.dev/icons/hand-helping. */
export const HandHelpingIcon = /*#__PURE__*/ createLucideIcon("hand-helping", registry.handHelping);

/** Renders the "hand-metal" icon from https://lucide.dev/icons/hand-metal. */
export const HandMetalIcon = /*#__PURE__*/ createLucideIcon("hand-metal", registry.handMetal);

/** Renders the "hand-platter" icon from https://lucide.dev/icons/hand-platter. */
export const HandPlatterIcon = /*#__PURE__*/ createLucideIcon("hand-platter", registry.handPlatter);

/** Renders the "handbag" icon from https://lucide.dev/icons/handbag. */
export const HandbagIcon = /*#__PURE__*/ createLucideIcon("handbag", registry.handbag);

/** Renders the "handle-bottom-right" icon from https://lucide.dev/icons/handle-bottom-right. */
export const HandleBottomRightIcon = /*#__PURE__*/ createLucideIcon(
	"handle-bottom-right",
	registry.handleBottomRight,
);

/** Renders the "handshake" icon from https://lucide.dev/icons/handshake. */
export const HandshakeIcon = /*#__PURE__*/ createLucideIcon("handshake", registry.handshake);

/** Renders the "hard-drive" icon from https://lucide.dev/icons/hard-drive. */
export const HardDriveIcon = /*#__PURE__*/ createLucideIcon("hard-drive", registry.hardDrive);

/** Renders the "hard-drive-download" icon from https://lucide.dev/icons/hard-drive-download. */
export const HardDriveDownloadIcon = /*#__PURE__*/ createLucideIcon(
	"hard-drive-download",
	registry.hardDriveDownload,
);

/** Renders the "hard-drive-upload" icon from https://lucide.dev/icons/hard-drive-upload. */
export const HardDriveUploadIcon = /*#__PURE__*/ createLucideIcon(
	"hard-drive-upload",
	registry.hardDriveUpload,
);

/** Renders the "hard-hat" icon from https://lucide.dev/icons/hard-hat. */
export const HardHatIcon = /*#__PURE__*/ createLucideIcon("hard-hat", registry.hardHat);

/** Renders the "hash" icon from https://lucide.dev/icons/hash. */
export const HashIcon = /*#__PURE__*/ createLucideIcon("hash", registry.hash);

/** Renders the "hat-glasses" icon from https://lucide.dev/icons/hat-glasses. */
export const HatGlassesIcon = /*#__PURE__*/ createLucideIcon("hat-glasses", registry.hatGlasses);

/** Renders the "haze" icon from https://lucide.dev/icons/haze. */
export const HazeIcon = /*#__PURE__*/ createLucideIcon("haze", registry.haze);

/** Renders the "hd" icon from https://lucide.dev/icons/hd. */
export const HdIcon = /*#__PURE__*/ createLucideIcon("hd", registry.hd);

/** Renders the "hdmi-port" icon from https://lucide.dev/icons/hdmi-port. */
export const HdmiPortIcon = /*#__PURE__*/ createLucideIcon("hdmi-port", registry.hdmiPort);

/** Renders the "heading" icon from https://lucide.dev/icons/heading. */
export const HeadingIcon = /*#__PURE__*/ createLucideIcon("heading", registry.heading);

/** Renders the "heading-1" icon from https://lucide.dev/icons/heading-1. */
export const Heading1Icon = /*#__PURE__*/ createLucideIcon("heading-1", registry.heading1);

/** Renders the "heading-2" icon from https://lucide.dev/icons/heading-2. */
export const Heading2Icon = /*#__PURE__*/ createLucideIcon("heading-2", registry.heading2);

/** Renders the "heading-3" icon from https://lucide.dev/icons/heading-3. */
export const Heading3Icon = /*#__PURE__*/ createLucideIcon("heading-3", registry.heading3);

/** Renders the "heading-4" icon from https://lucide.dev/icons/heading-4. */
export const Heading4Icon = /*#__PURE__*/ createLucideIcon("heading-4", registry.heading4);

/** Renders the "heading-5" icon from https://lucide.dev/icons/heading-5. */
export const Heading5Icon = /*#__PURE__*/ createLucideIcon("heading-5", registry.heading5);

/** Renders the "heading-6" icon from https://lucide.dev/icons/heading-6. */
export const Heading6Icon = /*#__PURE__*/ createLucideIcon("heading-6", registry.heading6);

/** Renders the "headphone-off" icon from https://lucide.dev/icons/headphone-off. */
export const HeadphoneOffIcon = /*#__PURE__*/ createLucideIcon(
	"headphone-off",
	registry.headphoneOff,
);

/** Renders the "headphones" icon from https://lucide.dev/icons/headphones. */
export const HeadphonesIcon = /*#__PURE__*/ createLucideIcon("headphones", registry.headphones);

/** Renders the "headset" icon from https://lucide.dev/icons/headset. */
export const HeadsetIcon = /*#__PURE__*/ createLucideIcon("headset", registry.headset);

/** Renders the "heart" icon from https://lucide.dev/icons/heart. */
export const HeartIcon = /*#__PURE__*/ createLucideIcon("heart", registry.heart);

/** Renders the "heart-crack" icon from https://lucide.dev/icons/heart-crack. */
export const HeartCrackIcon = /*#__PURE__*/ createLucideIcon("heart-crack", registry.heartCrack);

/** Renders the "heart-handshake" icon from https://lucide.dev/icons/heart-handshake. */
export const HeartHandshakeIcon = /*#__PURE__*/ createLucideIcon(
	"heart-handshake",
	registry.heartHandshake,
);

/** Renders the "heart-minus" icon from https://lucide.dev/icons/heart-minus. */
export const HeartMinusIcon = /*#__PURE__*/ createLucideIcon("heart-minus", registry.heartMinus);

/** Renders the "heart-off" icon from https://lucide.dev/icons/heart-off. */
export const HeartOffIcon = /*#__PURE__*/ createLucideIcon("heart-off", registry.heartOff);

/** Renders the "heart-plus" icon from https://lucide.dev/icons/heart-plus. */
export const HeartPlusIcon = /*#__PURE__*/ createLucideIcon("heart-plus", registry.heartPlus);

/** Renders the "heart-pulse" icon from https://lucide.dev/icons/heart-pulse. */
export const HeartPulseIcon = /*#__PURE__*/ createLucideIcon("heart-pulse", registry.heartPulse);

/** Renders the "heart-x" icon from https://lucide.dev/icons/heart-x. */
export const HeartXIcon = /*#__PURE__*/ createLucideIcon("heart-x", registry.heartX);

/** Renders the "heater" icon from https://lucide.dev/icons/heater. */
export const HeaterIcon = /*#__PURE__*/ createLucideIcon("heater", registry.heater);

/** Renders the "helicopter" icon from https://lucide.dev/icons/helicopter. */
export const HelicopterIcon = /*#__PURE__*/ createLucideIcon("helicopter", registry.helicopter);

/** Renders the "hexagon" icon from https://lucide.dev/icons/hexagon. */
export const HexagonIcon = /*#__PURE__*/ createLucideIcon("hexagon", registry.hexagon);

/** Renders the "highlighter" icon from https://lucide.dev/icons/highlighter. */
export const HighlighterIcon = /*#__PURE__*/ createLucideIcon("highlighter", registry.highlighter);

/** Renders the "hiking-stick" icon from https://lucide.dev/icons/hiking-stick. */
export const HikingStickIcon = /*#__PURE__*/ createLucideIcon("hiking-stick", registry.hikingStick);

/** Renders the "hop" icon from https://lucide.dev/icons/hop. */
export const HopIcon = /*#__PURE__*/ createLucideIcon("hop", registry.hop);

/** Renders the "hop-off" icon from https://lucide.dev/icons/hop-off. */
export const HopOffIcon = /*#__PURE__*/ createLucideIcon("hop-off", registry.hopOff);

/** Renders the "hospital" icon from https://lucide.dev/icons/hospital. */
export const HospitalIcon = /*#__PURE__*/ createLucideIcon("hospital", registry.hospital);

/** Renders the "hotel" icon from https://lucide.dev/icons/hotel. */
export const HotelIcon = /*#__PURE__*/ createLucideIcon("hotel", registry.hotel);

/** Renders the "hourglass" icon from https://lucide.dev/icons/hourglass. */
export const HourglassIcon = /*#__PURE__*/ createLucideIcon("hourglass", registry.hourglass);

/** Renders the "hourglass-cog" icon from https://lucide.dev/icons/hourglass-cog. */
export const HourglassCogIcon = /*#__PURE__*/ createLucideIcon(
	"hourglass-cog",
	registry.hourglassCog,
);

/** Renders the "house" icon from https://lucide.dev/icons/house. */
export const HouseIcon = /*#__PURE__*/ createLucideIcon("house", registry.house);

/** Renders the "house-cog" icon from https://lucide.dev/icons/house-cog. */
export const HouseCogIcon = /*#__PURE__*/ createLucideIcon("house-cog", registry.houseCog);

/** Renders the "house-heart" icon from https://lucide.dev/icons/house-heart. */
export const HouseHeartIcon = /*#__PURE__*/ createLucideIcon("house-heart", registry.houseHeart);

/** Renders the "house-plug" icon from https://lucide.dev/icons/house-plug. */
export const HousePlugIcon = /*#__PURE__*/ createLucideIcon("house-plug", registry.housePlug);

/** Renders the "house-plus" icon from https://lucide.dev/icons/house-plus. */
export const HousePlusIcon = /*#__PURE__*/ createLucideIcon("house-plus", registry.housePlus);

/** Renders the "house-wifi" icon from https://lucide.dev/icons/house-wifi. */
export const HouseWifiIcon = /*#__PURE__*/ createLucideIcon("house-wifi", registry.houseWifi);

/** Renders the "houses" icon from https://lucide.dev/icons/houses. */
export const HousesIcon = /*#__PURE__*/ createLucideIcon("houses", registry.houses);

/** Renders the "ice-cream-bowl" icon from https://lucide.dev/icons/ice-cream-bowl. */
export const IceCreamBowlIcon = /*#__PURE__*/ createLucideIcon(
	"ice-cream-bowl",
	registry.iceCreamBowl,
);

/** Renders the "ice-cream-cone" icon from https://lucide.dev/icons/ice-cream-cone. */
export const IceCreamConeIcon = /*#__PURE__*/ createLucideIcon(
	"ice-cream-cone",
	registry.iceCreamCone,
);

/** Renders the "id-card" icon from https://lucide.dev/icons/id-card. */
export const IdCardIcon = /*#__PURE__*/ createLucideIcon("id-card", registry.idCard);

/** Renders the "id-card-lanyard" icon from https://lucide.dev/icons/id-card-lanyard. */
export const IdCardLanyardIcon = /*#__PURE__*/ createLucideIcon(
	"id-card-lanyard",
	registry.idCardLanyard,
);

/** Renders the "image" icon from https://lucide.dev/icons/image. */
export const ImageIcon = /*#__PURE__*/ createLucideIcon("image", registry.image);

/** Renders the "image-down" icon from https://lucide.dev/icons/image-down. */
export const ImageDownIcon = /*#__PURE__*/ createLucideIcon("image-down", registry.imageDown);

/** Renders the "image-minus" icon from https://lucide.dev/icons/image-minus. */
export const ImageMinusIcon = /*#__PURE__*/ createLucideIcon("image-minus", registry.imageMinus);

/** Renders the "image-off" icon from https://lucide.dev/icons/image-off. */
export const ImageOffIcon = /*#__PURE__*/ createLucideIcon("image-off", registry.imageOff);

/** Renders the "image-play" icon from https://lucide.dev/icons/image-play. */
export const ImagePlayIcon = /*#__PURE__*/ createLucideIcon("image-play", registry.imagePlay);

/** Renders the "image-plus" icon from https://lucide.dev/icons/image-plus. */
export const ImagePlusIcon = /*#__PURE__*/ createLucideIcon("image-plus", registry.imagePlus);

/** Renders the "image-up" icon from https://lucide.dev/icons/image-up. */
export const ImageUpIcon = /*#__PURE__*/ createLucideIcon("image-up", registry.imageUp);

/** Renders the "image-upscale" icon from https://lucide.dev/icons/image-upscale. */
export const ImageUpscaleIcon = /*#__PURE__*/ createLucideIcon(
	"image-upscale",
	registry.imageUpscale,
);

/** Renders the "images" icon from https://lucide.dev/icons/images. */
export const ImagesIcon = /*#__PURE__*/ createLucideIcon("images", registry.images);

/** Renders the "import" icon from https://lucide.dev/icons/import. */
export const ImportIcon = /*#__PURE__*/ createLucideIcon("import", registry.importNode);

/** Renders the "inbox" icon from https://lucide.dev/icons/inbox. */
export const InboxIcon = /*#__PURE__*/ createLucideIcon("inbox", registry.inbox);

/** Renders the "indian-rupee" icon from https://lucide.dev/icons/indian-rupee. */
export const IndianRupeeIcon = /*#__PURE__*/ createLucideIcon("indian-rupee", registry.indianRupee);

/** Renders the "infinity" icon from https://lucide.dev/icons/infinity. */
export const InfinityIcon = /*#__PURE__*/ createLucideIcon("infinity", registry.infinity);

/** Renders the "info" icon from https://lucide.dev/icons/info. */
export const InfoIcon = /*#__PURE__*/ createLucideIcon("info", registry.info);

/** Renders the "inspection-panel" icon from https://lucide.dev/icons/inspection-panel. */
export const InspectionPanelIcon = /*#__PURE__*/ createLucideIcon(
	"inspection-panel",
	registry.inspectionPanel,
);

/** Renders the "italic" icon from https://lucide.dev/icons/italic. */
export const ItalicIcon = /*#__PURE__*/ createLucideIcon("italic", registry.italic);

/** Renders the "iteration-ccw" icon from https://lucide.dev/icons/iteration-ccw. */
export const IterationCcwIcon = /*#__PURE__*/ createLucideIcon(
	"iteration-ccw",
	registry.iterationCcw,
);

/** Renders the "iteration-cw" icon from https://lucide.dev/icons/iteration-cw. */
export const IterationCwIcon = /*#__PURE__*/ createLucideIcon("iteration-cw", registry.iterationCw);

/** Renders the "iv-bag" icon from https://lucide.dev/icons/iv-bag. */
export const IvBagIcon = /*#__PURE__*/ createLucideIcon("iv-bag", registry.ivBag);

/** Renders the "japanese-yen" icon from https://lucide.dev/icons/japanese-yen. */
export const JapaneseYenIcon = /*#__PURE__*/ createLucideIcon("japanese-yen", registry.japaneseYen);

/** Renders the "joystick" icon from https://lucide.dev/icons/joystick. */
export const JoystickIcon = /*#__PURE__*/ createLucideIcon("joystick", registry.joystick);

/** Renders the "kanban" icon from https://lucide.dev/icons/kanban. */
export const KanbanIcon = /*#__PURE__*/ createLucideIcon("kanban", registry.kanban);

/** Renders the "kayak" icon from https://lucide.dev/icons/kayak. */
export const KayakIcon = /*#__PURE__*/ createLucideIcon("kayak", registry.kayak);

/** Renders the "kazakh-tenge" icon from https://lucide.dev/icons/kazakh-tenge. */
export const KazakhTengeIcon = /*#__PURE__*/ createLucideIcon("kazakh-tenge", registry.kazakhTenge);

/** Renders the "key" icon from https://lucide.dev/icons/key. */
export const KeyIcon = /*#__PURE__*/ createLucideIcon("key", registry.key);

/** Renders the "key-round" icon from https://lucide.dev/icons/key-round. */
export const KeyRoundIcon = /*#__PURE__*/ createLucideIcon("key-round", registry.keyRound);

/** Renders the "key-square" icon from https://lucide.dev/icons/key-square. */
export const KeySquareIcon = /*#__PURE__*/ createLucideIcon("key-square", registry.keySquare);

/** Renders the "keyboard" icon from https://lucide.dev/icons/keyboard. */
export const KeyboardIcon = /*#__PURE__*/ createLucideIcon("keyboard", registry.keyboard);

/** Renders the "keyboard-music" icon from https://lucide.dev/icons/keyboard-music. */
export const KeyboardMusicIcon = /*#__PURE__*/ createLucideIcon(
	"keyboard-music",
	registry.keyboardMusic,
);

/** Renders the "keyboard-off" icon from https://lucide.dev/icons/keyboard-off. */
export const KeyboardOffIcon = /*#__PURE__*/ createLucideIcon("keyboard-off", registry.keyboardOff);

/** Renders the "lambda" icon from https://lucide.dev/icons/lambda. */
export const LambdaIcon = /*#__PURE__*/ createLucideIcon("lambda", registry.lambda);

/** Renders the "lamp" icon from https://lucide.dev/icons/lamp. */
export const LampIcon = /*#__PURE__*/ createLucideIcon("lamp", registry.lamp);

/** Renders the "lamp-ceiling" icon from https://lucide.dev/icons/lamp-ceiling. */
export const LampCeilingIcon = /*#__PURE__*/ createLucideIcon("lamp-ceiling", registry.lampCeiling);

/** Renders the "lamp-desk" icon from https://lucide.dev/icons/lamp-desk. */
export const LampDeskIcon = /*#__PURE__*/ createLucideIcon("lamp-desk", registry.lampDesk);

/** Renders the "lamp-floor" icon from https://lucide.dev/icons/lamp-floor. */
export const LampFloorIcon = /*#__PURE__*/ createLucideIcon("lamp-floor", registry.lampFloor);

/** Renders the "lamp-wall-down" icon from https://lucide.dev/icons/lamp-wall-down. */
export const LampWallDownIcon = /*#__PURE__*/ createLucideIcon(
	"lamp-wall-down",
	registry.lampWallDown,
);

/** Renders the "lamp-wall-up" icon from https://lucide.dev/icons/lamp-wall-up. */
export const LampWallUpIcon = /*#__PURE__*/ createLucideIcon("lamp-wall-up", registry.lampWallUp);

/** Renders the "land-plot" icon from https://lucide.dev/icons/land-plot. */
export const LandPlotIcon = /*#__PURE__*/ createLucideIcon("land-plot", registry.landPlot);

/** Renders the "landmark" icon from https://lucide.dev/icons/landmark. */
export const LandmarkIcon = /*#__PURE__*/ createLucideIcon("landmark", registry.landmark);

/** Renders the "languages" icon from https://lucide.dev/icons/languages. */
export const LanguagesIcon = /*#__PURE__*/ createLucideIcon("languages", registry.languages);

/** Renders the "laptop" icon from https://lucide.dev/icons/laptop. */
export const LaptopIcon = /*#__PURE__*/ createLucideIcon("laptop", registry.laptop);

/** Renders the "laptop-minimal" icon from https://lucide.dev/icons/laptop-minimal. */
export const LaptopMinimalIcon = /*#__PURE__*/ createLucideIcon(
	"laptop-minimal",
	registry.laptopMinimal,
);

/** Renders the "laptop-minimal-check" icon from https://lucide.dev/icons/laptop-minimal-check. */
export const LaptopMinimalCheckIcon = /*#__PURE__*/ createLucideIcon(
	"laptop-minimal-check",
	registry.laptopMinimalCheck,
);

/** Renders the "lasso" icon from https://lucide.dev/icons/lasso. */
export const LassoIcon = /*#__PURE__*/ createLucideIcon("lasso", registry.lasso);

/** Renders the "lasso-select" icon from https://lucide.dev/icons/lasso-select. */
export const LassoSelectIcon = /*#__PURE__*/ createLucideIcon("lasso-select", registry.lassoSelect);

/** Renders the "layer-arrow-down" icon from https://lucide.dev/icons/layer-arrow-down. */
export const LayerArrowDownIcon = /*#__PURE__*/ createLucideIcon(
	"layer-arrow-down",
	registry.layerArrowDown,
);

/** Renders the "layer-arrow-up" icon from https://lucide.dev/icons/layer-arrow-up. */
export const LayerArrowUpIcon = /*#__PURE__*/ createLucideIcon(
	"layer-arrow-up",
	registry.layerArrowUp,
);

/** Renders the "layers" icon from https://lucide.dev/icons/layers. */
export const LayersIcon = /*#__PURE__*/ createLucideIcon("layers", registry.layers);

/** Renders the "layers-2" icon from https://lucide.dev/icons/layers-2. */
export const Layers2Icon = /*#__PURE__*/ createLucideIcon("layers-2", registry.layers2);

/** Renders the "layers-arrow-down" icon from https://lucide.dev/icons/layers-arrow-down. */
export const LayersArrowDownIcon = /*#__PURE__*/ createLucideIcon(
	"layers-arrow-down",
	registry.layersArrowDown,
);

/** Renders the "layers-arrow-up" icon from https://lucide.dev/icons/layers-arrow-up. */
export const LayersArrowUpIcon = /*#__PURE__*/ createLucideIcon(
	"layers-arrow-up",
	registry.layersArrowUp,
);

/** Renders the "layers-minus" icon from https://lucide.dev/icons/layers-minus. */
export const LayersMinusIcon = /*#__PURE__*/ createLucideIcon("layers-minus", registry.layersMinus);

/** Renders the "layers-plus" icon from https://lucide.dev/icons/layers-plus. */
export const LayersPlusIcon = /*#__PURE__*/ createLucideIcon("layers-plus", registry.layersPlus);

/** Renders the "layout-arrow-down" icon from https://lucide.dev/icons/layout-arrow-down. */
export const LayoutArrowDownIcon = /*#__PURE__*/ createLucideIcon(
	"layout-arrow-down",
	registry.layoutArrowDown,
);

/** Renders the "layout-arrow-right" icon from https://lucide.dev/icons/layout-arrow-right. */
export const LayoutArrowRightIcon = /*#__PURE__*/ createLucideIcon(
	"layout-arrow-right",
	registry.layoutArrowRight,
);

/** Renders the "layout-dashboard" icon from https://lucide.dev/icons/layout-dashboard. */
export const LayoutDashboardIcon = /*#__PURE__*/ createLucideIcon(
	"layout-dashboard",
	registry.layoutDashboard,
);

/** Renders the "layout-freeform" icon from https://lucide.dev/icons/layout-freeform. */
export const LayoutFreeformIcon = /*#__PURE__*/ createLucideIcon(
	"layout-freeform",
	registry.layoutFreeform,
);

/** Renders the "layout-grid" icon from https://lucide.dev/icons/layout-grid. */
export const LayoutGridIcon = /*#__PURE__*/ createLucideIcon("layout-grid", registry.layoutGrid);

/** Renders the "layout-grid-circles" icon from https://lucide.dev/icons/layout-grid-circles. */
export const LayoutGridCirclesIcon = /*#__PURE__*/ createLucideIcon(
	"layout-grid-circles",
	registry.layoutGridCircles,
);

/** Renders the "layout-list" icon from https://lucide.dev/icons/layout-list. */
export const LayoutListIcon = /*#__PURE__*/ createLucideIcon("layout-list", registry.layoutList);

/** Renders the "layout-panel-left" icon from https://lucide.dev/icons/layout-panel-left. */
export const LayoutPanelLeftIcon = /*#__PURE__*/ createLucideIcon(
	"layout-panel-left",
	registry.layoutPanelLeft,
);

/** Renders the "layout-panel-top" icon from https://lucide.dev/icons/layout-panel-top. */
export const LayoutPanelTopIcon = /*#__PURE__*/ createLucideIcon(
	"layout-panel-top",
	registry.layoutPanelTop,
);

/** Renders the "layout-template" icon from https://lucide.dev/icons/layout-template. */
export const LayoutTemplateIcon = /*#__PURE__*/ createLucideIcon(
	"layout-template",
	registry.layoutTemplate,
);

/** Renders the "leaf" icon from https://lucide.dev/icons/leaf. */
export const LeafIcon = /*#__PURE__*/ createLucideIcon("leaf", registry.leaf);

/** Renders the "leafy-green" icon from https://lucide.dev/icons/leafy-green. */
export const LeafyGreenIcon = /*#__PURE__*/ createLucideIcon("leafy-green", registry.leafyGreen);

/** Renders the "lectern" icon from https://lucide.dev/icons/lectern. */
export const LecternIcon = /*#__PURE__*/ createLucideIcon("lectern", registry.lectern);

/** Renders the "lens" icon from https://lucide.dev/icons/lens. */
export const LensIcon = /*#__PURE__*/ createLucideIcon("lens", registry.lens);

/** Renders the "lens-concave" icon from https://lucide.dev/icons/lens-concave. */
export const LensConcaveIcon = /*#__PURE__*/ createLucideIcon("lens-concave", registry.lensConcave);

/** Renders the "lens-convex" icon from https://lucide.dev/icons/lens-convex. */
export const LensConvexIcon = /*#__PURE__*/ createLucideIcon("lens-convex", registry.lensConvex);

/** Renders the "letter-spacing" icon from https://lucide.dev/icons/letter-spacing. */
export const LetterSpacingIcon = /*#__PURE__*/ createLucideIcon(
	"letter-spacing",
	registry.letterSpacing,
);

/** Renders the "letters" icon from https://lucide.dev/icons/letters. */
export const LettersIcon = /*#__PURE__*/ createLucideIcon("letters", registry.letters);

/** Renders the "library" icon from https://lucide.dev/icons/library. */
export const LibraryIcon = /*#__PURE__*/ createLucideIcon("library", registry.library);

/** Renders the "library-big" icon from https://lucide.dev/icons/library-big. */
export const LibraryBigIcon = /*#__PURE__*/ createLucideIcon("library-big", registry.libraryBig);

/** Renders the "life-buoy" icon from https://lucide.dev/icons/life-buoy. */
export const LifeBuoyIcon = /*#__PURE__*/ createLucideIcon("life-buoy", registry.lifeBuoy);

/** Renders the "ligature" icon from https://lucide.dev/icons/ligature. */
export const LigatureIcon = /*#__PURE__*/ createLucideIcon("ligature", registry.ligature);

/** Renders the "lightbulb" icon from https://lucide.dev/icons/lightbulb. */
export const LightbulbIcon = /*#__PURE__*/ createLucideIcon("lightbulb", registry.lightbulb);

/** Renders the "lightbulb-off" icon from https://lucide.dev/icons/lightbulb-off. */
export const LightbulbOffIcon = /*#__PURE__*/ createLucideIcon(
	"lightbulb-off",
	registry.lightbulbOff,
);

/** Renders the "lighthouse" icon from https://lucide.dev/icons/lighthouse. */
export const LighthouseIcon = /*#__PURE__*/ createLucideIcon("lighthouse", registry.lighthouse);

/** Renders the "line-dot-bottom-vertical" icon from https://lucide.dev/icons/line-dot-bottom-vertical. */
export const LineDotBottomVerticalIcon = /*#__PURE__*/ createLucideIcon(
	"line-dot-bottom-vertical",
	registry.lineDotBottomVertical,
);

/** Renders the "line-dot-left-horizontal" icon from https://lucide.dev/icons/line-dot-left-horizontal. */
export const LineDotLeftHorizontalIcon = /*#__PURE__*/ createLucideIcon(
	"line-dot-left-horizontal",
	registry.lineDotLeftHorizontal,
);

/** Renders the "line-dot-right-horizontal" icon from https://lucide.dev/icons/line-dot-right-horizontal. */
export const LineDotRightHorizontalIcon = /*#__PURE__*/ createLucideIcon(
	"line-dot-right-horizontal",
	registry.lineDotRightHorizontal,
);

/** Renders the "line-dot-top-vertical" icon from https://lucide.dev/icons/line-dot-top-vertical. */
export const LineDotTopVerticalIcon = /*#__PURE__*/ createLucideIcon(
	"line-dot-top-vertical",
	registry.lineDotTopVertical,
);

/** Renders the "line-height" icon from https://lucide.dev/icons/line-height. */
export const LineHeightIcon = /*#__PURE__*/ createLucideIcon("line-height", registry.lineHeight);

/** Renders the "line-squiggle" icon from https://lucide.dev/icons/line-squiggle. */
export const LineSquiggleIcon = /*#__PURE__*/ createLucideIcon(
	"line-squiggle",
	registry.lineSquiggle,
);

/** Renders the "line-style" icon from https://lucide.dev/icons/line-style. */
export const LineStyleIcon = /*#__PURE__*/ createLucideIcon("line-style", registry.lineStyle);

/** Renders the "link" icon from https://lucide.dev/icons/link. */
export const LinkIcon = /*#__PURE__*/ createLucideIcon("link", registry.link);

/** Renders the "link-2" icon from https://lucide.dev/icons/link-2. */
export const Link2Icon = /*#__PURE__*/ createLucideIcon("link-2", registry.link2);

/** Renders the "link-2-off" icon from https://lucide.dev/icons/link-2-off. */
export const Link2OffIcon = /*#__PURE__*/ createLucideIcon("link-2-off", registry.link2Off);

/** Renders the "list" icon from https://lucide.dev/icons/list. */
export const ListIcon = /*#__PURE__*/ createLucideIcon("list", registry.list);

/** Renders the "list-check" icon from https://lucide.dev/icons/list-check. */
export const ListCheckIcon = /*#__PURE__*/ createLucideIcon("list-check", registry.listCheck);

/** Renders the "list-checks" icon from https://lucide.dev/icons/list-checks. */
export const ListChecksIcon = /*#__PURE__*/ createLucideIcon("list-checks", registry.listChecks);

/** Renders the "list-chevrons-down-up" icon from https://lucide.dev/icons/list-chevrons-down-up. */
export const ListChevronsDownUpIcon = /*#__PURE__*/ createLucideIcon(
	"list-chevrons-down-up",
	registry.listChevronsDownUp,
);

/** Renders the "list-chevrons-up-down" icon from https://lucide.dev/icons/list-chevrons-up-down. */
export const ListChevronsUpDownIcon = /*#__PURE__*/ createLucideIcon(
	"list-chevrons-up-down",
	registry.listChevronsUpDown,
);

/** Renders the "list-clock" icon from https://lucide.dev/icons/list-clock. */
export const ListClockIcon = /*#__PURE__*/ createLucideIcon("list-clock", registry.listClock);

/** Renders the "list-collapse" icon from https://lucide.dev/icons/list-collapse. */
export const ListCollapseIcon = /*#__PURE__*/ createLucideIcon(
	"list-collapse",
	registry.listCollapse,
);

/** Renders the "list-end" icon from https://lucide.dev/icons/list-end. */
export const ListEndIcon = /*#__PURE__*/ createLucideIcon("list-end", registry.listEnd);

/** Renders the "list-filter" icon from https://lucide.dev/icons/list-filter. */
export const ListFilterIcon = /*#__PURE__*/ createLucideIcon("list-filter", registry.listFilter);

/** Renders the "list-filter-plus" icon from https://lucide.dev/icons/list-filter-plus. */
export const ListFilterPlusIcon = /*#__PURE__*/ createLucideIcon(
	"list-filter-plus",
	registry.listFilterPlus,
);

/** Renders the "list-indent-decrease" icon from https://lucide.dev/icons/list-indent-decrease. */
export const ListIndentDecreaseIcon = /*#__PURE__*/ createLucideIcon(
	"list-indent-decrease",
	registry.listIndentDecrease,
);

/** Renders the "list-indent-increase" icon from https://lucide.dev/icons/list-indent-increase. */
export const ListIndentIncreaseIcon = /*#__PURE__*/ createLucideIcon(
	"list-indent-increase",
	registry.listIndentIncrease,
);

/** Renders the "list-minus" icon from https://lucide.dev/icons/list-minus. */
export const ListMinusIcon = /*#__PURE__*/ createLucideIcon("list-minus", registry.listMinus);

/** Renders the "list-music" icon from https://lucide.dev/icons/list-music. */
export const ListMusicIcon = /*#__PURE__*/ createLucideIcon("list-music", registry.listMusic);

/** Renders the "list-ordered" icon from https://lucide.dev/icons/list-ordered. */
export const ListOrderedIcon = /*#__PURE__*/ createLucideIcon("list-ordered", registry.listOrdered);

/** Renders the "list-plus" icon from https://lucide.dev/icons/list-plus. */
export const ListPlusIcon = /*#__PURE__*/ createLucideIcon("list-plus", registry.listPlus);

/** Renders the "list-restart" icon from https://lucide.dev/icons/list-restart. */
export const ListRestartIcon = /*#__PURE__*/ createLucideIcon("list-restart", registry.listRestart);

/** Renders the "list-sort-ascending" icon from https://lucide.dev/icons/list-sort-ascending. */
export const ListSortAscendingIcon = /*#__PURE__*/ createLucideIcon(
	"list-sort-ascending",
	registry.listSortAscending,
);

/** Renders the "list-sort-descending" icon from https://lucide.dev/icons/list-sort-descending. */
export const ListSortDescendingIcon = /*#__PURE__*/ createLucideIcon(
	"list-sort-descending",
	registry.listSortDescending,
);

/** Renders the "list-start" icon from https://lucide.dev/icons/list-start. */
export const ListStartIcon = /*#__PURE__*/ createLucideIcon("list-start", registry.listStart);

/** Renders the "list-todo" icon from https://lucide.dev/icons/list-todo. */
export const ListTodoIcon = /*#__PURE__*/ createLucideIcon("list-todo", registry.listTodo);

/** Renders the "list-tree" icon from https://lucide.dev/icons/list-tree. */
export const ListTreeIcon = /*#__PURE__*/ createLucideIcon("list-tree", registry.listTree);

/** Renders the "list-video" icon from https://lucide.dev/icons/list-video. */
export const ListVideoIcon = /*#__PURE__*/ createLucideIcon("list-video", registry.listVideo);

/** Renders the "list-x" icon from https://lucide.dev/icons/list-x. */
export const ListXIcon = /*#__PURE__*/ createLucideIcon("list-x", registry.listX);

/** Renders the "loader" icon from https://lucide.dev/icons/loader. */
export const LoaderIcon = /*#__PURE__*/ createLucideIcon("loader", registry.loader);

/** Renders the "loader-circle" icon from https://lucide.dev/icons/loader-circle. */
export const LoaderCircleIcon = /*#__PURE__*/ createLucideIcon(
	"loader-circle",
	registry.loaderCircle,
);

/** Renders the "loader-pinwheel" icon from https://lucide.dev/icons/loader-pinwheel. */
export const LoaderPinwheelIcon = /*#__PURE__*/ createLucideIcon(
	"loader-pinwheel",
	registry.loaderPinwheel,
);

/** Renders the "locate" icon from https://lucide.dev/icons/locate. */
export const LocateIcon = /*#__PURE__*/ createLucideIcon("locate", registry.locate);

/** Renders the "locate-fixed" icon from https://lucide.dev/icons/locate-fixed. */
export const LocateFixedIcon = /*#__PURE__*/ createLucideIcon("locate-fixed", registry.locateFixed);

/** Renders the "locate-off" icon from https://lucide.dev/icons/locate-off. */
export const LocateOffIcon = /*#__PURE__*/ createLucideIcon("locate-off", registry.locateOff);

/** Renders the "lock" icon from https://lucide.dev/icons/lock. */
export const LockIcon = /*#__PURE__*/ createLucideIcon("lock", registry.lock);

/** Renders the "lock-keyhole" icon from https://lucide.dev/icons/lock-keyhole. */
export const LockKeyholeIcon = /*#__PURE__*/ createLucideIcon("lock-keyhole", registry.lockKeyhole);

/** Renders the "lock-keyhole-open" icon from https://lucide.dev/icons/lock-keyhole-open. */
export const LockKeyholeOpenIcon = /*#__PURE__*/ createLucideIcon(
	"lock-keyhole-open",
	registry.lockKeyholeOpen,
);

/** Renders the "lock-open" icon from https://lucide.dev/icons/lock-open. */
export const LockOpenIcon = /*#__PURE__*/ createLucideIcon("lock-open", registry.lockOpen);

/** Renders the "log-in" icon from https://lucide.dev/icons/log-in. */
export const LogInIcon = /*#__PURE__*/ createLucideIcon("log-in", registry.logIn);

/** Renders the "log-out" icon from https://lucide.dev/icons/log-out. */
export const LogOutIcon = /*#__PURE__*/ createLucideIcon("log-out", registry.logOut);

/** Renders the "logs" icon from https://lucide.dev/icons/logs. */
export const LogsIcon = /*#__PURE__*/ createLucideIcon("logs", registry.logs);

/** Renders the "lollipop" icon from https://lucide.dev/icons/lollipop. */
export const LollipopIcon = /*#__PURE__*/ createLucideIcon("lollipop", registry.lollipop);

/** Renders the "luggage" icon from https://lucide.dev/icons/luggage. */
export const LuggageIcon = /*#__PURE__*/ createLucideIcon("luggage", registry.luggage);

/** Renders the "magnet" icon from https://lucide.dev/icons/magnet. */
export const MagnetIcon = /*#__PURE__*/ createLucideIcon("magnet", registry.magnet);

/** Renders the "mail" icon from https://lucide.dev/icons/mail. */
export const MailIcon = /*#__PURE__*/ createLucideIcon("mail", registry.mail);

/** Renders the "mail-badge" icon from https://lucide.dev/icons/mail-badge. */
export const MailBadgeIcon = /*#__PURE__*/ createLucideIcon("mail-badge", registry.mailBadge);

/** Renders the "mail-check" icon from https://lucide.dev/icons/mail-check. */
export const MailCheckIcon = /*#__PURE__*/ createLucideIcon("mail-check", registry.mailCheck);

/** Renders the "mail-clock" icon from https://lucide.dev/icons/mail-clock. */
export const MailClockIcon = /*#__PURE__*/ createLucideIcon("mail-clock", registry.mailClock);

/** Renders the "mail-dot" icon from https://lucide.dev/icons/mail-dot. */
export const MailDotIcon = /*#__PURE__*/ createLucideIcon("mail-dot", registry.mailDot);

/** Renders the "mail-minus" icon from https://lucide.dev/icons/mail-minus. */
export const MailMinusIcon = /*#__PURE__*/ createLucideIcon("mail-minus", registry.mailMinus);

/** Renders the "mail-open" icon from https://lucide.dev/icons/mail-open. */
export const MailOpenIcon = /*#__PURE__*/ createLucideIcon("mail-open", registry.mailOpen);

/** Renders the "mail-pen" icon from https://lucide.dev/icons/mail-pen. */
export const MailPenIcon = /*#__PURE__*/ createLucideIcon("mail-pen", registry.mailPen);

/** Renders the "mail-plus" icon from https://lucide.dev/icons/mail-plus. */
export const MailPlusIcon = /*#__PURE__*/ createLucideIcon("mail-plus", registry.mailPlus);

/** Renders the "mail-question-mark" icon from https://lucide.dev/icons/mail-question-mark. */
export const MailQuestionMarkIcon = /*#__PURE__*/ createLucideIcon(
	"mail-question-mark",
	registry.mailQuestionMark,
);

/** Renders the "mail-search" icon from https://lucide.dev/icons/mail-search. */
export const MailSearchIcon = /*#__PURE__*/ createLucideIcon("mail-search", registry.mailSearch);

/** Renders the "mail-warning" icon from https://lucide.dev/icons/mail-warning. */
export const MailWarningIcon = /*#__PURE__*/ createLucideIcon("mail-warning", registry.mailWarning);

/** Renders the "mail-x" icon from https://lucide.dev/icons/mail-x. */
export const MailXIcon = /*#__PURE__*/ createLucideIcon("mail-x", registry.mailX);

/** Renders the "mailbox" icon from https://lucide.dev/icons/mailbox. */
export const MailboxIcon = /*#__PURE__*/ createLucideIcon("mailbox", registry.mailbox);

/** Renders the "mails" icon from https://lucide.dev/icons/mails. */
export const MailsIcon = /*#__PURE__*/ createLucideIcon("mails", registry.mails);

/** Renders the "map" icon from https://lucide.dev/icons/map. */
export const MapIcon = /*#__PURE__*/ createLucideIcon("map", registry.map);

/** Renders the "map-minus" icon from https://lucide.dev/icons/map-minus. */
export const MapMinusIcon = /*#__PURE__*/ createLucideIcon("map-minus", registry.mapMinus);

/** Renders the "map-pin" icon from https://lucide.dev/icons/map-pin. */
export const MapPinIcon = /*#__PURE__*/ createLucideIcon("map-pin", registry.mapPin);

/** Renders the "map-pin-check" icon from https://lucide.dev/icons/map-pin-check. */
export const MapPinCheckIcon = /*#__PURE__*/ createLucideIcon(
	"map-pin-check",
	registry.mapPinCheck,
);

/** Renders the "map-pin-check-inside" icon from https://lucide.dev/icons/map-pin-check-inside. */
export const MapPinCheckInsideIcon = /*#__PURE__*/ createLucideIcon(
	"map-pin-check-inside",
	registry.mapPinCheckInside,
);

/** Renders the "map-pin-house" icon from https://lucide.dev/icons/map-pin-house. */
export const MapPinHouseIcon = /*#__PURE__*/ createLucideIcon(
	"map-pin-house",
	registry.mapPinHouse,
);

/** Renders the "map-pin-minus" icon from https://lucide.dev/icons/map-pin-minus. */
export const MapPinMinusIcon = /*#__PURE__*/ createLucideIcon(
	"map-pin-minus",
	registry.mapPinMinus,
);

/** Renders the "map-pin-minus-inside" icon from https://lucide.dev/icons/map-pin-minus-inside. */
export const MapPinMinusInsideIcon = /*#__PURE__*/ createLucideIcon(
	"map-pin-minus-inside",
	registry.mapPinMinusInside,
);

/** Renders the "map-pin-off" icon from https://lucide.dev/icons/map-pin-off. */
export const MapPinOffIcon = /*#__PURE__*/ createLucideIcon("map-pin-off", registry.mapPinOff);

/** Renders the "map-pin-pen" icon from https://lucide.dev/icons/map-pin-pen. */
export const MapPinPenIcon = /*#__PURE__*/ createLucideIcon("map-pin-pen", registry.mapPinPen);

/** Renders the "map-pin-plus" icon from https://lucide.dev/icons/map-pin-plus. */
export const MapPinPlusIcon = /*#__PURE__*/ createLucideIcon("map-pin-plus", registry.mapPinPlus);

/** Renders the "map-pin-plus-inside" icon from https://lucide.dev/icons/map-pin-plus-inside. */
export const MapPinPlusInsideIcon = /*#__PURE__*/ createLucideIcon(
	"map-pin-plus-inside",
	registry.mapPinPlusInside,
);

/** Renders the "map-pin-search" icon from https://lucide.dev/icons/map-pin-search. */
export const MapPinSearchIcon = /*#__PURE__*/ createLucideIcon(
	"map-pin-search",
	registry.mapPinSearch,
);

/** Renders the "map-pin-x" icon from https://lucide.dev/icons/map-pin-x. */
export const MapPinXIcon = /*#__PURE__*/ createLucideIcon("map-pin-x", registry.mapPinX);

/** Renders the "map-pin-x-inside" icon from https://lucide.dev/icons/map-pin-x-inside. */
export const MapPinXInsideIcon = /*#__PURE__*/ createLucideIcon(
	"map-pin-x-inside",
	registry.mapPinXInside,
);

/** Renders the "map-pinned" icon from https://lucide.dev/icons/map-pinned. */
export const MapPinnedIcon = /*#__PURE__*/ createLucideIcon("map-pinned", registry.mapPinned);

/** Renders the "map-plus" icon from https://lucide.dev/icons/map-plus. */
export const MapPlusIcon = /*#__PURE__*/ createLucideIcon("map-plus", registry.mapPlus);

/** Renders the "mars" icon from https://lucide.dev/icons/mars. */
export const MarsIcon = /*#__PURE__*/ createLucideIcon("mars", registry.mars);

/** Renders the "mars-stroke" icon from https://lucide.dev/icons/mars-stroke. */
export const MarsStrokeIcon = /*#__PURE__*/ createLucideIcon("mars-stroke", registry.marsStroke);

/** Renders the "martini" icon from https://lucide.dev/icons/martini. */
export const MartiniIcon = /*#__PURE__*/ createLucideIcon("martini", registry.martini);

/** Renders the "maximize" icon from https://lucide.dev/icons/maximize. */
export const MaximizeIcon = /*#__PURE__*/ createLucideIcon("maximize", registry.maximize);

/** Renders the "maximize-2" icon from https://lucide.dev/icons/maximize-2. */
export const Maximize2Icon = /*#__PURE__*/ createLucideIcon("maximize-2", registry.maximize2);

/** Renders the "medal" icon from https://lucide.dev/icons/medal. */
export const MedalIcon = /*#__PURE__*/ createLucideIcon("medal", registry.medal);

/** Renders the "megaphone" icon from https://lucide.dev/icons/megaphone. */
export const MegaphoneIcon = /*#__PURE__*/ createLucideIcon("megaphone", registry.megaphone);

/** Renders the "megaphone-off" icon from https://lucide.dev/icons/megaphone-off. */
export const MegaphoneOffIcon = /*#__PURE__*/ createLucideIcon(
	"megaphone-off",
	registry.megaphoneOff,
);

/** Renders the "memory-stick" icon from https://lucide.dev/icons/memory-stick. */
export const MemoryStickIcon = /*#__PURE__*/ createLucideIcon("memory-stick", registry.memoryStick);

/** Renders the "menu" icon from https://lucide.dev/icons/menu. */
export const MenuIcon = /*#__PURE__*/ createLucideIcon("menu", registry.menu);

/** Renders the "merge" icon from https://lucide.dev/icons/merge. */
export const MergeIcon = /*#__PURE__*/ createLucideIcon("merge", registry.merge);

/** Renders the "message-circle" icon from https://lucide.dev/icons/message-circle. */
export const MessageCircleIcon = /*#__PURE__*/ createLucideIcon(
	"message-circle",
	registry.messageCircle,
);

/** Renders the "message-circle-check" icon from https://lucide.dev/icons/message-circle-check. */
export const MessageCircleCheckIcon = /*#__PURE__*/ createLucideIcon(
	"message-circle-check",
	registry.messageCircleCheck,
);

/** Renders the "message-circle-code" icon from https://lucide.dev/icons/message-circle-code. */
export const MessageCircleCodeIcon = /*#__PURE__*/ createLucideIcon(
	"message-circle-code",
	registry.messageCircleCode,
);

/** Renders the "message-circle-dashed" icon from https://lucide.dev/icons/message-circle-dashed. */
export const MessageCircleDashedIcon = /*#__PURE__*/ createLucideIcon(
	"message-circle-dashed",
	registry.messageCircleDashed,
);

/** Renders the "message-circle-dashed-check" icon from https://lucide.dev/icons/message-circle-dashed-check. */
export const MessageCircleDashedCheckIcon = /*#__PURE__*/ createLucideIcon(
	"message-circle-dashed-check",
	registry.messageCircleDashedCheck,
);

/** Renders the "message-circle-heart" icon from https://lucide.dev/icons/message-circle-heart. */
export const MessageCircleHeartIcon = /*#__PURE__*/ createLucideIcon(
	"message-circle-heart",
	registry.messageCircleHeart,
);

/** Renders the "message-circle-more" icon from https://lucide.dev/icons/message-circle-more. */
export const MessageCircleMoreIcon = /*#__PURE__*/ createLucideIcon(
	"message-circle-more",
	registry.messageCircleMore,
);

/** Renders the "message-circle-off" icon from https://lucide.dev/icons/message-circle-off. */
export const MessageCircleOffIcon = /*#__PURE__*/ createLucideIcon(
	"message-circle-off",
	registry.messageCircleOff,
);

/** Renders the "message-circle-plus" icon from https://lucide.dev/icons/message-circle-plus. */
export const MessageCirclePlusIcon = /*#__PURE__*/ createLucideIcon(
	"message-circle-plus",
	registry.messageCirclePlus,
);

/** Renders the "message-circle-question-mark" icon from https://lucide.dev/icons/message-circle-question-mark. */
export const MessageCircleQuestionMarkIcon = /*#__PURE__*/ createLucideIcon(
	"message-circle-question-mark",
	registry.messageCircleQuestionMark,
);

/** Renders the "message-circle-reply" icon from https://lucide.dev/icons/message-circle-reply. */
export const MessageCircleReplyIcon = /*#__PURE__*/ createLucideIcon(
	"message-circle-reply",
	registry.messageCircleReply,
);

/** Renders the "message-circle-text" icon from https://lucide.dev/icons/message-circle-text. */
export const MessageCircleTextIcon = /*#__PURE__*/ createLucideIcon(
	"message-circle-text",
	registry.messageCircleText,
);

/** Renders the "message-circle-warning" icon from https://lucide.dev/icons/message-circle-warning. */
export const MessageCircleWarningIcon = /*#__PURE__*/ createLucideIcon(
	"message-circle-warning",
	registry.messageCircleWarning,
);

/** Renders the "message-circle-x" icon from https://lucide.dev/icons/message-circle-x. */
export const MessageCircleXIcon = /*#__PURE__*/ createLucideIcon(
	"message-circle-x",
	registry.messageCircleX,
);

/** Renders the "message-square" icon from https://lucide.dev/icons/message-square. */
export const MessageSquareIcon = /*#__PURE__*/ createLucideIcon(
	"message-square",
	registry.messageSquare,
);

/** Renders the "message-square-check" icon from https://lucide.dev/icons/message-square-check. */
export const MessageSquareCheckIcon = /*#__PURE__*/ createLucideIcon(
	"message-square-check",
	registry.messageSquareCheck,
);

/** Renders the "message-square-code" icon from https://lucide.dev/icons/message-square-code. */
export const MessageSquareCodeIcon = /*#__PURE__*/ createLucideIcon(
	"message-square-code",
	registry.messageSquareCode,
);

/** Renders the "message-square-dashed" icon from https://lucide.dev/icons/message-square-dashed. */
export const MessageSquareDashedIcon = /*#__PURE__*/ createLucideIcon(
	"message-square-dashed",
	registry.messageSquareDashed,
);

/** Renders the "message-square-diff" icon from https://lucide.dev/icons/message-square-diff. */
export const MessageSquareDiffIcon = /*#__PURE__*/ createLucideIcon(
	"message-square-diff",
	registry.messageSquareDiff,
);

/** Renders the "message-square-dot" icon from https://lucide.dev/icons/message-square-dot. */
export const MessageSquareDotIcon = /*#__PURE__*/ createLucideIcon(
	"message-square-dot",
	registry.messageSquareDot,
);

/** Renders the "message-square-heart" icon from https://lucide.dev/icons/message-square-heart. */
export const MessageSquareHeartIcon = /*#__PURE__*/ createLucideIcon(
	"message-square-heart",
	registry.messageSquareHeart,
);

/** Renders the "message-square-lock" icon from https://lucide.dev/icons/message-square-lock. */
export const MessageSquareLockIcon = /*#__PURE__*/ createLucideIcon(
	"message-square-lock",
	registry.messageSquareLock,
);

/** Renders the "message-square-more" icon from https://lucide.dev/icons/message-square-more. */
export const MessageSquareMoreIcon = /*#__PURE__*/ createLucideIcon(
	"message-square-more",
	registry.messageSquareMore,
);

/** Renders the "message-square-off" icon from https://lucide.dev/icons/message-square-off. */
export const MessageSquareOffIcon = /*#__PURE__*/ createLucideIcon(
	"message-square-off",
	registry.messageSquareOff,
);

/** Renders the "message-square-plus" icon from https://lucide.dev/icons/message-square-plus. */
export const MessageSquarePlusIcon = /*#__PURE__*/ createLucideIcon(
	"message-square-plus",
	registry.messageSquarePlus,
);

/** Renders the "message-square-quote" icon from https://lucide.dev/icons/message-square-quote. */
export const MessageSquareQuoteIcon = /*#__PURE__*/ createLucideIcon(
	"message-square-quote",
	registry.messageSquareQuote,
);

/** Renders the "message-square-reply" icon from https://lucide.dev/icons/message-square-reply. */
export const MessageSquareReplyIcon = /*#__PURE__*/ createLucideIcon(
	"message-square-reply",
	registry.messageSquareReply,
);

/** Renders the "message-square-share" icon from https://lucide.dev/icons/message-square-share. */
export const MessageSquareShareIcon = /*#__PURE__*/ createLucideIcon(
	"message-square-share",
	registry.messageSquareShare,
);

/** Renders the "message-square-text" icon from https://lucide.dev/icons/message-square-text. */
export const MessageSquareTextIcon = /*#__PURE__*/ createLucideIcon(
	"message-square-text",
	registry.messageSquareText,
);

/** Renders the "message-square-warning" icon from https://lucide.dev/icons/message-square-warning. */
export const MessageSquareWarningIcon = /*#__PURE__*/ createLucideIcon(
	"message-square-warning",
	registry.messageSquareWarning,
);

/** Renders the "message-square-x" icon from https://lucide.dev/icons/message-square-x. */
export const MessageSquareXIcon = /*#__PURE__*/ createLucideIcon(
	"message-square-x",
	registry.messageSquareX,
);

/** Renders the "messages-circle" icon from https://lucide.dev/icons/messages-circle. */
export const MessagesCircleIcon = /*#__PURE__*/ createLucideIcon(
	"messages-circle",
	registry.messagesCircle,
);

/** Renders the "messages-square" icon from https://lucide.dev/icons/messages-square. */
export const MessagesSquareIcon = /*#__PURE__*/ createLucideIcon(
	"messages-square",
	registry.messagesSquare,
);

/** Renders the "metronome" icon from https://lucide.dev/icons/metronome. */
export const MetronomeIcon = /*#__PURE__*/ createLucideIcon("metronome", registry.metronome);

/** Renders the "mic" icon from https://lucide.dev/icons/mic. */
export const MicIcon = /*#__PURE__*/ createLucideIcon("mic", registry.mic);

/** Renders the "mic-audio-lines" icon from https://lucide.dev/icons/mic-audio-lines. */
export const MicAudioLinesIcon = /*#__PURE__*/ createLucideIcon(
	"mic-audio-lines",
	registry.micAudioLines,
);

/** Renders the "mic-off" icon from https://lucide.dev/icons/mic-off. */
export const MicOffIcon = /*#__PURE__*/ createLucideIcon("mic-off", registry.micOff);

/** Renders the "mic-signal" icon from https://lucide.dev/icons/mic-signal. */
export const MicSignalIcon = /*#__PURE__*/ createLucideIcon("mic-signal", registry.micSignal);

/** Renders the "mic-vocal" icon from https://lucide.dev/icons/mic-vocal. */
export const MicVocalIcon = /*#__PURE__*/ createLucideIcon("mic-vocal", registry.micVocal);

/** Renders the "microchip" icon from https://lucide.dev/icons/microchip. */
export const MicrochipIcon = /*#__PURE__*/ createLucideIcon("microchip", registry.microchip);

/** Renders the "microscope" icon from https://lucide.dev/icons/microscope. */
export const MicroscopeIcon = /*#__PURE__*/ createLucideIcon("microscope", registry.microscope);

/** Renders the "microwave" icon from https://lucide.dev/icons/microwave. */
export const MicrowaveIcon = /*#__PURE__*/ createLucideIcon("microwave", registry.microwave);

/** Renders the "midi-port" icon from https://lucide.dev/icons/midi-port. */
export const MidiPortIcon = /*#__PURE__*/ createLucideIcon("midi-port", registry.midiPort);

/** Renders the "milestone" icon from https://lucide.dev/icons/milestone. */
export const MilestoneIcon = /*#__PURE__*/ createLucideIcon("milestone", registry.milestone);

/** Renders the "milk" icon from https://lucide.dev/icons/milk. */
export const MilkIcon = /*#__PURE__*/ createLucideIcon("milk", registry.milk);

/** Renders the "milk-off" icon from https://lucide.dev/icons/milk-off. */
export const MilkOffIcon = /*#__PURE__*/ createLucideIcon("milk-off", registry.milkOff);

/** Renders the "minimize" icon from https://lucide.dev/icons/minimize. */
export const MinimizeIcon = /*#__PURE__*/ createLucideIcon("minimize", registry.minimize);

/** Renders the "minimize-2" icon from https://lucide.dev/icons/minimize-2. */
export const Minimize2Icon = /*#__PURE__*/ createLucideIcon("minimize-2", registry.minimize2);

/** Renders the "minus" icon from https://lucide.dev/icons/minus. */
export const MinusIcon = /*#__PURE__*/ createLucideIcon("minus", registry.minus);

/** Renders the "mirror-rectangular" icon from https://lucide.dev/icons/mirror-rectangular. */
export const MirrorRectangularIcon = /*#__PURE__*/ createLucideIcon(
	"mirror-rectangular",
	registry.mirrorRectangular,
);

/** Renders the "mirror-round" icon from https://lucide.dev/icons/mirror-round. */
export const MirrorRoundIcon = /*#__PURE__*/ createLucideIcon("mirror-round", registry.mirrorRound);

/** Renders the "monitor" icon from https://lucide.dev/icons/monitor. */
export const MonitorIcon = /*#__PURE__*/ createLucideIcon("monitor", registry.monitor);

/** Renders the "monitor-check" icon from https://lucide.dev/icons/monitor-check. */
export const MonitorCheckIcon = /*#__PURE__*/ createLucideIcon(
	"monitor-check",
	registry.monitorCheck,
);

/** Renders the "monitor-cloud" icon from https://lucide.dev/icons/monitor-cloud. */
export const MonitorCloudIcon = /*#__PURE__*/ createLucideIcon(
	"monitor-cloud",
	registry.monitorCloud,
);

/** Renders the "monitor-cog" icon from https://lucide.dev/icons/monitor-cog. */
export const MonitorCogIcon = /*#__PURE__*/ createLucideIcon("monitor-cog", registry.monitorCog);

/** Renders the "monitor-dot" icon from https://lucide.dev/icons/monitor-dot. */
export const MonitorDotIcon = /*#__PURE__*/ createLucideIcon("monitor-dot", registry.monitorDot);

/** Renders the "monitor-down" icon from https://lucide.dev/icons/monitor-down. */
export const MonitorDownIcon = /*#__PURE__*/ createLucideIcon("monitor-down", registry.monitorDown);

/** Renders the "monitor-off" icon from https://lucide.dev/icons/monitor-off. */
export const MonitorOffIcon = /*#__PURE__*/ createLucideIcon("monitor-off", registry.monitorOff);

/** Renders the "monitor-pause" icon from https://lucide.dev/icons/monitor-pause. */
export const MonitorPauseIcon = /*#__PURE__*/ createLucideIcon(
	"monitor-pause",
	registry.monitorPause,
);

/** Renders the "monitor-pc" icon from https://lucide.dev/icons/monitor-pc. */
export const MonitorPcIcon = /*#__PURE__*/ createLucideIcon("monitor-pc", registry.monitorPc);

/** Renders the "monitor-play" icon from https://lucide.dev/icons/monitor-play. */
export const MonitorPlayIcon = /*#__PURE__*/ createLucideIcon("monitor-play", registry.monitorPlay);

/** Renders the "monitor-smartphone" icon from https://lucide.dev/icons/monitor-smartphone. */
export const MonitorSmartphoneIcon = /*#__PURE__*/ createLucideIcon(
	"monitor-smartphone",
	registry.monitorSmartphone,
);

/** Renders the "monitor-speaker" icon from https://lucide.dev/icons/monitor-speaker. */
export const MonitorSpeakerIcon = /*#__PURE__*/ createLucideIcon(
	"monitor-speaker",
	registry.monitorSpeaker,
);

/** Renders the "monitor-stop" icon from https://lucide.dev/icons/monitor-stop. */
export const MonitorStopIcon = /*#__PURE__*/ createLucideIcon("monitor-stop", registry.monitorStop);

/** Renders the "monitor-up" icon from https://lucide.dev/icons/monitor-up. */
export const MonitorUpIcon = /*#__PURE__*/ createLucideIcon("monitor-up", registry.monitorUp);

/** Renders the "monitor-x" icon from https://lucide.dev/icons/monitor-x. */
export const MonitorXIcon = /*#__PURE__*/ createLucideIcon("monitor-x", registry.monitorX);

/** Renders the "moon" icon from https://lucide.dev/icons/moon. */
export const MoonIcon = /*#__PURE__*/ createLucideIcon("moon", registry.moon);

/** Renders the "moon-star" icon from https://lucide.dev/icons/moon-star. */
export const MoonStarIcon = /*#__PURE__*/ createLucideIcon("moon-star", registry.moonStar);

/** Renders the "mop" icon from https://lucide.dev/icons/mop. */
export const MopIcon = /*#__PURE__*/ createLucideIcon("mop", registry.mop);

/** Renders the "mop-sparkles" icon from https://lucide.dev/icons/mop-sparkles. */
export const MopSparklesIcon = /*#__PURE__*/ createLucideIcon("mop-sparkles", registry.mopSparkles);

/** Renders the "mosque" icon from https://lucide.dev/icons/mosque. */
export const MosqueIcon = /*#__PURE__*/ createLucideIcon("mosque", registry.mosque);

/** Renders the "motorbike" icon from https://lucide.dev/icons/motorbike. */
export const MotorbikeIcon = /*#__PURE__*/ createLucideIcon("motorbike", registry.motorbike);

/** Renders the "mountain" icon from https://lucide.dev/icons/mountain. */
export const MountainIcon = /*#__PURE__*/ createLucideIcon("mountain", registry.mountain);

/** Renders the "mountain-snow" icon from https://lucide.dev/icons/mountain-snow. */
export const MountainSnowIcon = /*#__PURE__*/ createLucideIcon(
	"mountain-snow",
	registry.mountainSnow,
);

/** Renders the "mouse" icon from https://lucide.dev/icons/mouse. */
export const MouseIcon = /*#__PURE__*/ createLucideIcon("mouse", registry.mouse);

/** Renders the "mouse-left" icon from https://lucide.dev/icons/mouse-left. */
export const MouseLeftIcon = /*#__PURE__*/ createLucideIcon("mouse-left", registry.mouseLeft);

/** Renders the "mouse-off" icon from https://lucide.dev/icons/mouse-off. */
export const MouseOffIcon = /*#__PURE__*/ createLucideIcon("mouse-off", registry.mouseOff);

/** Renders the "mouse-pointer" icon from https://lucide.dev/icons/mouse-pointer. */
export const MousePointerIcon = /*#__PURE__*/ createLucideIcon(
	"mouse-pointer",
	registry.mousePointer,
);

/** Renders the "mouse-pointer-2" icon from https://lucide.dev/icons/mouse-pointer-2. */
export const MousePointer2Icon = /*#__PURE__*/ createLucideIcon(
	"mouse-pointer-2",
	registry.mousePointer2,
);

/** Renders the "mouse-pointer-2-off" icon from https://lucide.dev/icons/mouse-pointer-2-off. */
export const MousePointer2OffIcon = /*#__PURE__*/ createLucideIcon(
	"mouse-pointer-2-off",
	registry.mousePointer2Off,
);

/** Renders the "mouse-pointer-ban" icon from https://lucide.dev/icons/mouse-pointer-ban. */
export const MousePointerBanIcon = /*#__PURE__*/ createLucideIcon(
	"mouse-pointer-ban",
	registry.mousePointerBan,
);

/** Renders the "mouse-pointer-click" icon from https://lucide.dev/icons/mouse-pointer-click. */
export const MousePointerClickIcon = /*#__PURE__*/ createLucideIcon(
	"mouse-pointer-click",
	registry.mousePointerClick,
);

/** Renders the "mouse-right" icon from https://lucide.dev/icons/mouse-right. */
export const MouseRightIcon = /*#__PURE__*/ createLucideIcon("mouse-right", registry.mouseRight);

/** Renders the "mouth" icon from https://lucide.dev/icons/mouth. */
export const MouthIcon = /*#__PURE__*/ createLucideIcon("mouth", registry.mouth);

/** Renders the "mouth-off" icon from https://lucide.dev/icons/mouth-off. */
export const MouthOffIcon = /*#__PURE__*/ createLucideIcon("mouth-off", registry.mouthOff);

/** Renders the "move" icon from https://lucide.dev/icons/move. */
export const MoveIcon = /*#__PURE__*/ createLucideIcon("move", registry.move);

/** Renders the "move-3d" icon from https://lucide.dev/icons/move-3d. */
export const Move3dIcon = /*#__PURE__*/ createLucideIcon("move-3d", registry.move3d);

/** Renders the "move-diagonal" icon from https://lucide.dev/icons/move-diagonal. */
export const MoveDiagonalIcon = /*#__PURE__*/ createLucideIcon(
	"move-diagonal",
	registry.moveDiagonal,
);

/** Renders the "move-diagonal-2" icon from https://lucide.dev/icons/move-diagonal-2. */
export const MoveDiagonal2Icon = /*#__PURE__*/ createLucideIcon(
	"move-diagonal-2",
	registry.moveDiagonal2,
);

/** Renders the "move-down" icon from https://lucide.dev/icons/move-down. */
export const MoveDownIcon = /*#__PURE__*/ createLucideIcon("move-down", registry.moveDown);

/** Renders the "move-down-left" icon from https://lucide.dev/icons/move-down-left. */
export const MoveDownLeftIcon = /*#__PURE__*/ createLucideIcon(
	"move-down-left",
	registry.moveDownLeft,
);

/** Renders the "move-down-right" icon from https://lucide.dev/icons/move-down-right. */
export const MoveDownRightIcon = /*#__PURE__*/ createLucideIcon(
	"move-down-right",
	registry.moveDownRight,
);

/** Renders the "move-horizontal" icon from https://lucide.dev/icons/move-horizontal. */
export const MoveHorizontalIcon = /*#__PURE__*/ createLucideIcon(
	"move-horizontal",
	registry.moveHorizontal,
);

/** Renders the "move-left" icon from https://lucide.dev/icons/move-left. */
export const MoveLeftIcon = /*#__PURE__*/ createLucideIcon("move-left", registry.moveLeft);

/** Renders the "move-right" icon from https://lucide.dev/icons/move-right. */
export const MoveRightIcon = /*#__PURE__*/ createLucideIcon("move-right", registry.moveRight);

/** Renders the "move-up" icon from https://lucide.dev/icons/move-up. */
export const MoveUpIcon = /*#__PURE__*/ createLucideIcon("move-up", registry.moveUp);

/** Renders the "move-up-left" icon from https://lucide.dev/icons/move-up-left. */
export const MoveUpLeftIcon = /*#__PURE__*/ createLucideIcon("move-up-left", registry.moveUpLeft);

/** Renders the "move-up-right" icon from https://lucide.dev/icons/move-up-right. */
export const MoveUpRightIcon = /*#__PURE__*/ createLucideIcon(
	"move-up-right",
	registry.moveUpRight,
);

/** Renders the "move-vertical" icon from https://lucide.dev/icons/move-vertical. */
export const MoveVerticalIcon = /*#__PURE__*/ createLucideIcon(
	"move-vertical",
	registry.moveVertical,
);

/** Renders the "navigation" icon from https://lucide.dev/icons/navigation. */
export const NavigationIcon = /*#__PURE__*/ createLucideIcon("navigation", registry.navigation);

/** Renders the "navigation-2" icon from https://lucide.dev/icons/navigation-2. */
export const Navigation2Icon = /*#__PURE__*/ createLucideIcon("navigation-2", registry.navigation2);

/** Renders the "navigation-2-off" icon from https://lucide.dev/icons/navigation-2-off. */
export const Navigation2OffIcon = /*#__PURE__*/ createLucideIcon(
	"navigation-2-off",
	registry.navigation2Off,
);

/** Renders the "navigation-off" icon from https://lucide.dev/icons/navigation-off. */
export const NavigationOffIcon = /*#__PURE__*/ createLucideIcon(
	"navigation-off",
	registry.navigationOff,
);

/** Renders the "nepali-rupee" icon from https://lucide.dev/icons/nepali-rupee. */
export const NepaliRupeeIcon = /*#__PURE__*/ createLucideIcon("nepali-rupee", registry.nepaliRupee);

/** Renders the "network" icon from https://lucide.dev/icons/network. */
export const NetworkIcon = /*#__PURE__*/ createLucideIcon("network", registry.network);

/** Renders the "newspaper" icon from https://lucide.dev/icons/newspaper. */
export const NewspaperIcon = /*#__PURE__*/ createLucideIcon("newspaper", registry.newspaper);

/** Renders the "nfc" icon from https://lucide.dev/icons/nfc. */
export const NfcIcon = /*#__PURE__*/ createLucideIcon("nfc", registry.nfc);

/** Renders the "non-binary" icon from https://lucide.dev/icons/non-binary. */
export const NonBinaryIcon = /*#__PURE__*/ createLucideIcon("non-binary", registry.nonBinary);

/** Renders the "notebook" icon from https://lucide.dev/icons/notebook. */
export const NotebookIcon = /*#__PURE__*/ createLucideIcon("notebook", registry.notebook);

/** Renders the "notebook-dot" icon from https://lucide.dev/icons/notebook-dot. */
export const NotebookDotIcon = /*#__PURE__*/ createLucideIcon("notebook-dot", registry.notebookDot);

/** Renders the "notebook-pen" icon from https://lucide.dev/icons/notebook-pen. */
export const NotebookPenIcon = /*#__PURE__*/ createLucideIcon("notebook-pen", registry.notebookPen);

/** Renders the "notebook-tabs" icon from https://lucide.dev/icons/notebook-tabs. */
export const NotebookTabsIcon = /*#__PURE__*/ createLucideIcon(
	"notebook-tabs",
	registry.notebookTabs,
);

/** Renders the "notebook-text" icon from https://lucide.dev/icons/notebook-text. */
export const NotebookTextIcon = /*#__PURE__*/ createLucideIcon(
	"notebook-text",
	registry.notebookText,
);

/** Renders the "notepad-text" icon from https://lucide.dev/icons/notepad-text. */
export const NotepadTextIcon = /*#__PURE__*/ createLucideIcon("notepad-text", registry.notepadText);

/** Renders the "notepad-text-dashed" icon from https://lucide.dev/icons/notepad-text-dashed. */
export const NotepadTextDashedIcon = /*#__PURE__*/ createLucideIcon(
	"notepad-text-dashed",
	registry.notepadTextDashed,
);

/** Renders the "nut" icon from https://lucide.dev/icons/nut. */
export const NutIcon = /*#__PURE__*/ createLucideIcon("nut", registry.nut);

/** Renders the "nut-off" icon from https://lucide.dev/icons/nut-off. */
export const NutOffIcon = /*#__PURE__*/ createLucideIcon("nut-off", registry.nutOff);

/** Renders the "octagon" icon from https://lucide.dev/icons/octagon. */
export const OctagonIcon = /*#__PURE__*/ createLucideIcon("octagon", registry.octagon);

/** Renders the "octagon-alert" icon from https://lucide.dev/icons/octagon-alert. */
export const OctagonAlertIcon = /*#__PURE__*/ createLucideIcon(
	"octagon-alert",
	registry.octagonAlert,
);

/** Renders the "octagon-minus" icon from https://lucide.dev/icons/octagon-minus. */
export const OctagonMinusIcon = /*#__PURE__*/ createLucideIcon(
	"octagon-minus",
	registry.octagonMinus,
);

/** Renders the "octagon-pause" icon from https://lucide.dev/icons/octagon-pause. */
export const OctagonPauseIcon = /*#__PURE__*/ createLucideIcon(
	"octagon-pause",
	registry.octagonPause,
);

/** Renders the "octagon-x" icon from https://lucide.dev/icons/octagon-x. */
export const OctagonXIcon = /*#__PURE__*/ createLucideIcon("octagon-x", registry.octagonX);

/** Renders the "omega" icon from https://lucide.dev/icons/omega. */
export const OmegaIcon = /*#__PURE__*/ createLucideIcon("omega", registry.omega);

/** Renders the "option" icon from https://lucide.dev/icons/option. */
export const OptionIcon = /*#__PURE__*/ createLucideIcon("option", registry.option);

/** Renders the "orbit" icon from https://lucide.dev/icons/orbit. */
export const OrbitIcon = /*#__PURE__*/ createLucideIcon("orbit", registry.orbit);

/** Renders the "origami" icon from https://lucide.dev/icons/origami. */
export const OrigamiIcon = /*#__PURE__*/ createLucideIcon("origami", registry.origami);

/** Renders the "package" icon from https://lucide.dev/icons/package. */
export const PackageIcon = /*#__PURE__*/ createLucideIcon("package", registry.packageNode);

/** Renders the "package-2" icon from https://lucide.dev/icons/package-2. */
export const Package2Icon = /*#__PURE__*/ createLucideIcon("package-2", registry.package2);

/** Renders the "package-check" icon from https://lucide.dev/icons/package-check. */
export const PackageCheckIcon = /*#__PURE__*/ createLucideIcon(
	"package-check",
	registry.packageCheck,
);

/** Renders the "package-minus" icon from https://lucide.dev/icons/package-minus. */
export const PackageMinusIcon = /*#__PURE__*/ createLucideIcon(
	"package-minus",
	registry.packageMinus,
);

/** Renders the "package-open" icon from https://lucide.dev/icons/package-open. */
export const PackageOpenIcon = /*#__PURE__*/ createLucideIcon("package-open", registry.packageOpen);

/** Renders the "package-plus" icon from https://lucide.dev/icons/package-plus. */
export const PackagePlusIcon = /*#__PURE__*/ createLucideIcon("package-plus", registry.packagePlus);

/** Renders the "package-search" icon from https://lucide.dev/icons/package-search. */
export const PackageSearchIcon = /*#__PURE__*/ createLucideIcon(
	"package-search",
	registry.packageSearch,
);

/** Renders the "package-x" icon from https://lucide.dev/icons/package-x. */
export const PackageXIcon = /*#__PURE__*/ createLucideIcon("package-x", registry.packageX);

/** Renders the "paint-bucket" icon from https://lucide.dev/icons/paint-bucket. */
export const PaintBucketIcon = /*#__PURE__*/ createLucideIcon("paint-bucket", registry.paintBucket);

/** Renders the "paint-roller" icon from https://lucide.dev/icons/paint-roller. */
export const PaintRollerIcon = /*#__PURE__*/ createLucideIcon("paint-roller", registry.paintRoller);

/** Renders the "paintbrush" icon from https://lucide.dev/icons/paintbrush. */
export const PaintbrushIcon = /*#__PURE__*/ createLucideIcon("paintbrush", registry.paintbrush);

/** Renders the "paintbrush-vertical" icon from https://lucide.dev/icons/paintbrush-vertical. */
export const PaintbrushVerticalIcon = /*#__PURE__*/ createLucideIcon(
	"paintbrush-vertical",
	registry.paintbrushVertical,
);

/** Renders the "palette" icon from https://lucide.dev/icons/palette. */
export const PaletteIcon = /*#__PURE__*/ createLucideIcon("palette", registry.palette);

/** Renders the "panda" icon from https://lucide.dev/icons/panda. */
export const PandaIcon = /*#__PURE__*/ createLucideIcon("panda", registry.panda);

/** Renders the "panel-bottom" icon from https://lucide.dev/icons/panel-bottom. */
export const PanelBottomIcon = /*#__PURE__*/ createLucideIcon("panel-bottom", registry.panelBottom);

/** Renders the "panel-bottom-close" icon from https://lucide.dev/icons/panel-bottom-close. */
export const PanelBottomCloseIcon = /*#__PURE__*/ createLucideIcon(
	"panel-bottom-close",
	registry.panelBottomClose,
);

/** Renders the "panel-bottom-dashed" icon from https://lucide.dev/icons/panel-bottom-dashed. */
export const PanelBottomDashedIcon = /*#__PURE__*/ createLucideIcon(
	"panel-bottom-dashed",
	registry.panelBottomDashed,
);

/** Renders the "panel-bottom-open" icon from https://lucide.dev/icons/panel-bottom-open. */
export const PanelBottomOpenIcon = /*#__PURE__*/ createLucideIcon(
	"panel-bottom-open",
	registry.panelBottomOpen,
);

/** Renders the "panel-left" icon from https://lucide.dev/icons/panel-left. */
export const PanelLeftIcon = /*#__PURE__*/ createLucideIcon("panel-left", registry.panelLeft);

/** Renders the "panel-left-close" icon from https://lucide.dev/icons/panel-left-close. */
export const PanelLeftCloseIcon = /*#__PURE__*/ createLucideIcon(
	"panel-left-close",
	registry.panelLeftClose,
);

/** Renders the "panel-left-dashed" icon from https://lucide.dev/icons/panel-left-dashed. */
export const PanelLeftDashedIcon = /*#__PURE__*/ createLucideIcon(
	"panel-left-dashed",
	registry.panelLeftDashed,
);

/** Renders the "panel-left-open" icon from https://lucide.dev/icons/panel-left-open. */
export const PanelLeftOpenIcon = /*#__PURE__*/ createLucideIcon(
	"panel-left-open",
	registry.panelLeftOpen,
);

/** Renders the "panel-left-right-dashed" icon from https://lucide.dev/icons/panel-left-right-dashed. */
export const PanelLeftRightDashedIcon = /*#__PURE__*/ createLucideIcon(
	"panel-left-right-dashed",
	registry.panelLeftRightDashed,
);

/** Renders the "panel-right" icon from https://lucide.dev/icons/panel-right. */
export const PanelRightIcon = /*#__PURE__*/ createLucideIcon("panel-right", registry.panelRight);

/** Renders the "panel-right-close" icon from https://lucide.dev/icons/panel-right-close. */
export const PanelRightCloseIcon = /*#__PURE__*/ createLucideIcon(
	"panel-right-close",
	registry.panelRightClose,
);

/** Renders the "panel-right-dashed" icon from https://lucide.dev/icons/panel-right-dashed. */
export const PanelRightDashedIcon = /*#__PURE__*/ createLucideIcon(
	"panel-right-dashed",
	registry.panelRightDashed,
);

/** Renders the "panel-right-open" icon from https://lucide.dev/icons/panel-right-open. */
export const PanelRightOpenIcon = /*#__PURE__*/ createLucideIcon(
	"panel-right-open",
	registry.panelRightOpen,
);

/** Renders the "panel-top" icon from https://lucide.dev/icons/panel-top. */
export const PanelTopIcon = /*#__PURE__*/ createLucideIcon("panel-top", registry.panelTop);

/** Renders the "panel-top-bottom-dashed" icon from https://lucide.dev/icons/panel-top-bottom-dashed. */
export const PanelTopBottomDashedIcon = /*#__PURE__*/ createLucideIcon(
	"panel-top-bottom-dashed",
	registry.panelTopBottomDashed,
);

/** Renders the "panel-top-close" icon from https://lucide.dev/icons/panel-top-close. */
export const PanelTopCloseIcon = /*#__PURE__*/ createLucideIcon(
	"panel-top-close",
	registry.panelTopClose,
);

/** Renders the "panel-top-dashed" icon from https://lucide.dev/icons/panel-top-dashed. */
export const PanelTopDashedIcon = /*#__PURE__*/ createLucideIcon(
	"panel-top-dashed",
	registry.panelTopDashed,
);

/** Renders the "panel-top-open" icon from https://lucide.dev/icons/panel-top-open. */
export const PanelTopOpenIcon = /*#__PURE__*/ createLucideIcon(
	"panel-top-open",
	registry.panelTopOpen,
);

/** Renders the "panels-left-bottom" icon from https://lucide.dev/icons/panels-left-bottom. */
export const PanelsLeftBottomIcon = /*#__PURE__*/ createLucideIcon(
	"panels-left-bottom",
	registry.panelsLeftBottom,
);

/** Renders the "panels-right-bottom" icon from https://lucide.dev/icons/panels-right-bottom. */
export const PanelsRightBottomIcon = /*#__PURE__*/ createLucideIcon(
	"panels-right-bottom",
	registry.panelsRightBottom,
);

/** Renders the "panels-top-left" icon from https://lucide.dev/icons/panels-top-left. */
export const PanelsTopLeftIcon = /*#__PURE__*/ createLucideIcon(
	"panels-top-left",
	registry.panelsTopLeft,
);

/** Renders the "paper-bag" icon from https://lucide.dev/icons/paper-bag. */
export const PaperBagIcon = /*#__PURE__*/ createLucideIcon("paper-bag", registry.paperBag);

/** Renders the "paperclip" icon from https://lucide.dev/icons/paperclip. */
export const PaperclipIcon = /*#__PURE__*/ createLucideIcon("paperclip", registry.paperclip);

/** Renders the "parasol" icon from https://lucide.dev/icons/parasol. */
export const ParasolIcon = /*#__PURE__*/ createLucideIcon("parasol", registry.parasol);

/** Renders the "parentheses" icon from https://lucide.dev/icons/parentheses. */
export const ParenthesesIcon = /*#__PURE__*/ createLucideIcon("parentheses", registry.parentheses);

/** Renders the "park" icon from https://lucide.dev/icons/park. */
export const ParkIcon = /*#__PURE__*/ createLucideIcon("park", registry.park);

/** Renders the "parking-meter" icon from https://lucide.dev/icons/parking-meter. */
export const ParkingMeterIcon = /*#__PURE__*/ createLucideIcon(
	"parking-meter",
	registry.parkingMeter,
);

/** Renders the "party-popper" icon from https://lucide.dev/icons/party-popper. */
export const PartyPopperIcon = /*#__PURE__*/ createLucideIcon("party-popper", registry.partyPopper);

/** Renders the "pause" icon from https://lucide.dev/icons/pause. */
export const PauseIcon = /*#__PURE__*/ createLucideIcon("pause", registry.pause);

/** Renders the "paw-print" icon from https://lucide.dev/icons/paw-print. */
export const PawPrintIcon = /*#__PURE__*/ createLucideIcon("paw-print", registry.pawPrint);

/** Renders the "pc-case" icon from https://lucide.dev/icons/pc-case. */
export const PcCaseIcon = /*#__PURE__*/ createLucideIcon("pc-case", registry.pcCase);

/** Renders the "pen" icon from https://lucide.dev/icons/pen. */
export const PenIcon = /*#__PURE__*/ createLucideIcon("pen", registry.pen);

/** Renders the "pen-line" icon from https://lucide.dev/icons/pen-line. */
export const PenLineIcon = /*#__PURE__*/ createLucideIcon("pen-line", registry.penLine);

/** Renders the "pen-off" icon from https://lucide.dev/icons/pen-off. */
export const PenOffIcon = /*#__PURE__*/ createLucideIcon("pen-off", registry.penOff);

/** Renders the "pen-tool" icon from https://lucide.dev/icons/pen-tool. */
export const PenToolIcon = /*#__PURE__*/ createLucideIcon("pen-tool", registry.penTool);

/** Renders the "pencil" icon from https://lucide.dev/icons/pencil. */
export const PencilIcon = /*#__PURE__*/ createLucideIcon("pencil", registry.pencil);

/** Renders the "pencil-line" icon from https://lucide.dev/icons/pencil-line. */
export const PencilLineIcon = /*#__PURE__*/ createLucideIcon("pencil-line", registry.pencilLine);

/** Renders the "pencil-off" icon from https://lucide.dev/icons/pencil-off. */
export const PencilOffIcon = /*#__PURE__*/ createLucideIcon("pencil-off", registry.pencilOff);

/** Renders the "pencil-ruler" icon from https://lucide.dev/icons/pencil-ruler. */
export const PencilRulerIcon = /*#__PURE__*/ createLucideIcon("pencil-ruler", registry.pencilRuler);

/** Renders the "pencil-sparkles" icon from https://lucide.dev/icons/pencil-sparkles. */
export const PencilSparklesIcon = /*#__PURE__*/ createLucideIcon(
	"pencil-sparkles",
	registry.pencilSparkles,
);

/** Renders the "pentagon" icon from https://lucide.dev/icons/pentagon. */
export const PentagonIcon = /*#__PURE__*/ createLucideIcon("pentagon", registry.pentagon);

/** Renders the "percent" icon from https://lucide.dev/icons/percent. */
export const PercentIcon = /*#__PURE__*/ createLucideIcon("percent", registry.percent);

/** Renders the "perfume-bottle" icon from https://lucide.dev/icons/perfume-bottle. */
export const PerfumeBottleIcon = /*#__PURE__*/ createLucideIcon(
	"perfume-bottle",
	registry.perfumeBottle,
);

/** Renders the "person-standing" icon from https://lucide.dev/icons/person-standing. */
export const PersonStandingIcon = /*#__PURE__*/ createLucideIcon(
	"person-standing",
	registry.personStanding,
);

/** Renders the "phi" icon from https://lucide.dev/icons/phi. */
export const PhiIcon = /*#__PURE__*/ createLucideIcon("phi", registry.phi);

/** Renders the "philippine-peso" icon from https://lucide.dev/icons/philippine-peso. */
export const PhilippinePesoIcon = /*#__PURE__*/ createLucideIcon(
	"philippine-peso",
	registry.philippinePeso,
);

/** Renders the "phone" icon from https://lucide.dev/icons/phone. */
export const PhoneIcon = /*#__PURE__*/ createLucideIcon("phone", registry.phone);

/** Renders the "phone-call" icon from https://lucide.dev/icons/phone-call. */
export const PhoneCallIcon = /*#__PURE__*/ createLucideIcon("phone-call", registry.phoneCall);

/** Renders the "phone-forwarded" icon from https://lucide.dev/icons/phone-forwarded. */
export const PhoneForwardedIcon = /*#__PURE__*/ createLucideIcon(
	"phone-forwarded",
	registry.phoneForwarded,
);

/** Renders the "phone-incoming" icon from https://lucide.dev/icons/phone-incoming. */
export const PhoneIncomingIcon = /*#__PURE__*/ createLucideIcon(
	"phone-incoming",
	registry.phoneIncoming,
);

/** Renders the "phone-log" icon from https://lucide.dev/icons/phone-log. */
export const PhoneLogIcon = /*#__PURE__*/ createLucideIcon("phone-log", registry.phoneLog);

/** Renders the "phone-missed" icon from https://lucide.dev/icons/phone-missed. */
export const PhoneMissedIcon = /*#__PURE__*/ createLucideIcon("phone-missed", registry.phoneMissed);

/** Renders the "phone-off" icon from https://lucide.dev/icons/phone-off. */
export const PhoneOffIcon = /*#__PURE__*/ createLucideIcon("phone-off", registry.phoneOff);

/** Renders the "phone-outgoing" icon from https://lucide.dev/icons/phone-outgoing. */
export const PhoneOutgoingIcon = /*#__PURE__*/ createLucideIcon(
	"phone-outgoing",
	registry.phoneOutgoing,
);

/** Renders the "pi" icon from https://lucide.dev/icons/pi. */
export const PiIcon = /*#__PURE__*/ createLucideIcon("pi", registry.pi);

/** Renders the "piano" icon from https://lucide.dev/icons/piano. */
export const PianoIcon = /*#__PURE__*/ createLucideIcon("piano", registry.piano);

/** Renders the "pickaxe" icon from https://lucide.dev/icons/pickaxe. */
export const PickaxeIcon = /*#__PURE__*/ createLucideIcon("pickaxe", registry.pickaxe);

/** Renders the "picture-in-picture" icon from https://lucide.dev/icons/picture-in-picture. */
export const PictureInPictureIcon = /*#__PURE__*/ createLucideIcon(
	"picture-in-picture",
	registry.pictureInPicture,
);

/** Renders the "picture-in-picture-2" icon from https://lucide.dev/icons/picture-in-picture-2. */
export const PictureInPicture2Icon = /*#__PURE__*/ createLucideIcon(
	"picture-in-picture-2",
	registry.pictureInPicture2,
);

/** Renders the "piggy-bank" icon from https://lucide.dev/icons/piggy-bank. */
export const PiggyBankIcon = /*#__PURE__*/ createLucideIcon("piggy-bank", registry.piggyBank);

/** Renders the "pilcrow" icon from https://lucide.dev/icons/pilcrow. */
export const PilcrowIcon = /*#__PURE__*/ createLucideIcon("pilcrow", registry.pilcrow);

/** Renders the "pilcrow-left" icon from https://lucide.dev/icons/pilcrow-left. */
export const PilcrowLeftIcon = /*#__PURE__*/ createLucideIcon("pilcrow-left", registry.pilcrowLeft);

/** Renders the "pilcrow-right" icon from https://lucide.dev/icons/pilcrow-right. */
export const PilcrowRightIcon = /*#__PURE__*/ createLucideIcon(
	"pilcrow-right",
	registry.pilcrowRight,
);

/** Renders the "pill" icon from https://lucide.dev/icons/pill. */
export const PillIcon = /*#__PURE__*/ createLucideIcon("pill", registry.pill);

/** Renders the "pill-bottle" icon from https://lucide.dev/icons/pill-bottle. */
export const PillBottleIcon = /*#__PURE__*/ createLucideIcon("pill-bottle", registry.pillBottle);

/** Renders the "pin" icon from https://lucide.dev/icons/pin. */
export const PinIcon = /*#__PURE__*/ createLucideIcon("pin", registry.pin);

/** Renders the "pin-off" icon from https://lucide.dev/icons/pin-off. */
export const PinOffIcon = /*#__PURE__*/ createLucideIcon("pin-off", registry.pinOff);

/** Renders the "pipette" icon from https://lucide.dev/icons/pipette. */
export const PipetteIcon = /*#__PURE__*/ createLucideIcon("pipette", registry.pipette);

/** Renders the "pizza" icon from https://lucide.dev/icons/pizza. */
export const PizzaIcon = /*#__PURE__*/ createLucideIcon("pizza", registry.pizza);

/** Renders the "plane" icon from https://lucide.dev/icons/plane. */
export const PlaneIcon = /*#__PURE__*/ createLucideIcon("plane", registry.plane);

/** Renders the "plane-landing" icon from https://lucide.dev/icons/plane-landing. */
export const PlaneLandingIcon = /*#__PURE__*/ createLucideIcon(
	"plane-landing",
	registry.planeLanding,
);

/** Renders the "plane-takeoff" icon from https://lucide.dev/icons/plane-takeoff. */
export const PlaneTakeoffIcon = /*#__PURE__*/ createLucideIcon(
	"plane-takeoff",
	registry.planeTakeoff,
);

/** Renders the "plant-pot" icon from https://lucide.dev/icons/plant-pot. */
export const PlantPotIcon = /*#__PURE__*/ createLucideIcon("plant-pot", registry.plantPot);

/** Renders the "play" icon from https://lucide.dev/icons/play. */
export const PlayIcon = /*#__PURE__*/ createLucideIcon("play", registry.play);

/** Renders the "play-off" icon from https://lucide.dev/icons/play-off. */
export const PlayOffIcon = /*#__PURE__*/ createLucideIcon("play-off", registry.playOff);

/** Renders the "playing-card" icon from https://lucide.dev/icons/playing-card. */
export const PlayingCardIcon = /*#__PURE__*/ createLucideIcon("playing-card", registry.playingCard);

/** Renders the "playing-cards" icon from https://lucide.dev/icons/playing-cards. */
export const PlayingCardsIcon = /*#__PURE__*/ createLucideIcon(
	"playing-cards",
	registry.playingCards,
);

/** Renders the "playing-cards-fan" icon from https://lucide.dev/icons/playing-cards-fan. */
export const PlayingCardsFanIcon = /*#__PURE__*/ createLucideIcon(
	"playing-cards-fan",
	registry.playingCardsFan,
);

/** Renders the "plug" icon from https://lucide.dev/icons/plug. */
export const PlugIcon = /*#__PURE__*/ createLucideIcon("plug", registry.plug);

/** Renders the "plug-2" icon from https://lucide.dev/icons/plug-2. */
export const Plug2Icon = /*#__PURE__*/ createLucideIcon("plug-2", registry.plug2);

/** Renders the "plug-zap" icon from https://lucide.dev/icons/plug-zap. */
export const PlugZapIcon = /*#__PURE__*/ createLucideIcon("plug-zap", registry.plugZap);

/** Renders the "plus" icon from https://lucide.dev/icons/plus. */
export const PlusIcon = /*#__PURE__*/ createLucideIcon("plus", registry.plus);

/** Renders the "pocket-knife" icon from https://lucide.dev/icons/pocket-knife. */
export const PocketKnifeIcon = /*#__PURE__*/ createLucideIcon("pocket-knife", registry.pocketKnife);

/** Renders the "podium" icon from https://lucide.dev/icons/podium. */
export const PodiumIcon = /*#__PURE__*/ createLucideIcon("podium", registry.podium);

/** Renders the "pointer" icon from https://lucide.dev/icons/pointer. */
export const PointerIcon = /*#__PURE__*/ createLucideIcon("pointer", registry.pointer);

/** Renders the "pointer-off" icon from https://lucide.dev/icons/pointer-off. */
export const PointerOffIcon = /*#__PURE__*/ createLucideIcon("pointer-off", registry.pointerOff);

/** Renders the "popcorn" icon from https://lucide.dev/icons/popcorn. */
export const PopcornIcon = /*#__PURE__*/ createLucideIcon("popcorn", registry.popcorn);

/** Renders the "popsicle" icon from https://lucide.dev/icons/popsicle. */
export const PopsicleIcon = /*#__PURE__*/ createLucideIcon("popsicle", registry.popsicle);

/** Renders the "pound-sterling" icon from https://lucide.dev/icons/pound-sterling. */
export const PoundSterlingIcon = /*#__PURE__*/ createLucideIcon(
	"pound-sterling",
	registry.poundSterling,
);

/** Renders the "power" icon from https://lucide.dev/icons/power. */
export const PowerIcon = /*#__PURE__*/ createLucideIcon("power", registry.power);

/** Renders the "power-off" icon from https://lucide.dev/icons/power-off. */
export const PowerOffIcon = /*#__PURE__*/ createLucideIcon("power-off", registry.powerOff);

/** Renders the "presentation" icon from https://lucide.dev/icons/presentation. */
export const PresentationIcon = /*#__PURE__*/ createLucideIcon(
	"presentation",
	registry.presentation,
);

/** Renders the "printer" icon from https://lucide.dev/icons/printer. */
export const PrinterIcon = /*#__PURE__*/ createLucideIcon("printer", registry.printer);

/** Renders the "printer-3d" icon from https://lucide.dev/icons/printer-3d. */
export const Printer3dIcon = /*#__PURE__*/ createLucideIcon("printer-3d", registry.printer3d);

/** Renders the "printer-check" icon from https://lucide.dev/icons/printer-check. */
export const PrinterCheckIcon = /*#__PURE__*/ createLucideIcon(
	"printer-check",
	registry.printerCheck,
);

/** Renders the "printer-x" icon from https://lucide.dev/icons/printer-x. */
export const PrinterXIcon = /*#__PURE__*/ createLucideIcon("printer-x", registry.printerX);

/** Renders the "projector" icon from https://lucide.dev/icons/projector. */
export const ProjectorIcon = /*#__PURE__*/ createLucideIcon("projector", registry.projector);

/** Renders the "proportions" icon from https://lucide.dev/icons/proportions. */
export const ProportionsIcon = /*#__PURE__*/ createLucideIcon("proportions", registry.proportions);

/** Renders the "puzzle" icon from https://lucide.dev/icons/puzzle. */
export const PuzzleIcon = /*#__PURE__*/ createLucideIcon("puzzle", registry.puzzle);

/** Renders the "pyramid" icon from https://lucide.dev/icons/pyramid. */
export const PyramidIcon = /*#__PURE__*/ createLucideIcon("pyramid", registry.pyramid);

/** Renders the "qr-code" icon from https://lucide.dev/icons/qr-code. */
export const QrCodeIcon = /*#__PURE__*/ createLucideIcon("qr-code", registry.qrCode);

/** Renders the "quote" icon from https://lucide.dev/icons/quote. */
export const QuoteIcon = /*#__PURE__*/ createLucideIcon("quote", registry.quote);

/** Renders the "rabbit" icon from https://lucide.dev/icons/rabbit. */
export const RabbitIcon = /*#__PURE__*/ createLucideIcon("rabbit", registry.rabbit);

/** Renders the "radar" icon from https://lucide.dev/icons/radar. */
export const RadarIcon = /*#__PURE__*/ createLucideIcon("radar", registry.radar);

/** Renders the "radiation" icon from https://lucide.dev/icons/radiation. */
export const RadiationIcon = /*#__PURE__*/ createLucideIcon("radiation", registry.radiation);

/** Renders the "radical" icon from https://lucide.dev/icons/radical. */
export const RadicalIcon = /*#__PURE__*/ createLucideIcon("radical", registry.radical);

/** Renders the "radio" icon from https://lucide.dev/icons/radio. */
export const RadioIcon = /*#__PURE__*/ createLucideIcon("radio", registry.radio);

/** Renders the "radio-off" icon from https://lucide.dev/icons/radio-off. */
export const RadioOffIcon = /*#__PURE__*/ createLucideIcon("radio-off", registry.radioOff);

/** Renders the "radio-receiver" icon from https://lucide.dev/icons/radio-receiver. */
export const RadioReceiverIcon = /*#__PURE__*/ createLucideIcon(
	"radio-receiver",
	registry.radioReceiver,
);

/** Renders the "radio-tower" icon from https://lucide.dev/icons/radio-tower. */
export const RadioTowerIcon = /*#__PURE__*/ createLucideIcon("radio-tower", registry.radioTower);

/** Renders the "radius" icon from https://lucide.dev/icons/radius. */
export const RadiusIcon = /*#__PURE__*/ createLucideIcon("radius", registry.radius);

/** Renders the "rainbow" icon from https://lucide.dev/icons/rainbow. */
export const RainbowIcon = /*#__PURE__*/ createLucideIcon("rainbow", registry.rainbow);

/** Renders the "rat" icon from https://lucide.dev/icons/rat. */
export const RatIcon = /*#__PURE__*/ createLucideIcon("rat", registry.rat);

/** Renders the "ratio" icon from https://lucide.dev/icons/ratio. */
export const RatioIcon = /*#__PURE__*/ createLucideIcon("ratio", registry.ratio);

/** Renders the "receipt" icon from https://lucide.dev/icons/receipt. */
export const ReceiptIcon = /*#__PURE__*/ createLucideIcon("receipt", registry.receipt);

/** Renders the "receipt-cent" icon from https://lucide.dev/icons/receipt-cent. */
export const ReceiptCentIcon = /*#__PURE__*/ createLucideIcon("receipt-cent", registry.receiptCent);

/** Renders the "receipt-euro" icon from https://lucide.dev/icons/receipt-euro. */
export const ReceiptEuroIcon = /*#__PURE__*/ createLucideIcon("receipt-euro", registry.receiptEuro);

/** Renders the "receipt-indian-rupee" icon from https://lucide.dev/icons/receipt-indian-rupee. */
export const ReceiptIndianRupeeIcon = /*#__PURE__*/ createLucideIcon(
	"receipt-indian-rupee",
	registry.receiptIndianRupee,
);

/** Renders the "receipt-japanese-yen" icon from https://lucide.dev/icons/receipt-japanese-yen. */
export const ReceiptJapaneseYenIcon = /*#__PURE__*/ createLucideIcon(
	"receipt-japanese-yen",
	registry.receiptJapaneseYen,
);

/** Renders the "receipt-pound-sterling" icon from https://lucide.dev/icons/receipt-pound-sterling. */
export const ReceiptPoundSterlingIcon = /*#__PURE__*/ createLucideIcon(
	"receipt-pound-sterling",
	registry.receiptPoundSterling,
);

/** Renders the "receipt-russian-ruble" icon from https://lucide.dev/icons/receipt-russian-ruble. */
export const ReceiptRussianRubleIcon = /*#__PURE__*/ createLucideIcon(
	"receipt-russian-ruble",
	registry.receiptRussianRuble,
);

/** Renders the "receipt-swiss-franc" icon from https://lucide.dev/icons/receipt-swiss-franc. */
export const ReceiptSwissFrancIcon = /*#__PURE__*/ createLucideIcon(
	"receipt-swiss-franc",
	registry.receiptSwissFranc,
);

/** Renders the "receipt-text" icon from https://lucide.dev/icons/receipt-text. */
export const ReceiptTextIcon = /*#__PURE__*/ createLucideIcon("receipt-text", registry.receiptText);

/** Renders the "receipt-turkish-lira" icon from https://lucide.dev/icons/receipt-turkish-lira. */
export const ReceiptTurkishLiraIcon = /*#__PURE__*/ createLucideIcon(
	"receipt-turkish-lira",
	registry.receiptTurkishLira,
);

/** Renders the "rectangle-circle" icon from https://lucide.dev/icons/rectangle-circle. */
export const RectangleCircleIcon = /*#__PURE__*/ createLucideIcon(
	"rectangle-circle",
	registry.rectangleCircle,
);

/** Renders the "rectangle-ellipsis" icon from https://lucide.dev/icons/rectangle-ellipsis. */
export const RectangleEllipsisIcon = /*#__PURE__*/ createLucideIcon(
	"rectangle-ellipsis",
	registry.rectangleEllipsis,
);

/** Renders the "rectangle-goggles" icon from https://lucide.dev/icons/rectangle-goggles. */
export const RectangleGogglesIcon = /*#__PURE__*/ createLucideIcon(
	"rectangle-goggles",
	registry.rectangleGoggles,
);

/** Renders the "rectangle-horizontal" icon from https://lucide.dev/icons/rectangle-horizontal. */
export const RectangleHorizontalIcon = /*#__PURE__*/ createLucideIcon(
	"rectangle-horizontal",
	registry.rectangleHorizontal,
);

/** Renders the "rectangle-vertical" icon from https://lucide.dev/icons/rectangle-vertical. */
export const RectangleVerticalIcon = /*#__PURE__*/ createLucideIcon(
	"rectangle-vertical",
	registry.rectangleVertical,
);

/** Renders the "recycle" icon from https://lucide.dev/icons/recycle. */
export const RecycleIcon = /*#__PURE__*/ createLucideIcon("recycle", registry.recycle);

/** Renders the "redo" icon from https://lucide.dev/icons/redo. */
export const RedoIcon = /*#__PURE__*/ createLucideIcon("redo", registry.redo);

/** Renders the "redo-2" icon from https://lucide.dev/icons/redo-2. */
export const Redo2Icon = /*#__PURE__*/ createLucideIcon("redo-2", registry.redo2);

/** Renders the "redo-dot" icon from https://lucide.dev/icons/redo-dot. */
export const RedoDotIcon = /*#__PURE__*/ createLucideIcon("redo-dot", registry.redoDot);

/** Renders the "refresh-ccw" icon from https://lucide.dev/icons/refresh-ccw. */
export const RefreshCcwIcon = /*#__PURE__*/ createLucideIcon("refresh-ccw", registry.refreshCcw);

/** Renders the "refresh-ccw-dot" icon from https://lucide.dev/icons/refresh-ccw-dot. */
export const RefreshCcwDotIcon = /*#__PURE__*/ createLucideIcon(
	"refresh-ccw-dot",
	registry.refreshCcwDot,
);

/** Renders the "refresh-cw" icon from https://lucide.dev/icons/refresh-cw. */
export const RefreshCwIcon = /*#__PURE__*/ createLucideIcon("refresh-cw", registry.refreshCw);

/** Renders the "refresh-cw-off" icon from https://lucide.dev/icons/refresh-cw-off. */
export const RefreshCwOffIcon = /*#__PURE__*/ createLucideIcon(
	"refresh-cw-off",
	registry.refreshCwOff,
);

/** Renders the "refrigerator" icon from https://lucide.dev/icons/refrigerator. */
export const RefrigeratorIcon = /*#__PURE__*/ createLucideIcon(
	"refrigerator",
	registry.refrigerator,
);

/** Renders the "regex" icon from https://lucide.dev/icons/regex. */
export const RegexIcon = /*#__PURE__*/ createLucideIcon("regex", registry.regex);

/** Renders the "remove-formatting" icon from https://lucide.dev/icons/remove-formatting. */
export const RemoveFormattingIcon = /*#__PURE__*/ createLucideIcon(
	"remove-formatting",
	registry.removeFormatting,
);

/** Renders the "repeat" icon from https://lucide.dev/icons/repeat. */
export const RepeatIcon = /*#__PURE__*/ createLucideIcon("repeat", registry.repeat);

/** Renders the "repeat-1" icon from https://lucide.dev/icons/repeat-1. */
export const Repeat1Icon = /*#__PURE__*/ createLucideIcon("repeat-1", registry.repeat1);

/** Renders the "repeat-2" icon from https://lucide.dev/icons/repeat-2. */
export const Repeat2Icon = /*#__PURE__*/ createLucideIcon("repeat-2", registry.repeat2);

/** Renders the "repeat-off" icon from https://lucide.dev/icons/repeat-off. */
export const RepeatOffIcon = /*#__PURE__*/ createLucideIcon("repeat-off", registry.repeatOff);

/** Renders the "replace" icon from https://lucide.dev/icons/replace. */
export const ReplaceIcon = /*#__PURE__*/ createLucideIcon("replace", registry.replace);

/** Renders the "replace-all" icon from https://lucide.dev/icons/replace-all. */
export const ReplaceAllIcon = /*#__PURE__*/ createLucideIcon("replace-all", registry.replaceAll);

/** Renders the "reply" icon from https://lucide.dev/icons/reply. */
export const ReplyIcon = /*#__PURE__*/ createLucideIcon("reply", registry.reply);

/** Renders the "reply-all" icon from https://lucide.dev/icons/reply-all. */
export const ReplyAllIcon = /*#__PURE__*/ createLucideIcon("reply-all", registry.replyAll);

/** Renders the "rewind" icon from https://lucide.dev/icons/rewind. */
export const RewindIcon = /*#__PURE__*/ createLucideIcon("rewind", registry.rewind);

/** Renders the "ribbon" icon from https://lucide.dev/icons/ribbon. */
export const RibbonIcon = /*#__PURE__*/ createLucideIcon("ribbon", registry.ribbon);

/** Renders the "road" icon from https://lucide.dev/icons/road. */
export const RoadIcon = /*#__PURE__*/ createLucideIcon("road", registry.road);

/** Renders the "robot-arm" icon from https://lucide.dev/icons/robot-arm. */
export const RobotArmIcon = /*#__PURE__*/ createLucideIcon("robot-arm", registry.robotArm);

/** Renders the "robot-vacuum" icon from https://lucide.dev/icons/robot-vacuum. */
export const RobotVacuumIcon = /*#__PURE__*/ createLucideIcon("robot-vacuum", registry.robotVacuum);

/** Renders the "rocket" icon from https://lucide.dev/icons/rocket. */
export const RocketIcon = /*#__PURE__*/ createLucideIcon("rocket", registry.rocket);

/** Renders the "rocking-chair" icon from https://lucide.dev/icons/rocking-chair. */
export const RockingChairIcon = /*#__PURE__*/ createLucideIcon(
	"rocking-chair",
	registry.rockingChair,
);

/** Renders the "roller-coaster" icon from https://lucide.dev/icons/roller-coaster. */
export const RollerCoasterIcon = /*#__PURE__*/ createLucideIcon(
	"roller-coaster",
	registry.rollerCoaster,
);

/** Renders the "rose" icon from https://lucide.dev/icons/rose. */
export const RoseIcon = /*#__PURE__*/ createLucideIcon("rose", registry.rose);

/** Renders the "rotate-3d" icon from https://lucide.dev/icons/rotate-3d. */
export const Rotate3dIcon = /*#__PURE__*/ createLucideIcon("rotate-3d", registry.rotate3d);

/** Renders the "rotate-ccw" icon from https://lucide.dev/icons/rotate-ccw. */
export const RotateCcwIcon = /*#__PURE__*/ createLucideIcon("rotate-ccw", registry.rotateCcw);

/** Renders the "rotate-ccw-clock" icon from https://lucide.dev/icons/rotate-ccw-clock. */
export const RotateCcwClockIcon = /*#__PURE__*/ createLucideIcon(
	"rotate-ccw-clock",
	registry.rotateCcwClock,
);

/** Renders the "rotate-ccw-key" icon from https://lucide.dev/icons/rotate-ccw-key. */
export const RotateCcwKeyIcon = /*#__PURE__*/ createLucideIcon(
	"rotate-ccw-key",
	registry.rotateCcwKey,
);

/** Renders the "rotate-ccw-square" icon from https://lucide.dev/icons/rotate-ccw-square. */
export const RotateCcwSquareIcon = /*#__PURE__*/ createLucideIcon(
	"rotate-ccw-square",
	registry.rotateCcwSquare,
);

/** Renders the "rotate-cw" icon from https://lucide.dev/icons/rotate-cw. */
export const RotateCwIcon = /*#__PURE__*/ createLucideIcon("rotate-cw", registry.rotateCw);

/** Renders the "rotate-cw-clock" icon from https://lucide.dev/icons/rotate-cw-clock. */
export const RotateCwClockIcon = /*#__PURE__*/ createLucideIcon(
	"rotate-cw-clock",
	registry.rotateCwClock,
);

/** Renders the "rotate-cw-fading-clock" icon from https://lucide.dev/icons/rotate-cw-fading-clock. */
export const RotateCwFadingClockIcon = /*#__PURE__*/ createLucideIcon(
	"rotate-cw-fading-clock",
	registry.rotateCwFadingClock,
);

/** Renders the "rotate-cw-square" icon from https://lucide.dev/icons/rotate-cw-square. */
export const RotateCwSquareIcon = /*#__PURE__*/ createLucideIcon(
	"rotate-cw-square",
	registry.rotateCwSquare,
);

/** Renders the "route" icon from https://lucide.dev/icons/route. */
export const RouteIcon = /*#__PURE__*/ createLucideIcon("route", registry.route);

/** Renders the "route-off" icon from https://lucide.dev/icons/route-off. */
export const RouteOffIcon = /*#__PURE__*/ createLucideIcon("route-off", registry.routeOff);

/** Renders the "router" icon from https://lucide.dev/icons/router. */
export const RouterIcon = /*#__PURE__*/ createLucideIcon("router", registry.router);

/** Renders the "rows-2" icon from https://lucide.dev/icons/rows-2. */
export const Rows2Icon = /*#__PURE__*/ createLucideIcon("rows-2", registry.rows2);

/** Renders the "rows-3" icon from https://lucide.dev/icons/rows-3. */
export const Rows3Icon = /*#__PURE__*/ createLucideIcon("rows-3", registry.rows3);

/** Renders the "rows-4" icon from https://lucide.dev/icons/rows-4. */
export const Rows4Icon = /*#__PURE__*/ createLucideIcon("rows-4", registry.rows4);

/** Renders the "rss" icon from https://lucide.dev/icons/rss. */
export const RssIcon = /*#__PURE__*/ createLucideIcon("rss", registry.rss);

/** Renders the "rugby-ball" icon from https://lucide.dev/icons/rugby-ball. */
export const RugbyBallIcon = /*#__PURE__*/ createLucideIcon("rugby-ball", registry.rugbyBall);

/** Renders the "ruler" icon from https://lucide.dev/icons/ruler. */
export const RulerIcon = /*#__PURE__*/ createLucideIcon("ruler", registry.ruler);

/** Renders the "ruler-dimension-line" icon from https://lucide.dev/icons/ruler-dimension-line. */
export const RulerDimensionLineIcon = /*#__PURE__*/ createLucideIcon(
	"ruler-dimension-line",
	registry.rulerDimensionLine,
);

/** Renders the "russian-ruble" icon from https://lucide.dev/icons/russian-ruble. */
export const RussianRubleIcon = /*#__PURE__*/ createLucideIcon(
	"russian-ruble",
	registry.russianRuble,
);

/** Renders the "sailboat" icon from https://lucide.dev/icons/sailboat. */
export const SailboatIcon = /*#__PURE__*/ createLucideIcon("sailboat", registry.sailboat);

/** Renders the "salad" icon from https://lucide.dev/icons/salad. */
export const SaladIcon = /*#__PURE__*/ createLucideIcon("salad", registry.salad);

/** Renders the "sandwich" icon from https://lucide.dev/icons/sandwich. */
export const SandwichIcon = /*#__PURE__*/ createLucideIcon("sandwich", registry.sandwich);

/** Renders the "satellite" icon from https://lucide.dev/icons/satellite. */
export const SatelliteIcon = /*#__PURE__*/ createLucideIcon("satellite", registry.satellite);

/** Renders the "satellite-dish" icon from https://lucide.dev/icons/satellite-dish. */
export const SatelliteDishIcon = /*#__PURE__*/ createLucideIcon(
	"satellite-dish",
	registry.satelliteDish,
);

/** Renders the "saudi-riyal" icon from https://lucide.dev/icons/saudi-riyal. */
export const SaudiRiyalIcon = /*#__PURE__*/ createLucideIcon("saudi-riyal", registry.saudiRiyal);

/** Renders the "save" icon from https://lucide.dev/icons/save. */
export const SaveIcon = /*#__PURE__*/ createLucideIcon("save", registry.save);

/** Renders the "save-all" icon from https://lucide.dev/icons/save-all. */
export const SaveAllIcon = /*#__PURE__*/ createLucideIcon("save-all", registry.saveAll);

/** Renders the "save-check" icon from https://lucide.dev/icons/save-check. */
export const SaveCheckIcon = /*#__PURE__*/ createLucideIcon("save-check", registry.saveCheck);

/** Renders the "save-off" icon from https://lucide.dev/icons/save-off. */
export const SaveOffIcon = /*#__PURE__*/ createLucideIcon("save-off", registry.saveOff);

/** Renders the "save-pen" icon from https://lucide.dev/icons/save-pen. */
export const SavePenIcon = /*#__PURE__*/ createLucideIcon("save-pen", registry.savePen);

/** Renders the "save-plus" icon from https://lucide.dev/icons/save-plus. */
export const SavePlusIcon = /*#__PURE__*/ createLucideIcon("save-plus", registry.savePlus);

/** Renders the "scale" icon from https://lucide.dev/icons/scale. */
export const ScaleIcon = /*#__PURE__*/ createLucideIcon("scale", registry.scale);

/** Renders the "scale-3d" icon from https://lucide.dev/icons/scale-3d. */
export const Scale3dIcon = /*#__PURE__*/ createLucideIcon("scale-3d", registry.scale3d);

/** Renders the "scaling" icon from https://lucide.dev/icons/scaling. */
export const ScalingIcon = /*#__PURE__*/ createLucideIcon("scaling", registry.scaling);

/** Renders the "scan" icon from https://lucide.dev/icons/scan. */
export const ScanIcon = /*#__PURE__*/ createLucideIcon("scan", registry.scan);

/** Renders the "scan-barcode" icon from https://lucide.dev/icons/scan-barcode. */
export const ScanBarcodeIcon = /*#__PURE__*/ createLucideIcon("scan-barcode", registry.scanBarcode);

/** Renders the "scan-box" icon from https://lucide.dev/icons/scan-box. */
export const ScanBoxIcon = /*#__PURE__*/ createLucideIcon("scan-box", registry.scanBox);

/** Renders the "scan-eye" icon from https://lucide.dev/icons/scan-eye. */
export const ScanEyeIcon = /*#__PURE__*/ createLucideIcon("scan-eye", registry.scanEye);

/** Renders the "scan-face" icon from https://lucide.dev/icons/scan-face. */
export const ScanFaceIcon = /*#__PURE__*/ createLucideIcon("scan-face", registry.scanFace);

/** Renders the "scan-heart" icon from https://lucide.dev/icons/scan-heart. */
export const ScanHeartIcon = /*#__PURE__*/ createLucideIcon("scan-heart", registry.scanHeart);

/** Renders the "scan-line" icon from https://lucide.dev/icons/scan-line. */
export const ScanLineIcon = /*#__PURE__*/ createLucideIcon("scan-line", registry.scanLine);

/** Renders the "scan-qr-code" icon from https://lucide.dev/icons/scan-qr-code. */
export const ScanQrCodeIcon = /*#__PURE__*/ createLucideIcon("scan-qr-code", registry.scanQrCode);

/** Renders the "scan-search" icon from https://lucide.dev/icons/scan-search. */
export const ScanSearchIcon = /*#__PURE__*/ createLucideIcon("scan-search", registry.scanSearch);

/** Renders the "scan-square" icon from https://lucide.dev/icons/scan-square. */
export const ScanSquareIcon = /*#__PURE__*/ createLucideIcon("scan-square", registry.scanSquare);

/** Renders the "scan-text" icon from https://lucide.dev/icons/scan-text. */
export const ScanTextIcon = /*#__PURE__*/ createLucideIcon("scan-text", registry.scanText);

/** Renders the "school" icon from https://lucide.dev/icons/school. */
export const SchoolIcon = /*#__PURE__*/ createLucideIcon("school", registry.school);

/** Renders the "scissors" icon from https://lucide.dev/icons/scissors. */
export const ScissorsIcon = /*#__PURE__*/ createLucideIcon("scissors", registry.scissors);

/** Renders the "scissors-line-dashed" icon from https://lucide.dev/icons/scissors-line-dashed. */
export const ScissorsLineDashedIcon = /*#__PURE__*/ createLucideIcon(
	"scissors-line-dashed",
	registry.scissorsLineDashed,
);

/** Renders the "scooter" icon from https://lucide.dev/icons/scooter. */
export const ScooterIcon = /*#__PURE__*/ createLucideIcon("scooter", registry.scooter);

/** Renders the "scratch-blocks" icon from https://lucide.dev/icons/scratch-blocks. */
export const ScratchBlocksIcon = /*#__PURE__*/ createLucideIcon(
	"scratch-blocks",
	registry.scratchBlocks,
);

/** Renders the "screen-share" icon from https://lucide.dev/icons/screen-share. */
export const ScreenShareIcon = /*#__PURE__*/ createLucideIcon("screen-share", registry.screenShare);

/** Renders the "screen-share-off" icon from https://lucide.dev/icons/screen-share-off. */
export const ScreenShareOffIcon = /*#__PURE__*/ createLucideIcon(
	"screen-share-off",
	registry.screenShareOff,
);

/** Renders the "screw" icon from https://lucide.dev/icons/screw. */
export const ScrewIcon = /*#__PURE__*/ createLucideIcon("screw", registry.screw);

/** Renders the "scroll" icon from https://lucide.dev/icons/scroll. */
export const ScrollIcon = /*#__PURE__*/ createLucideIcon("scroll", registry.scroll);

/** Renders the "scroll-text" icon from https://lucide.dev/icons/scroll-text. */
export const ScrollTextIcon = /*#__PURE__*/ createLucideIcon("scroll-text", registry.scrollText);

/** Renders the "search" icon from https://lucide.dev/icons/search. */
export const SearchIcon = /*#__PURE__*/ createLucideIcon("search", registry.search);

/** Renders the "search-alert" icon from https://lucide.dev/icons/search-alert. */
export const SearchAlertIcon = /*#__PURE__*/ createLucideIcon("search-alert", registry.searchAlert);

/** Renders the "search-check" icon from https://lucide.dev/icons/search-check. */
export const SearchCheckIcon = /*#__PURE__*/ createLucideIcon("search-check", registry.searchCheck);

/** Renders the "search-code" icon from https://lucide.dev/icons/search-code. */
export const SearchCodeIcon = /*#__PURE__*/ createLucideIcon("search-code", registry.searchCode);

/** Renders the "search-slash" icon from https://lucide.dev/icons/search-slash. */
export const SearchSlashIcon = /*#__PURE__*/ createLucideIcon("search-slash", registry.searchSlash);

/** Renders the "search-x" icon from https://lucide.dev/icons/search-x. */
export const SearchXIcon = /*#__PURE__*/ createLucideIcon("search-x", registry.searchX);

/** Renders the "section" icon from https://lucide.dev/icons/section. */
export const SectionIcon = /*#__PURE__*/ createLucideIcon("section", registry.section);

/** Renders the "send" icon from https://lucide.dev/icons/send. */
export const SendIcon = /*#__PURE__*/ createLucideIcon("send", registry.send);

/** Renders the "send-horizontal" icon from https://lucide.dev/icons/send-horizontal. */
export const SendHorizontalIcon = /*#__PURE__*/ createLucideIcon(
	"send-horizontal",
	registry.sendHorizontal,
);

/** Renders the "send-to-back" icon from https://lucide.dev/icons/send-to-back. */
export const SendToBackIcon = /*#__PURE__*/ createLucideIcon("send-to-back", registry.sendToBack);

/** Renders the "separator-horizontal" icon from https://lucide.dev/icons/separator-horizontal. */
export const SeparatorHorizontalIcon = /*#__PURE__*/ createLucideIcon(
	"separator-horizontal",
	registry.separatorHorizontal,
);

/** Renders the "separator-vertical" icon from https://lucide.dev/icons/separator-vertical. */
export const SeparatorVerticalIcon = /*#__PURE__*/ createLucideIcon(
	"separator-vertical",
	registry.separatorVertical,
);

/** Renders the "server" icon from https://lucide.dev/icons/server. */
export const ServerIcon = /*#__PURE__*/ createLucideIcon("server", registry.server);

/** Renders the "server-cog" icon from https://lucide.dev/icons/server-cog. */
export const ServerCogIcon = /*#__PURE__*/ createLucideIcon("server-cog", registry.serverCog);

/** Renders the "server-crash" icon from https://lucide.dev/icons/server-crash. */
export const ServerCrashIcon = /*#__PURE__*/ createLucideIcon("server-crash", registry.serverCrash);

/** Renders the "server-nas" icon from https://lucide.dev/icons/server-nas. */
export const ServerNasIcon = /*#__PURE__*/ createLucideIcon("server-nas", registry.serverNas);

/** Renders the "server-off" icon from https://lucide.dev/icons/server-off. */
export const ServerOffIcon = /*#__PURE__*/ createLucideIcon("server-off", registry.serverOff);

/** Renders the "server-plus" icon from https://lucide.dev/icons/server-plus. */
export const ServerPlusIcon = /*#__PURE__*/ createLucideIcon("server-plus", registry.serverPlus);

/** Renders the "settings" icon from https://lucide.dev/icons/settings. */
export const SettingsIcon = /*#__PURE__*/ createLucideIcon("settings", registry.settings);

/** Renders the "settings-2" icon from https://lucide.dev/icons/settings-2. */
export const Settings2Icon = /*#__PURE__*/ createLucideIcon("settings-2", registry.settings2);

/** Renders the "shapes" icon from https://lucide.dev/icons/shapes. */
export const ShapesIcon = /*#__PURE__*/ createLucideIcon("shapes", registry.shapes);

/** Renders the "share" icon from https://lucide.dev/icons/share. */
export const ShareIcon = /*#__PURE__*/ createLucideIcon("share", registry.share);

/** Renders the "share-2" icon from https://lucide.dev/icons/share-2. */
export const Share2Icon = /*#__PURE__*/ createLucideIcon("share-2", registry.share2);

/** Renders the "sheet" icon from https://lucide.dev/icons/sheet. */
export const SheetIcon = /*#__PURE__*/ createLucideIcon("sheet", registry.sheet);

/** Renders the "shell" icon from https://lucide.dev/icons/shell. */
export const ShellIcon = /*#__PURE__*/ createLucideIcon("shell", registry.shell);

/** Renders the "shelving-unit" icon from https://lucide.dev/icons/shelving-unit. */
export const ShelvingUnitIcon = /*#__PURE__*/ createLucideIcon(
	"shelving-unit",
	registry.shelvingUnit,
);

/** Renders the "shield" icon from https://lucide.dev/icons/shield. */
export const ShieldIcon = /*#__PURE__*/ createLucideIcon("shield", registry.shield);

/** Renders the "shield-alert" icon from https://lucide.dev/icons/shield-alert. */
export const ShieldAlertIcon = /*#__PURE__*/ createLucideIcon("shield-alert", registry.shieldAlert);

/** Renders the "shield-ban" icon from https://lucide.dev/icons/shield-ban. */
export const ShieldBanIcon = /*#__PURE__*/ createLucideIcon("shield-ban", registry.shieldBan);

/** Renders the "shield-check" icon from https://lucide.dev/icons/shield-check. */
export const ShieldCheckIcon = /*#__PURE__*/ createLucideIcon("shield-check", registry.shieldCheck);

/** Renders the "shield-cog" icon from https://lucide.dev/icons/shield-cog. */
export const ShieldCogIcon = /*#__PURE__*/ createLucideIcon("shield-cog", registry.shieldCog);

/** Renders the "shield-cog-corner" icon from https://lucide.dev/icons/shield-cog-corner. */
export const ShieldCogCornerIcon = /*#__PURE__*/ createLucideIcon(
	"shield-cog-corner",
	registry.shieldCogCorner,
);

/** Renders the "shield-ellipsis" icon from https://lucide.dev/icons/shield-ellipsis. */
export const ShieldEllipsisIcon = /*#__PURE__*/ createLucideIcon(
	"shield-ellipsis",
	registry.shieldEllipsis,
);

/** Renders the "shield-half" icon from https://lucide.dev/icons/shield-half. */
export const ShieldHalfIcon = /*#__PURE__*/ createLucideIcon("shield-half", registry.shieldHalf);

/** Renders the "shield-house" icon from https://lucide.dev/icons/shield-house. */
export const ShieldHouseIcon = /*#__PURE__*/ createLucideIcon("shield-house", registry.shieldHouse);

/** Renders the "shield-keyhole" icon from https://lucide.dev/icons/shield-keyhole. */
export const ShieldKeyholeIcon = /*#__PURE__*/ createLucideIcon(
	"shield-keyhole",
	registry.shieldKeyhole,
);

/** Renders the "shield-lock" icon from https://lucide.dev/icons/shield-lock. */
export const ShieldLockIcon = /*#__PURE__*/ createLucideIcon("shield-lock", registry.shieldLock);

/** Renders the "shield-minus" icon from https://lucide.dev/icons/shield-minus. */
export const ShieldMinusIcon = /*#__PURE__*/ createLucideIcon("shield-minus", registry.shieldMinus);

/** Renders the "shield-off" icon from https://lucide.dev/icons/shield-off. */
export const ShieldOffIcon = /*#__PURE__*/ createLucideIcon("shield-off", registry.shieldOff);

/** Renders the "shield-plus" icon from https://lucide.dev/icons/shield-plus. */
export const ShieldPlusIcon = /*#__PURE__*/ createLucideIcon("shield-plus", registry.shieldPlus);

/** Renders the "shield-question-mark" icon from https://lucide.dev/icons/shield-question-mark. */
export const ShieldQuestionMarkIcon = /*#__PURE__*/ createLucideIcon(
	"shield-question-mark",
	registry.shieldQuestionMark,
);

/** Renders the "shield-user" icon from https://lucide.dev/icons/shield-user. */
export const ShieldUserIcon = /*#__PURE__*/ createLucideIcon("shield-user", registry.shieldUser);

/** Renders the "shield-x" icon from https://lucide.dev/icons/shield-x. */
export const ShieldXIcon = /*#__PURE__*/ createLucideIcon("shield-x", registry.shieldX);

/** Renders the "ship" icon from https://lucide.dev/icons/ship. */
export const ShipIcon = /*#__PURE__*/ createLucideIcon("ship", registry.ship);

/** Renders the "ship-cargo" icon from https://lucide.dev/icons/ship-cargo. */
export const ShipCargoIcon = /*#__PURE__*/ createLucideIcon("ship-cargo", registry.shipCargo);

/** Renders the "ship-wheel" icon from https://lucide.dev/icons/ship-wheel. */
export const ShipWheelIcon = /*#__PURE__*/ createLucideIcon("ship-wheel", registry.shipWheel);

/** Renders the "shirt" icon from https://lucide.dev/icons/shirt. */
export const ShirtIcon = /*#__PURE__*/ createLucideIcon("shirt", registry.shirt);

/** Renders the "shopping-bag" icon from https://lucide.dev/icons/shopping-bag. */
export const ShoppingBagIcon = /*#__PURE__*/ createLucideIcon("shopping-bag", registry.shoppingBag);

/** Renders the "shopping-basket" icon from https://lucide.dev/icons/shopping-basket. */
export const ShoppingBasketIcon = /*#__PURE__*/ createLucideIcon(
	"shopping-basket",
	registry.shoppingBasket,
);

/** Renders the "shopping-cart" icon from https://lucide.dev/icons/shopping-cart. */
export const ShoppingCartIcon = /*#__PURE__*/ createLucideIcon(
	"shopping-cart",
	registry.shoppingCart,
);

/** Renders the "shopping-cart-minus" icon from https://lucide.dev/icons/shopping-cart-minus. */
export const ShoppingCartMinusIcon = /*#__PURE__*/ createLucideIcon(
	"shopping-cart-minus",
	registry.shoppingCartMinus,
);

/** Renders the "shopping-cart-plus" icon from https://lucide.dev/icons/shopping-cart-plus. */
export const ShoppingCartPlusIcon = /*#__PURE__*/ createLucideIcon(
	"shopping-cart-plus",
	registry.shoppingCartPlus,
);

/** Renders the "shovel" icon from https://lucide.dev/icons/shovel. */
export const ShovelIcon = /*#__PURE__*/ createLucideIcon("shovel", registry.shovel);

/** Renders the "shower-head" icon from https://lucide.dev/icons/shower-head. */
export const ShowerHeadIcon = /*#__PURE__*/ createLucideIcon("shower-head", registry.showerHead);

/** Renders the "shredder" icon from https://lucide.dev/icons/shredder. */
export const ShredderIcon = /*#__PURE__*/ createLucideIcon("shredder", registry.shredder);

/** Renders the "shrimp" icon from https://lucide.dev/icons/shrimp. */
export const ShrimpIcon = /*#__PURE__*/ createLucideIcon("shrimp", registry.shrimp);

/** Renders the "shrimp-off" icon from https://lucide.dev/icons/shrimp-off. */
export const ShrimpOffIcon = /*#__PURE__*/ createLucideIcon("shrimp-off", registry.shrimpOff);

/** Renders the "shrink" icon from https://lucide.dev/icons/shrink. */
export const ShrinkIcon = /*#__PURE__*/ createLucideIcon("shrink", registry.shrink);

/** Renders the "shrub" icon from https://lucide.dev/icons/shrub. */
export const ShrubIcon = /*#__PURE__*/ createLucideIcon("shrub", registry.shrub);

/** Renders the "shuffle" icon from https://lucide.dev/icons/shuffle. */
export const ShuffleIcon = /*#__PURE__*/ createLucideIcon("shuffle", registry.shuffle);

/** Renders the "sigma" icon from https://lucide.dev/icons/sigma. */
export const SigmaIcon = /*#__PURE__*/ createLucideIcon("sigma", registry.sigma);

/** Renders the "signal" icon from https://lucide.dev/icons/signal. */
export const SignalIcon = /*#__PURE__*/ createLucideIcon("signal", registry.signal);

/** Renders the "signal-high" icon from https://lucide.dev/icons/signal-high. */
export const SignalHighIcon = /*#__PURE__*/ createLucideIcon("signal-high", registry.signalHigh);

/** Renders the "signal-low" icon from https://lucide.dev/icons/signal-low. */
export const SignalLowIcon = /*#__PURE__*/ createLucideIcon("signal-low", registry.signalLow);

/** Renders the "signal-medium" icon from https://lucide.dev/icons/signal-medium. */
export const SignalMediumIcon = /*#__PURE__*/ createLucideIcon(
	"signal-medium",
	registry.signalMedium,
);

/** Renders the "signal-zero" icon from https://lucide.dev/icons/signal-zero. */
export const SignalZeroIcon = /*#__PURE__*/ createLucideIcon("signal-zero", registry.signalZero);

/** Renders the "signature" icon from https://lucide.dev/icons/signature. */
export const SignatureIcon = /*#__PURE__*/ createLucideIcon("signature", registry.signature);

/** Renders the "signpost" icon from https://lucide.dev/icons/signpost. */
export const SignpostIcon = /*#__PURE__*/ createLucideIcon("signpost", registry.signpost);

/** Renders the "signpost-big" icon from https://lucide.dev/icons/signpost-big. */
export const SignpostBigIcon = /*#__PURE__*/ createLucideIcon("signpost-big", registry.signpostBig);

/** Renders the "siren" icon from https://lucide.dev/icons/siren. */
export const SirenIcon = /*#__PURE__*/ createLucideIcon("siren", registry.siren);

/** Renders the "sixteenth-notes" icon from https://lucide.dev/icons/sixteenth-notes. */
export const SixteenthNotesIcon = /*#__PURE__*/ createLucideIcon(
	"sixteenth-notes",
	registry.sixteenthNotes,
);

/** Renders the "skip-back" icon from https://lucide.dev/icons/skip-back. */
export const SkipBackIcon = /*#__PURE__*/ createLucideIcon("skip-back", registry.skipBack);

/** Renders the "skip-forward" icon from https://lucide.dev/icons/skip-forward. */
export const SkipForwardIcon = /*#__PURE__*/ createLucideIcon("skip-forward", registry.skipForward);

/** Renders the "skull" icon from https://lucide.dev/icons/skull. */
export const SkullIcon = /*#__PURE__*/ createLucideIcon("skull", registry.skull);

/** Renders the "slash" icon from https://lucide.dev/icons/slash. */
export const SlashIcon = /*#__PURE__*/ createLucideIcon("slash", registry.slash);

/** Renders the "slice" icon from https://lucide.dev/icons/slice. */
export const SliceIcon = /*#__PURE__*/ createLucideIcon("slice", registry.slice);

/** Renders the "sliders-horizontal" icon from https://lucide.dev/icons/sliders-horizontal. */
export const SlidersHorizontalIcon = /*#__PURE__*/ createLucideIcon(
	"sliders-horizontal",
	registry.slidersHorizontal,
);

/** Renders the "sliders-vertical" icon from https://lucide.dev/icons/sliders-vertical. */
export const SlidersVerticalIcon = /*#__PURE__*/ createLucideIcon(
	"sliders-vertical",
	registry.slidersVertical,
);

/** Renders the "smartphone" icon from https://lucide.dev/icons/smartphone. */
export const SmartphoneIcon = /*#__PURE__*/ createLucideIcon("smartphone", registry.smartphone);

/** Renders the "smartphone-charging" icon from https://lucide.dev/icons/smartphone-charging. */
export const SmartphoneChargingIcon = /*#__PURE__*/ createLucideIcon(
	"smartphone-charging",
	registry.smartphoneCharging,
);

/** Renders the "smartphone-nfc" icon from https://lucide.dev/icons/smartphone-nfc. */
export const SmartphoneNfcIcon = /*#__PURE__*/ createLucideIcon(
	"smartphone-nfc",
	registry.smartphoneNfc,
);

/** Renders the "smartphone-user-round" icon from https://lucide.dev/icons/smartphone-user-round. */
export const SmartphoneUserRoundIcon = /*#__PURE__*/ createLucideIcon(
	"smartphone-user-round",
	registry.smartphoneUserRound,
);

/** Renders the "snail" icon from https://lucide.dev/icons/snail. */
export const SnailIcon = /*#__PURE__*/ createLucideIcon("snail", registry.snail);

/** Renders the "snowflake" icon from https://lucide.dev/icons/snowflake. */
export const SnowflakeIcon = /*#__PURE__*/ createLucideIcon("snowflake", registry.snowflake);

/** Renders the "soap-dispenser-droplet" icon from https://lucide.dev/icons/soap-dispenser-droplet. */
export const SoapDispenserDropletIcon = /*#__PURE__*/ createLucideIcon(
	"soap-dispenser-droplet",
	registry.soapDispenserDroplet,
);

/** Renders the "sofa" icon from https://lucide.dev/icons/sofa. */
export const SofaIcon = /*#__PURE__*/ createLucideIcon("sofa", registry.sofa);

/** Renders the "solar-panel" icon from https://lucide.dev/icons/solar-panel. */
export const SolarPanelIcon = /*#__PURE__*/ createLucideIcon("solar-panel", registry.solarPanel);

/** Renders the "soup" icon from https://lucide.dev/icons/soup. */
export const SoupIcon = /*#__PURE__*/ createLucideIcon("soup", registry.soup);

/** Renders the "space" icon from https://lucide.dev/icons/space. */
export const SpaceIcon = /*#__PURE__*/ createLucideIcon("space", registry.space);

/** Renders the "spade" icon from https://lucide.dev/icons/spade. */
export const SpadeIcon = /*#__PURE__*/ createLucideIcon("spade", registry.spade);

/** Renders the "sparkle" icon from https://lucide.dev/icons/sparkle. */
export const SparkleIcon = /*#__PURE__*/ createLucideIcon("sparkle", registry.sparkle);

/** Renders the "sparkles" icon from https://lucide.dev/icons/sparkles. */
export const SparklesIcon = /*#__PURE__*/ createLucideIcon("sparkles", registry.sparkles);

/** Renders the "speaker" icon from https://lucide.dev/icons/speaker. */
export const SpeakerIcon = /*#__PURE__*/ createLucideIcon("speaker", registry.speaker);

/** Renders the "speech" icon from https://lucide.dev/icons/speech. */
export const SpeechIcon = /*#__PURE__*/ createLucideIcon("speech", registry.speech);

/** Renders the "spell-check" icon from https://lucide.dev/icons/spell-check. */
export const SpellCheckIcon = /*#__PURE__*/ createLucideIcon("spell-check", registry.spellCheck);

/** Renders the "spell-check-2" icon from https://lucide.dev/icons/spell-check-2. */
export const SpellCheck2Icon = /*#__PURE__*/ createLucideIcon(
	"spell-check-2",
	registry.spellCheck2,
);

/** Renders the "spline" icon from https://lucide.dev/icons/spline. */
export const SplineIcon = /*#__PURE__*/ createLucideIcon("spline", registry.spline);

/** Renders the "spline-pointer" icon from https://lucide.dev/icons/spline-pointer. */
export const SplinePointerIcon = /*#__PURE__*/ createLucideIcon(
	"spline-pointer",
	registry.splinePointer,
);

/** Renders the "split" icon from https://lucide.dev/icons/split. */
export const SplitIcon = /*#__PURE__*/ createLucideIcon("split", registry.split);

/** Renders the "spool" icon from https://lucide.dev/icons/spool. */
export const SpoolIcon = /*#__PURE__*/ createLucideIcon("spool", registry.spool);

/** Renders the "sport-shoe" icon from https://lucide.dev/icons/sport-shoe. */
export const SportShoeIcon = /*#__PURE__*/ createLucideIcon("sport-shoe", registry.sportShoe);

/** Renders the "spotlight" icon from https://lucide.dev/icons/spotlight. */
export const SpotlightIcon = /*#__PURE__*/ createLucideIcon("spotlight", registry.spotlight);

/** Renders the "spray-can" icon from https://lucide.dev/icons/spray-can. */
export const SprayCanIcon = /*#__PURE__*/ createLucideIcon("spray-can", registry.sprayCan);

/** Renders the "sprout" icon from https://lucide.dev/icons/sprout. */
export const SproutIcon = /*#__PURE__*/ createLucideIcon("sprout", registry.sprout);

/** Renders the "square" icon from https://lucide.dev/icons/square. */
export const SquareIcon = /*#__PURE__*/ createLucideIcon("square", registry.square);

/** Renders the "square-activity" icon from https://lucide.dev/icons/square-activity. */
export const SquareActivityIcon = /*#__PURE__*/ createLucideIcon(
	"square-activity",
	registry.squareActivity,
);

/** Renders the "square-arrow-down" icon from https://lucide.dev/icons/square-arrow-down. */
export const SquareArrowDownIcon = /*#__PURE__*/ createLucideIcon(
	"square-arrow-down",
	registry.squareArrowDown,
);

/** Renders the "square-arrow-down-left" icon from https://lucide.dev/icons/square-arrow-down-left. */
export const SquareArrowDownLeftIcon = /*#__PURE__*/ createLucideIcon(
	"square-arrow-down-left",
	registry.squareArrowDownLeft,
);

/** Renders the "square-arrow-down-right" icon from https://lucide.dev/icons/square-arrow-down-right. */
export const SquareArrowDownRightIcon = /*#__PURE__*/ createLucideIcon(
	"square-arrow-down-right",
	registry.squareArrowDownRight,
);

/** Renders the "square-arrow-left" icon from https://lucide.dev/icons/square-arrow-left. */
export const SquareArrowLeftIcon = /*#__PURE__*/ createLucideIcon(
	"square-arrow-left",
	registry.squareArrowLeft,
);

/** Renders the "square-arrow-out-down-left" icon from https://lucide.dev/icons/square-arrow-out-down-left. */
export const SquareArrowOutDownLeftIcon = /*#__PURE__*/ createLucideIcon(
	"square-arrow-out-down-left",
	registry.squareArrowOutDownLeft,
);

/** Renders the "square-arrow-out-down-right" icon from https://lucide.dev/icons/square-arrow-out-down-right. */
export const SquareArrowOutDownRightIcon = /*#__PURE__*/ createLucideIcon(
	"square-arrow-out-down-right",
	registry.squareArrowOutDownRight,
);

/** Renders the "square-arrow-out-up-left" icon from https://lucide.dev/icons/square-arrow-out-up-left. */
export const SquareArrowOutUpLeftIcon = /*#__PURE__*/ createLucideIcon(
	"square-arrow-out-up-left",
	registry.squareArrowOutUpLeft,
);

/** Renders the "square-arrow-out-up-right" icon from https://lucide.dev/icons/square-arrow-out-up-right. */
export const SquareArrowOutUpRightIcon = /*#__PURE__*/ createLucideIcon(
	"square-arrow-out-up-right",
	registry.squareArrowOutUpRight,
);

/** Renders the "square-arrow-right" icon from https://lucide.dev/icons/square-arrow-right. */
export const SquareArrowRightIcon = /*#__PURE__*/ createLucideIcon(
	"square-arrow-right",
	registry.squareArrowRight,
);

/** Renders the "square-arrow-right-enter" icon from https://lucide.dev/icons/square-arrow-right-enter. */
export const SquareArrowRightEnterIcon = /*#__PURE__*/ createLucideIcon(
	"square-arrow-right-enter",
	registry.squareArrowRightEnter,
);

/** Renders the "square-arrow-right-exit" icon from https://lucide.dev/icons/square-arrow-right-exit. */
export const SquareArrowRightExitIcon = /*#__PURE__*/ createLucideIcon(
	"square-arrow-right-exit",
	registry.squareArrowRightExit,
);

/** Renders the "square-arrow-up" icon from https://lucide.dev/icons/square-arrow-up. */
export const SquareArrowUpIcon = /*#__PURE__*/ createLucideIcon(
	"square-arrow-up",
	registry.squareArrowUp,
);

/** Renders the "square-arrow-up-left" icon from https://lucide.dev/icons/square-arrow-up-left. */
export const SquareArrowUpLeftIcon = /*#__PURE__*/ createLucideIcon(
	"square-arrow-up-left",
	registry.squareArrowUpLeft,
);

/** Renders the "square-arrow-up-right" icon from https://lucide.dev/icons/square-arrow-up-right. */
export const SquareArrowUpRightIcon = /*#__PURE__*/ createLucideIcon(
	"square-arrow-up-right",
	registry.squareArrowUpRight,
);

/** Renders the "square-asterisk" icon from https://lucide.dev/icons/square-asterisk. */
export const SquareAsteriskIcon = /*#__PURE__*/ createLucideIcon(
	"square-asterisk",
	registry.squareAsterisk,
);

/** Renders the "square-bookmark" icon from https://lucide.dev/icons/square-bookmark. */
export const SquareBookmarkIcon = /*#__PURE__*/ createLucideIcon(
	"square-bookmark",
	registry.squareBookmark,
);

/** Renders the "square-bottom-dashed-scissors" icon from https://lucide.dev/icons/square-bottom-dashed-scissors. */
export const SquareBottomDashedScissorsIcon = /*#__PURE__*/ createLucideIcon(
	"square-bottom-dashed-scissors",
	registry.squareBottomDashedScissors,
);

/** Renders the "square-centerline-dashed-horizontal" icon from https://lucide.dev/icons/square-centerline-dashed-horizontal. */
export const SquareCenterlineDashedHorizontalIcon = /*#__PURE__*/ createLucideIcon(
	"square-centerline-dashed-horizontal",
	registry.squareCenterlineDashedHorizontal,
);

/** Renders the "square-centerline-dashed-vertical" icon from https://lucide.dev/icons/square-centerline-dashed-vertical. */
export const SquareCenterlineDashedVerticalIcon = /*#__PURE__*/ createLucideIcon(
	"square-centerline-dashed-vertical",
	registry.squareCenterlineDashedVertical,
);

/** Renders the "square-chart-gantt" icon from https://lucide.dev/icons/square-chart-gantt. */
export const SquareChartGanttIcon = /*#__PURE__*/ createLucideIcon(
	"square-chart-gantt",
	registry.squareChartGantt,
);

/** Renders the "square-check" icon from https://lucide.dev/icons/square-check. */
export const SquareCheckIcon = /*#__PURE__*/ createLucideIcon("square-check", registry.squareCheck);

/** Renders the "square-check-big" icon from https://lucide.dev/icons/square-check-big. */
export const SquareCheckBigIcon = /*#__PURE__*/ createLucideIcon(
	"square-check-big",
	registry.squareCheckBig,
);

/** Renders the "square-chevron-down" icon from https://lucide.dev/icons/square-chevron-down. */
export const SquareChevronDownIcon = /*#__PURE__*/ createLucideIcon(
	"square-chevron-down",
	registry.squareChevronDown,
);

/** Renders the "square-chevron-left" icon from https://lucide.dev/icons/square-chevron-left. */
export const SquareChevronLeftIcon = /*#__PURE__*/ createLucideIcon(
	"square-chevron-left",
	registry.squareChevronLeft,
);

/** Renders the "square-chevron-right" icon from https://lucide.dev/icons/square-chevron-right. */
export const SquareChevronRightIcon = /*#__PURE__*/ createLucideIcon(
	"square-chevron-right",
	registry.squareChevronRight,
);

/** Renders the "square-chevron-up" icon from https://lucide.dev/icons/square-chevron-up. */
export const SquareChevronUpIcon = /*#__PURE__*/ createLucideIcon(
	"square-chevron-up",
	registry.squareChevronUp,
);

/** Renders the "square-code" icon from https://lucide.dev/icons/square-code. */
export const SquareCodeIcon = /*#__PURE__*/ createLucideIcon("square-code", registry.squareCode);

/** Renders the "square-dashed" icon from https://lucide.dev/icons/square-dashed. */
export const SquareDashedIcon = /*#__PURE__*/ createLucideIcon(
	"square-dashed",
	registry.squareDashed,
);

/** Renders the "square-dashed-bottom" icon from https://lucide.dev/icons/square-dashed-bottom. */
export const SquareDashedBottomIcon = /*#__PURE__*/ createLucideIcon(
	"square-dashed-bottom",
	registry.squareDashedBottom,
);

/** Renders the "square-dashed-bottom-code" icon from https://lucide.dev/icons/square-dashed-bottom-code. */
export const SquareDashedBottomCodeIcon = /*#__PURE__*/ createLucideIcon(
	"square-dashed-bottom-code",
	registry.squareDashedBottomCode,
);

/** Renders the "square-dashed-kanban" icon from https://lucide.dev/icons/square-dashed-kanban. */
export const SquareDashedKanbanIcon = /*#__PURE__*/ createLucideIcon(
	"square-dashed-kanban",
	registry.squareDashedKanban,
);

/** Renders the "square-dashed-mouse-pointer" icon from https://lucide.dev/icons/square-dashed-mouse-pointer. */
export const SquareDashedMousePointerIcon = /*#__PURE__*/ createLucideIcon(
	"square-dashed-mouse-pointer",
	registry.squareDashedMousePointer,
);

/** Renders the "square-dashed-plus" icon from https://lucide.dev/icons/square-dashed-plus. */
export const SquareDashedPlusIcon = /*#__PURE__*/ createLucideIcon(
	"square-dashed-plus",
	registry.squareDashedPlus,
);

/** Renders the "square-dashed-text" icon from https://lucide.dev/icons/square-dashed-text. */
export const SquareDashedTextIcon = /*#__PURE__*/ createLucideIcon(
	"square-dashed-text",
	registry.squareDashedText,
);

/** Renders the "square-dashed-top-solid" icon from https://lucide.dev/icons/square-dashed-top-solid. */
export const SquareDashedTopSolidIcon = /*#__PURE__*/ createLucideIcon(
	"square-dashed-top-solid",
	registry.squareDashedTopSolid,
);

/** Renders the "square-dashed-x" icon from https://lucide.dev/icons/square-dashed-x. */
export const SquareDashedXIcon = /*#__PURE__*/ createLucideIcon(
	"square-dashed-x",
	registry.squareDashedX,
);

/** Renders the "square-dashed-x-corner" icon from https://lucide.dev/icons/square-dashed-x-corner. */
export const SquareDashedXCornerIcon = /*#__PURE__*/ createLucideIcon(
	"square-dashed-x-corner",
	registry.squareDashedXCorner,
);

/** Renders the "square-dimensions" icon from https://lucide.dev/icons/square-dimensions. */
export const SquareDimensionsIcon = /*#__PURE__*/ createLucideIcon(
	"square-dimensions",
	registry.squareDimensions,
);

/** Renders the "square-divide" icon from https://lucide.dev/icons/square-divide. */
export const SquareDivideIcon = /*#__PURE__*/ createLucideIcon(
	"square-divide",
	registry.squareDivide,
);

/** Renders the "square-dot" icon from https://lucide.dev/icons/square-dot. */
export const SquareDotIcon = /*#__PURE__*/ createLucideIcon("square-dot", registry.squareDot);

/** Renders the "square-equal" icon from https://lucide.dev/icons/square-equal. */
export const SquareEqualIcon = /*#__PURE__*/ createLucideIcon("square-equal", registry.squareEqual);

/** Renders the "square-exclamation-point" icon from https://lucide.dev/icons/square-exclamation-point. */
export const SquareExclamationPointIcon = /*#__PURE__*/ createLucideIcon(
	"square-exclamation-point",
	registry.squareExclamationPoint,
);

/** Renders the "square-function" icon from https://lucide.dev/icons/square-function. */
export const SquareFunctionIcon = /*#__PURE__*/ createLucideIcon(
	"square-function",
	registry.squareFunction,
);

/** Renders the "square-kanban" icon from https://lucide.dev/icons/square-kanban. */
export const SquareKanbanIcon = /*#__PURE__*/ createLucideIcon(
	"square-kanban",
	registry.squareKanban,
);

/** Renders the "square-library" icon from https://lucide.dev/icons/square-library. */
export const SquareLibraryIcon = /*#__PURE__*/ createLucideIcon(
	"square-library",
	registry.squareLibrary,
);

/** Renders the "square-m" icon from https://lucide.dev/icons/square-m. */
export const SquareMIcon = /*#__PURE__*/ createLucideIcon("square-m", registry.squareM);

/** Renders the "square-menu" icon from https://lucide.dev/icons/square-menu. */
export const SquareMenuIcon = /*#__PURE__*/ createLucideIcon("square-menu", registry.squareMenu);

/** Renders the "square-minus" icon from https://lucide.dev/icons/square-minus. */
export const SquareMinusIcon = /*#__PURE__*/ createLucideIcon("square-minus", registry.squareMinus);

/** Renders the "square-mouse-pointer" icon from https://lucide.dev/icons/square-mouse-pointer. */
export const SquareMousePointerIcon = /*#__PURE__*/ createLucideIcon(
	"square-mouse-pointer",
	registry.squareMousePointer,
);

/** Renders the "square-off" icon from https://lucide.dev/icons/square-off. */
export const SquareOffIcon = /*#__PURE__*/ createLucideIcon("square-off", registry.squareOff);

/** Renders the "square-parking" icon from https://lucide.dev/icons/square-parking. */
export const SquareParkingIcon = /*#__PURE__*/ createLucideIcon(
	"square-parking",
	registry.squareParking,
);

/** Renders the "square-parking-off" icon from https://lucide.dev/icons/square-parking-off. */
export const SquareParkingOffIcon = /*#__PURE__*/ createLucideIcon(
	"square-parking-off",
	registry.squareParkingOff,
);

/** Renders the "square-pause" icon from https://lucide.dev/icons/square-pause. */
export const SquarePauseIcon = /*#__PURE__*/ createLucideIcon("square-pause", registry.squarePause);

/** Renders the "square-pen" icon from https://lucide.dev/icons/square-pen. */
export const SquarePenIcon = /*#__PURE__*/ createLucideIcon("square-pen", registry.squarePen);

/** Renders the "square-percent" icon from https://lucide.dev/icons/square-percent. */
export const SquarePercentIcon = /*#__PURE__*/ createLucideIcon(
	"square-percent",
	registry.squarePercent,
);

/** Renders the "square-pi" icon from https://lucide.dev/icons/square-pi. */
export const SquarePiIcon = /*#__PURE__*/ createLucideIcon("square-pi", registry.squarePi);

/** Renders the "square-pilcrow" icon from https://lucide.dev/icons/square-pilcrow. */
export const SquarePilcrowIcon = /*#__PURE__*/ createLucideIcon(
	"square-pilcrow",
	registry.squarePilcrow,
);

/** Renders the "square-play" icon from https://lucide.dev/icons/square-play. */
export const SquarePlayIcon = /*#__PURE__*/ createLucideIcon("square-play", registry.squarePlay);

/** Renders the "square-plus" icon from https://lucide.dev/icons/square-plus. */
export const SquarePlusIcon = /*#__PURE__*/ createLucideIcon("square-plus", registry.squarePlus);

/** Renders the "square-power" icon from https://lucide.dev/icons/square-power. */
export const SquarePowerIcon = /*#__PURE__*/ createLucideIcon("square-power", registry.squarePower);

/** Renders the "square-radical" icon from https://lucide.dev/icons/square-radical. */
export const SquareRadicalIcon = /*#__PURE__*/ createLucideIcon(
	"square-radical",
	registry.squareRadical,
);

/** Renders the "square-round-corner" icon from https://lucide.dev/icons/square-round-corner. */
export const SquareRoundCornerIcon = /*#__PURE__*/ createLucideIcon(
	"square-round-corner",
	registry.squareRoundCorner,
);

/** Renders the "square-scissors" icon from https://lucide.dev/icons/square-scissors. */
export const SquareScissorsIcon = /*#__PURE__*/ createLucideIcon(
	"square-scissors",
	registry.squareScissors,
);

/** Renders the "square-sigma" icon from https://lucide.dev/icons/square-sigma. */
export const SquareSigmaIcon = /*#__PURE__*/ createLucideIcon("square-sigma", registry.squareSigma);

/** Renders the "square-slash" icon from https://lucide.dev/icons/square-slash. */
export const SquareSlashIcon = /*#__PURE__*/ createLucideIcon("square-slash", registry.squareSlash);

/** Renders the "square-sparkles" icon from https://lucide.dev/icons/square-sparkles. */
export const SquareSparklesIcon = /*#__PURE__*/ createLucideIcon(
	"square-sparkles",
	registry.squareSparkles,
);

/** Renders the "square-split-horizontal" icon from https://lucide.dev/icons/square-split-horizontal. */
export const SquareSplitHorizontalIcon = /*#__PURE__*/ createLucideIcon(
	"square-split-horizontal",
	registry.squareSplitHorizontal,
);

/** Renders the "square-split-vertical" icon from https://lucide.dev/icons/square-split-vertical. */
export const SquareSplitVerticalIcon = /*#__PURE__*/ createLucideIcon(
	"square-split-vertical",
	registry.squareSplitVertical,
);

/** Renders the "square-square" icon from https://lucide.dev/icons/square-square. */
export const SquareSquareIcon = /*#__PURE__*/ createLucideIcon(
	"square-square",
	registry.squareSquare,
);

/** Renders the "square-stack" icon from https://lucide.dev/icons/square-stack. */
export const SquareStackIcon = /*#__PURE__*/ createLucideIcon("square-stack", registry.squareStack);

/** Renders the "square-star" icon from https://lucide.dev/icons/square-star. */
export const SquareStarIcon = /*#__PURE__*/ createLucideIcon("square-star", registry.squareStar);

/** Renders the "square-stop" icon from https://lucide.dev/icons/square-stop. */
export const SquareStopIcon = /*#__PURE__*/ createLucideIcon("square-stop", registry.squareStop);

/** Renders the "square-terminal" icon from https://lucide.dev/icons/square-terminal. */
export const SquareTerminalIcon = /*#__PURE__*/ createLucideIcon(
	"square-terminal",
	registry.squareTerminal,
);

/** Renders the "square-text" icon from https://lucide.dev/icons/square-text. */
export const SquareTextIcon = /*#__PURE__*/ createLucideIcon("square-text", registry.squareText);

/** Renders the "square-user" icon from https://lucide.dev/icons/square-user. */
export const SquareUserIcon = /*#__PURE__*/ createLucideIcon("square-user", registry.squareUser);

/** Renders the "square-user-round" icon from https://lucide.dev/icons/square-user-round. */
export const SquareUserRoundIcon = /*#__PURE__*/ createLucideIcon(
	"square-user-round",
	registry.squareUserRound,
);

/** Renders the "square-x" icon from https://lucide.dev/icons/square-x. */
export const SquareXIcon = /*#__PURE__*/ createLucideIcon("square-x", registry.squareX);

/** Renders the "squares-exclude" icon from https://lucide.dev/icons/squares-exclude. */
export const SquaresExcludeIcon = /*#__PURE__*/ createLucideIcon(
	"squares-exclude",
	registry.squaresExclude,
);

/** Renders the "squares-intersect" icon from https://lucide.dev/icons/squares-intersect. */
export const SquaresIntersectIcon = /*#__PURE__*/ createLucideIcon(
	"squares-intersect",
	registry.squaresIntersect,
);

/** Renders the "squares-subtract" icon from https://lucide.dev/icons/squares-subtract. */
export const SquaresSubtractIcon = /*#__PURE__*/ createLucideIcon(
	"squares-subtract",
	registry.squaresSubtract,
);

/** Renders the "squares-unite" icon from https://lucide.dev/icons/squares-unite. */
export const SquaresUniteIcon = /*#__PURE__*/ createLucideIcon(
	"squares-unite",
	registry.squaresUnite,
);

/** Renders the "squircle" icon from https://lucide.dev/icons/squircle. */
export const SquircleIcon = /*#__PURE__*/ createLucideIcon("squircle", registry.squircle);

/** Renders the "squircle-dashed" icon from https://lucide.dev/icons/squircle-dashed. */
export const SquircleDashedIcon = /*#__PURE__*/ createLucideIcon(
	"squircle-dashed",
	registry.squircleDashed,
);

/** Renders the "squirrel" icon from https://lucide.dev/icons/squirrel. */
export const SquirrelIcon = /*#__PURE__*/ createLucideIcon("squirrel", registry.squirrel);

/** Renders the "stamp" icon from https://lucide.dev/icons/stamp. */
export const StampIcon = /*#__PURE__*/ createLucideIcon("stamp", registry.stamp);

/** Renders the "star" icon from https://lucide.dev/icons/star. */
export const StarIcon = /*#__PURE__*/ createLucideIcon("star", registry.star);

/** Renders the "star-check" icon from https://lucide.dev/icons/star-check. */
export const StarCheckIcon = /*#__PURE__*/ createLucideIcon("star-check", registry.starCheck);

/** Renders the "star-half" icon from https://lucide.dev/icons/star-half. */
export const StarHalfIcon = /*#__PURE__*/ createLucideIcon("star-half", registry.starHalf);

/** Renders the "star-minus" icon from https://lucide.dev/icons/star-minus. */
export const StarMinusIcon = /*#__PURE__*/ createLucideIcon("star-minus", registry.starMinus);

/** Renders the "star-off" icon from https://lucide.dev/icons/star-off. */
export const StarOffIcon = /*#__PURE__*/ createLucideIcon("star-off", registry.starOff);

/** Renders the "star-plus" icon from https://lucide.dev/icons/star-plus. */
export const StarPlusIcon = /*#__PURE__*/ createLucideIcon("star-plus", registry.starPlus);

/** Renders the "star-x" icon from https://lucide.dev/icons/star-x. */
export const StarXIcon = /*#__PURE__*/ createLucideIcon("star-x", registry.starX);

/** Renders the "step-back" icon from https://lucide.dev/icons/step-back. */
export const StepBackIcon = /*#__PURE__*/ createLucideIcon("step-back", registry.stepBack);

/** Renders the "step-forward" icon from https://lucide.dev/icons/step-forward. */
export const StepForwardIcon = /*#__PURE__*/ createLucideIcon("step-forward", registry.stepForward);

/** Renders the "stethoscope" icon from https://lucide.dev/icons/stethoscope. */
export const StethoscopeIcon = /*#__PURE__*/ createLucideIcon("stethoscope", registry.stethoscope);

/** Renders the "sticker" icon from https://lucide.dev/icons/sticker. */
export const StickerIcon = /*#__PURE__*/ createLucideIcon("sticker", registry.sticker);

/** Renders the "sticky-note" icon from https://lucide.dev/icons/sticky-note. */
export const StickyNoteIcon = /*#__PURE__*/ createLucideIcon("sticky-note", registry.stickyNote);

/** Renders the "sticky-note-check" icon from https://lucide.dev/icons/sticky-note-check. */
export const StickyNoteCheckIcon = /*#__PURE__*/ createLucideIcon(
	"sticky-note-check",
	registry.stickyNoteCheck,
);

/** Renders the "sticky-note-minus" icon from https://lucide.dev/icons/sticky-note-minus. */
export const StickyNoteMinusIcon = /*#__PURE__*/ createLucideIcon(
	"sticky-note-minus",
	registry.stickyNoteMinus,
);

/** Renders the "sticky-note-off" icon from https://lucide.dev/icons/sticky-note-off. */
export const StickyNoteOffIcon = /*#__PURE__*/ createLucideIcon(
	"sticky-note-off",
	registry.stickyNoteOff,
);

/** Renders the "sticky-note-plus" icon from https://lucide.dev/icons/sticky-note-plus. */
export const StickyNotePlusIcon = /*#__PURE__*/ createLucideIcon(
	"sticky-note-plus",
	registry.stickyNotePlus,
);

/** Renders the "sticky-note-x" icon from https://lucide.dev/icons/sticky-note-x. */
export const StickyNoteXIcon = /*#__PURE__*/ createLucideIcon(
	"sticky-note-x",
	registry.stickyNoteX,
);

/** Renders the "sticky-notes" icon from https://lucide.dev/icons/sticky-notes. */
export const StickyNotesIcon = /*#__PURE__*/ createLucideIcon("sticky-notes", registry.stickyNotes);

/** Renders the "stone" icon from https://lucide.dev/icons/stone. */
export const StoneIcon = /*#__PURE__*/ createLucideIcon("stone", registry.stone);

/** Renders the "store" icon from https://lucide.dev/icons/store. */
export const StoreIcon = /*#__PURE__*/ createLucideIcon("store", registry.store);

/** Renders the "stretch-horizontal" icon from https://lucide.dev/icons/stretch-horizontal. */
export const StretchHorizontalIcon = /*#__PURE__*/ createLucideIcon(
	"stretch-horizontal",
	registry.stretchHorizontal,
);

/** Renders the "stretch-vertical" icon from https://lucide.dev/icons/stretch-vertical. */
export const StretchVerticalIcon = /*#__PURE__*/ createLucideIcon(
	"stretch-vertical",
	registry.stretchVertical,
);

/** Renders the "strikethrough" icon from https://lucide.dev/icons/strikethrough. */
export const StrikethroughIcon = /*#__PURE__*/ createLucideIcon(
	"strikethrough",
	registry.strikethrough,
);

/** Renders the "subscript" icon from https://lucide.dev/icons/subscript. */
export const SubscriptIcon = /*#__PURE__*/ createLucideIcon("subscript", registry.subscript);

/** Renders the "summary" icon from https://lucide.dev/icons/summary. */
export const SummaryIcon = /*#__PURE__*/ createLucideIcon("summary", registry.summary);

/** Renders the "sun" icon from https://lucide.dev/icons/sun. */
export const SunIcon = /*#__PURE__*/ createLucideIcon("sun", registry.sun);

/** Renders the "sun-dim" icon from https://lucide.dev/icons/sun-dim. */
export const SunDimIcon = /*#__PURE__*/ createLucideIcon("sun-dim", registry.sunDim);

/** Renders the "sun-medium" icon from https://lucide.dev/icons/sun-medium. */
export const SunMediumIcon = /*#__PURE__*/ createLucideIcon("sun-medium", registry.sunMedium);

/** Renders the "sun-moon" icon from https://lucide.dev/icons/sun-moon. */
export const SunMoonIcon = /*#__PURE__*/ createLucideIcon("sun-moon", registry.sunMoon);

/** Renders the "sun-snow" icon from https://lucide.dev/icons/sun-snow. */
export const SunSnowIcon = /*#__PURE__*/ createLucideIcon("sun-snow", registry.sunSnow);

/** Renders the "sunrise" icon from https://lucide.dev/icons/sunrise. */
export const SunriseIcon = /*#__PURE__*/ createLucideIcon("sunrise", registry.sunrise);

/** Renders the "sunset" icon from https://lucide.dev/icons/sunset. */
export const SunsetIcon = /*#__PURE__*/ createLucideIcon("sunset", registry.sunset);

/** Renders the "superscript" icon from https://lucide.dev/icons/superscript. */
export const SuperscriptIcon = /*#__PURE__*/ createLucideIcon("superscript", registry.superscript);

/** Renders the "swatch-book" icon from https://lucide.dev/icons/swatch-book. */
export const SwatchBookIcon = /*#__PURE__*/ createLucideIcon("swatch-book", registry.swatchBook);

/** Renders the "swiss-franc" icon from https://lucide.dev/icons/swiss-franc. */
export const SwissFrancIcon = /*#__PURE__*/ createLucideIcon("swiss-franc", registry.swissFranc);

/** Renders the "switch-camera" icon from https://lucide.dev/icons/switch-camera. */
export const SwitchCameraIcon = /*#__PURE__*/ createLucideIcon(
	"switch-camera",
	registry.switchCamera,
);

/** Renders the "sword" icon from https://lucide.dev/icons/sword. */
export const SwordIcon = /*#__PURE__*/ createLucideIcon("sword", registry.sword);

/** Renders the "swords" icon from https://lucide.dev/icons/swords. */
export const SwordsIcon = /*#__PURE__*/ createLucideIcon("swords", registry.swords);

/** Renders the "syringe" icon from https://lucide.dev/icons/syringe. */
export const SyringeIcon = /*#__PURE__*/ createLucideIcon("syringe", registry.syringe);

/** Renders the "table" icon from https://lucide.dev/icons/table. */
export const TableIcon = /*#__PURE__*/ createLucideIcon("table", registry.table);

/** Renders the "table-2" icon from https://lucide.dev/icons/table-2. */
export const Table2Icon = /*#__PURE__*/ createLucideIcon("table-2", registry.table2);

/** Renders the "table-cells-merge" icon from https://lucide.dev/icons/table-cells-merge. */
export const TableCellsMergeIcon = /*#__PURE__*/ createLucideIcon(
	"table-cells-merge",
	registry.tableCellsMerge,
);

/** Renders the "table-cells-split" icon from https://lucide.dev/icons/table-cells-split. */
export const TableCellsSplitIcon = /*#__PURE__*/ createLucideIcon(
	"table-cells-split",
	registry.tableCellsSplit,
);

/** Renders the "table-columns-split" icon from https://lucide.dev/icons/table-columns-split. */
export const TableColumnsSplitIcon = /*#__PURE__*/ createLucideIcon(
	"table-columns-split",
	registry.tableColumnsSplit,
);

/** Renders the "table-of-contents" icon from https://lucide.dev/icons/table-of-contents. */
export const TableOfContentsIcon = /*#__PURE__*/ createLucideIcon(
	"table-of-contents",
	registry.tableOfContents,
);

/** Renders the "table-properties" icon from https://lucide.dev/icons/table-properties. */
export const TablePropertiesIcon = /*#__PURE__*/ createLucideIcon(
	"table-properties",
	registry.tableProperties,
);

/** Renders the "table-rows-split" icon from https://lucide.dev/icons/table-rows-split. */
export const TableRowsSplitIcon = /*#__PURE__*/ createLucideIcon(
	"table-rows-split",
	registry.tableRowsSplit,
);

/** Renders the "tablet" icon from https://lucide.dev/icons/tablet. */
export const TabletIcon = /*#__PURE__*/ createLucideIcon("tablet", registry.tablet);

/** Renders the "tablet-smartphone" icon from https://lucide.dev/icons/tablet-smartphone. */
export const TabletSmartphoneIcon = /*#__PURE__*/ createLucideIcon(
	"tablet-smartphone",
	registry.tabletSmartphone,
);

/** Renders the "tablets" icon from https://lucide.dev/icons/tablets. */
export const TabletsIcon = /*#__PURE__*/ createLucideIcon("tablets", registry.tablets);

/** Renders the "tag" icon from https://lucide.dev/icons/tag. */
export const TagIcon = /*#__PURE__*/ createLucideIcon("tag", registry.tag);

/** Renders the "tag-plus" icon from https://lucide.dev/icons/tag-plus. */
export const TagPlusIcon = /*#__PURE__*/ createLucideIcon("tag-plus", registry.tagPlus);

/** Renders the "tag-x" icon from https://lucide.dev/icons/tag-x. */
export const TagXIcon = /*#__PURE__*/ createLucideIcon("tag-x", registry.tagX);

/** Renders the "tags" icon from https://lucide.dev/icons/tags. */
export const TagsIcon = /*#__PURE__*/ createLucideIcon("tags", registry.tags);

/** Renders the "tally-1" icon from https://lucide.dev/icons/tally-1. */
export const Tally1Icon = /*#__PURE__*/ createLucideIcon("tally-1", registry.tally1);

/** Renders the "tally-2" icon from https://lucide.dev/icons/tally-2. */
export const Tally2Icon = /*#__PURE__*/ createLucideIcon("tally-2", registry.tally2);

/** Renders the "tally-3" icon from https://lucide.dev/icons/tally-3. */
export const Tally3Icon = /*#__PURE__*/ createLucideIcon("tally-3", registry.tally3);

/** Renders the "tally-4" icon from https://lucide.dev/icons/tally-4. */
export const Tally4Icon = /*#__PURE__*/ createLucideIcon("tally-4", registry.tally4);

/** Renders the "tally-5" icon from https://lucide.dev/icons/tally-5. */
export const Tally5Icon = /*#__PURE__*/ createLucideIcon("tally-5", registry.tally5);

/** Renders the "tangent" icon from https://lucide.dev/icons/tangent. */
export const TangentIcon = /*#__PURE__*/ createLucideIcon("tangent", registry.tangent);

/** Renders the "target" icon from https://lucide.dev/icons/target. */
export const TargetIcon = /*#__PURE__*/ createLucideIcon("target", registry.target);

/** Renders the "telescope" icon from https://lucide.dev/icons/telescope. */
export const TelescopeIcon = /*#__PURE__*/ createLucideIcon("telescope", registry.telescope);

/** Renders the "tent" icon from https://lucide.dev/icons/tent. */
export const TentIcon = /*#__PURE__*/ createLucideIcon("tent", registry.tent);

/** Renders the "tent-tree" icon from https://lucide.dev/icons/tent-tree. */
export const TentTreeIcon = /*#__PURE__*/ createLucideIcon("tent-tree", registry.tentTree);

/** Renders the "terminal" icon from https://lucide.dev/icons/terminal. */
export const TerminalIcon = /*#__PURE__*/ createLucideIcon("terminal", registry.terminal);

/** Renders the "test-tube" icon from https://lucide.dev/icons/test-tube. */
export const TestTubeIcon = /*#__PURE__*/ createLucideIcon("test-tube", registry.testTube);

/** Renders the "test-tube-diagonal" icon from https://lucide.dev/icons/test-tube-diagonal. */
export const TestTubeDiagonalIcon = /*#__PURE__*/ createLucideIcon(
	"test-tube-diagonal",
	registry.testTubeDiagonal,
);

/** Renders the "test-tubes" icon from https://lucide.dev/icons/test-tubes. */
export const TestTubesIcon = /*#__PURE__*/ createLucideIcon("test-tubes", registry.testTubes);

/** Renders the "text-align-center" icon from https://lucide.dev/icons/text-align-center. */
export const TextAlignCenterIcon = /*#__PURE__*/ createLucideIcon(
	"text-align-center",
	registry.textAlignCenter,
);

/** Renders the "text-align-end" icon from https://lucide.dev/icons/text-align-end. */
export const TextAlignEndIcon = /*#__PURE__*/ createLucideIcon(
	"text-align-end",
	registry.textAlignEnd,
);

/** Renders the "text-align-justify" icon from https://lucide.dev/icons/text-align-justify. */
export const TextAlignJustifyIcon = /*#__PURE__*/ createLucideIcon(
	"text-align-justify",
	registry.textAlignJustify,
);

/** Renders the "text-align-justify-center" icon from https://lucide.dev/icons/text-align-justify-center. */
export const TextAlignJustifyCenterIcon = /*#__PURE__*/ createLucideIcon(
	"text-align-justify-center",
	registry.textAlignJustifyCenter,
);

/** Renders the "text-align-justify-end" icon from https://lucide.dev/icons/text-align-justify-end. */
export const TextAlignJustifyEndIcon = /*#__PURE__*/ createLucideIcon(
	"text-align-justify-end",
	registry.textAlignJustifyEnd,
);

/** Renders the "text-align-justify-start" icon from https://lucide.dev/icons/text-align-justify-start. */
export const TextAlignJustifyStartIcon = /*#__PURE__*/ createLucideIcon(
	"text-align-justify-start",
	registry.textAlignJustifyStart,
);

/** Renders the "text-align-start" icon from https://lucide.dev/icons/text-align-start. */
export const TextAlignStartIcon = /*#__PURE__*/ createLucideIcon(
	"text-align-start",
	registry.textAlignStart,
);

/** Renders the "text-cursor" icon from https://lucide.dev/icons/text-cursor. */
export const TextCursorIcon = /*#__PURE__*/ createLucideIcon("text-cursor", registry.textCursor);

/** Renders the "text-cursor-input" icon from https://lucide.dev/icons/text-cursor-input. */
export const TextCursorInputIcon = /*#__PURE__*/ createLucideIcon(
	"text-cursor-input",
	registry.textCursorInput,
);

/** Renders the "text-initial" icon from https://lucide.dev/icons/text-initial. */
export const TextInitialIcon = /*#__PURE__*/ createLucideIcon("text-initial", registry.textInitial);

/** Renders the "text-quote" icon from https://lucide.dev/icons/text-quote. */
export const TextQuoteIcon = /*#__PURE__*/ createLucideIcon("text-quote", registry.textQuote);

/** Renders the "text-search" icon from https://lucide.dev/icons/text-search. */
export const TextSearchIcon = /*#__PURE__*/ createLucideIcon("text-search", registry.textSearch);

/** Renders the "text-wrap" icon from https://lucide.dev/icons/text-wrap. */
export const TextWrapIcon = /*#__PURE__*/ createLucideIcon("text-wrap", registry.textWrap);

/** Renders the "theater" icon from https://lucide.dev/icons/theater. */
export const TheaterIcon = /*#__PURE__*/ createLucideIcon("theater", registry.theater);

/** Renders the "thermometer" icon from https://lucide.dev/icons/thermometer. */
export const ThermometerIcon = /*#__PURE__*/ createLucideIcon("thermometer", registry.thermometer);

/** Renders the "thermometer-snowflake" icon from https://lucide.dev/icons/thermometer-snowflake. */
export const ThermometerSnowflakeIcon = /*#__PURE__*/ createLucideIcon(
	"thermometer-snowflake",
	registry.thermometerSnowflake,
);

/** Renders the "thermometer-sun" icon from https://lucide.dev/icons/thermometer-sun. */
export const ThermometerSunIcon = /*#__PURE__*/ createLucideIcon(
	"thermometer-sun",
	registry.thermometerSun,
);

/** Renders the "thumbs-down" icon from https://lucide.dev/icons/thumbs-down. */
export const ThumbsDownIcon = /*#__PURE__*/ createLucideIcon("thumbs-down", registry.thumbsDown);

/** Renders the "thumbs-up" icon from https://lucide.dev/icons/thumbs-up. */
export const ThumbsUpIcon = /*#__PURE__*/ createLucideIcon("thumbs-up", registry.thumbsUp);

/** Renders the "tic-tac-toe" icon from https://lucide.dev/icons/tic-tac-toe. */
export const TicTacToeIcon = /*#__PURE__*/ createLucideIcon("tic-tac-toe", registry.ticTacToe);

/** Renders the "ticket" icon from https://lucide.dev/icons/ticket. */
export const TicketIcon = /*#__PURE__*/ createLucideIcon("ticket", registry.ticket);

/** Renders the "ticket-check" icon from https://lucide.dev/icons/ticket-check. */
export const TicketCheckIcon = /*#__PURE__*/ createLucideIcon("ticket-check", registry.ticketCheck);

/** Renders the "ticket-minus" icon from https://lucide.dev/icons/ticket-minus. */
export const TicketMinusIcon = /*#__PURE__*/ createLucideIcon("ticket-minus", registry.ticketMinus);

/** Renders the "ticket-percent" icon from https://lucide.dev/icons/ticket-percent. */
export const TicketPercentIcon = /*#__PURE__*/ createLucideIcon(
	"ticket-percent",
	registry.ticketPercent,
);

/** Renders the "ticket-plus" icon from https://lucide.dev/icons/ticket-plus. */
export const TicketPlusIcon = /*#__PURE__*/ createLucideIcon("ticket-plus", registry.ticketPlus);

/** Renders the "ticket-slash" icon from https://lucide.dev/icons/ticket-slash. */
export const TicketSlashIcon = /*#__PURE__*/ createLucideIcon("ticket-slash", registry.ticketSlash);

/** Renders the "ticket-x" icon from https://lucide.dev/icons/ticket-x. */
export const TicketXIcon = /*#__PURE__*/ createLucideIcon("ticket-x", registry.ticketX);

/** Renders the "tickets" icon from https://lucide.dev/icons/tickets. */
export const TicketsIcon = /*#__PURE__*/ createLucideIcon("tickets", registry.tickets);

/** Renders the "tickets-plane" icon from https://lucide.dev/icons/tickets-plane. */
export const TicketsPlaneIcon = /*#__PURE__*/ createLucideIcon(
	"tickets-plane",
	registry.ticketsPlane,
);

/** Renders the "timeline" icon from https://lucide.dev/icons/timeline. */
export const TimelineIcon = /*#__PURE__*/ createLucideIcon("timeline", registry.timeline);

/** Renders the "timer" icon from https://lucide.dev/icons/timer. */
export const TimerIcon = /*#__PURE__*/ createLucideIcon("timer", registry.timer);

/** Renders the "timer-off" icon from https://lucide.dev/icons/timer-off. */
export const TimerOffIcon = /*#__PURE__*/ createLucideIcon("timer-off", registry.timerOff);

/** Renders the "timer-reset" icon from https://lucide.dev/icons/timer-reset. */
export const TimerResetIcon = /*#__PURE__*/ createLucideIcon("timer-reset", registry.timerReset);

/** Renders the "toggle-left" icon from https://lucide.dev/icons/toggle-left. */
export const ToggleLeftIcon = /*#__PURE__*/ createLucideIcon("toggle-left", registry.toggleLeft);

/** Renders the "toggle-right" icon from https://lucide.dev/icons/toggle-right. */
export const ToggleRightIcon = /*#__PURE__*/ createLucideIcon("toggle-right", registry.toggleRight);

/** Renders the "toilet" icon from https://lucide.dev/icons/toilet. */
export const ToiletIcon = /*#__PURE__*/ createLucideIcon("toilet", registry.toilet);

/** Renders the "tombstone" icon from https://lucide.dev/icons/tombstone. */
export const TombstoneIcon = /*#__PURE__*/ createLucideIcon("tombstone", registry.tombstone);

/** Renders the "tool-case" icon from https://lucide.dev/icons/tool-case. */
export const ToolCaseIcon = /*#__PURE__*/ createLucideIcon("tool-case", registry.toolCase);

/** Renders the "toolbox" icon from https://lucide.dev/icons/toolbox. */
export const ToolboxIcon = /*#__PURE__*/ createLucideIcon("toolbox", registry.toolbox);

/** Renders the "toothbrush" icon from https://lucide.dev/icons/toothbrush. */
export const ToothbrushIcon = /*#__PURE__*/ createLucideIcon("toothbrush", registry.toothbrush);

/** Renders the "toothbrush-sparkles" icon from https://lucide.dev/icons/toothbrush-sparkles. */
export const ToothbrushSparklesIcon = /*#__PURE__*/ createLucideIcon(
	"toothbrush-sparkles",
	registry.toothbrushSparkles,
);

/** Renders the "tornado" icon from https://lucide.dev/icons/tornado. */
export const TornadoIcon = /*#__PURE__*/ createLucideIcon("tornado", registry.tornado);

/** Renders the "torus" icon from https://lucide.dev/icons/torus. */
export const TorusIcon = /*#__PURE__*/ createLucideIcon("torus", registry.torus);

/** Renders the "touchpad" icon from https://lucide.dev/icons/touchpad. */
export const TouchpadIcon = /*#__PURE__*/ createLucideIcon("touchpad", registry.touchpad);

/** Renders the "touchpad-off" icon from https://lucide.dev/icons/touchpad-off. */
export const TouchpadOffIcon = /*#__PURE__*/ createLucideIcon("touchpad-off", registry.touchpadOff);

/** Renders the "towel-rack" icon from https://lucide.dev/icons/towel-rack. */
export const TowelRackIcon = /*#__PURE__*/ createLucideIcon("towel-rack", registry.towelRack);

/** Renders the "tower-control" icon from https://lucide.dev/icons/tower-control. */
export const TowerControlIcon = /*#__PURE__*/ createLucideIcon(
	"tower-control",
	registry.towerControl,
);

/** Renders the "toy-brick" icon from https://lucide.dev/icons/toy-brick. */
export const ToyBrickIcon = /*#__PURE__*/ createLucideIcon("toy-brick", registry.toyBrick);

/** Renders the "tractor" icon from https://lucide.dev/icons/tractor. */
export const TractorIcon = /*#__PURE__*/ createLucideIcon("tractor", registry.tractor);

/** Renders the "traffic-cone" icon from https://lucide.dev/icons/traffic-cone. */
export const TrafficConeIcon = /*#__PURE__*/ createLucideIcon("traffic-cone", registry.trafficCone);

/** Renders the "trailer" icon from https://lucide.dev/icons/trailer. */
export const TrailerIcon = /*#__PURE__*/ createLucideIcon("trailer", registry.trailer);

/** Renders the "train-front" icon from https://lucide.dev/icons/train-front. */
export const TrainFrontIcon = /*#__PURE__*/ createLucideIcon("train-front", registry.trainFront);

/** Renders the "train-front-tunnel" icon from https://lucide.dev/icons/train-front-tunnel. */
export const TrainFrontTunnelIcon = /*#__PURE__*/ createLucideIcon(
	"train-front-tunnel",
	registry.trainFrontTunnel,
);

/** Renders the "train-track" icon from https://lucide.dev/icons/train-track. */
export const TrainTrackIcon = /*#__PURE__*/ createLucideIcon("train-track", registry.trainTrack);

/** Renders the "tram-front" icon from https://lucide.dev/icons/tram-front. */
export const TramFrontIcon = /*#__PURE__*/ createLucideIcon("tram-front", registry.tramFront);

/** Renders the "transgender" icon from https://lucide.dev/icons/transgender. */
export const TransgenderIcon = /*#__PURE__*/ createLucideIcon("transgender", registry.transgender);

/** Renders the "trash" icon from https://lucide.dev/icons/trash. */
export const TrashIcon = /*#__PURE__*/ createLucideIcon("trash", registry.trash);

/** Renders the "trash-off" icon from https://lucide.dev/icons/trash-off. */
export const TrashOffIcon = /*#__PURE__*/ createLucideIcon("trash-off", registry.trashOff);

/** Renders the "tree-deciduous" icon from https://lucide.dev/icons/tree-deciduous. */
export const TreeDeciduousIcon = /*#__PURE__*/ createLucideIcon(
	"tree-deciduous",
	registry.treeDeciduous,
);

/** Renders the "tree-palm" icon from https://lucide.dev/icons/tree-palm. */
export const TreePalmIcon = /*#__PURE__*/ createLucideIcon("tree-palm", registry.treePalm);

/** Renders the "tree-pine" icon from https://lucide.dev/icons/tree-pine. */
export const TreePineIcon = /*#__PURE__*/ createLucideIcon("tree-pine", registry.treePine);

/** Renders the "trees" icon from https://lucide.dev/icons/trees. */
export const TreesIcon = /*#__PURE__*/ createLucideIcon("trees", registry.trees);

/** Renders the "trending-down" icon from https://lucide.dev/icons/trending-down. */
export const TrendingDownIcon = /*#__PURE__*/ createLucideIcon(
	"trending-down",
	registry.trendingDown,
);

/** Renders the "trending-up" icon from https://lucide.dev/icons/trending-up. */
export const TrendingUpIcon = /*#__PURE__*/ createLucideIcon("trending-up", registry.trendingUp);

/** Renders the "trending-up-down" icon from https://lucide.dev/icons/trending-up-down. */
export const TrendingUpDownIcon = /*#__PURE__*/ createLucideIcon(
	"trending-up-down",
	registry.trendingUpDown,
);

/** Renders the "triangle" icon from https://lucide.dev/icons/triangle. */
export const TriangleIcon = /*#__PURE__*/ createLucideIcon("triangle", registry.triangle);

/** Renders the "triangle-alert" icon from https://lucide.dev/icons/triangle-alert. */
export const TriangleAlertIcon = /*#__PURE__*/ createLucideIcon(
	"triangle-alert",
	registry.triangleAlert,
);

/** Renders the "triangle-dashed" icon from https://lucide.dev/icons/triangle-dashed. */
export const TriangleDashedIcon = /*#__PURE__*/ createLucideIcon(
	"triangle-dashed",
	registry.triangleDashed,
);

/** Renders the "triangle-right" icon from https://lucide.dev/icons/triangle-right. */
export const TriangleRightIcon = /*#__PURE__*/ createLucideIcon(
	"triangle-right",
	registry.triangleRight,
);

/** Renders the "triangles-centerline-dashed-horizontal" icon from https://lucide.dev/icons/triangles-centerline-dashed-horizontal. */
export const TrianglesCenterlineDashedHorizontalIcon = /*#__PURE__*/ createLucideIcon(
	"triangles-centerline-dashed-horizontal",
	registry.trianglesCenterlineDashedHorizontal,
);

/** Renders the "triangles-centerline-dashed-vertical" icon from https://lucide.dev/icons/triangles-centerline-dashed-vertical. */
export const TrianglesCenterlineDashedVerticalIcon = /*#__PURE__*/ createLucideIcon(
	"triangles-centerline-dashed-vertical",
	registry.trianglesCenterlineDashedVertical,
);

/** Renders the "trophy" icon from https://lucide.dev/icons/trophy. */
export const TrophyIcon = /*#__PURE__*/ createLucideIcon("trophy", registry.trophy);

/** Renders the "truck" icon from https://lucide.dev/icons/truck. */
export const TruckIcon = /*#__PURE__*/ createLucideIcon("truck", registry.truck);

/** Renders the "truck-electric" icon from https://lucide.dev/icons/truck-electric. */
export const TruckElectricIcon = /*#__PURE__*/ createLucideIcon(
	"truck-electric",
	registry.truckElectric,
);

/** Renders the "tube-lotion" icon from https://lucide.dev/icons/tube-lotion. */
export const TubeLotionIcon = /*#__PURE__*/ createLucideIcon("tube-lotion", registry.tubeLotion);

/** Renders the "turkish-lira" icon from https://lucide.dev/icons/turkish-lira. */
export const TurkishLiraIcon = /*#__PURE__*/ createLucideIcon("turkish-lira", registry.turkishLira);

/** Renders the "turntable" icon from https://lucide.dev/icons/turntable. */
export const TurntableIcon = /*#__PURE__*/ createLucideIcon("turntable", registry.turntable);

/** Renders the "turtle" icon from https://lucide.dev/icons/turtle. */
export const TurtleIcon = /*#__PURE__*/ createLucideIcon("turtle", registry.turtle);

/** Renders the "tv" icon from https://lucide.dev/icons/tv. */
export const TvIcon = /*#__PURE__*/ createLucideIcon("tv", registry.tv);

/** Renders the "tv-minimal" icon from https://lucide.dev/icons/tv-minimal. */
export const TvMinimalIcon = /*#__PURE__*/ createLucideIcon("tv-minimal", registry.tvMinimal);

/** Renders the "tv-minimal-play" icon from https://lucide.dev/icons/tv-minimal-play. */
export const TvMinimalPlayIcon = /*#__PURE__*/ createLucideIcon(
	"tv-minimal-play",
	registry.tvMinimalPlay,
);

/** Renders the "type" icon from https://lucide.dev/icons/type. */
export const TypeIcon = /*#__PURE__*/ createLucideIcon("type", registry.type);

/** Renders the "type-outline" icon from https://lucide.dev/icons/type-outline. */
export const TypeOutlineIcon = /*#__PURE__*/ createLucideIcon("type-outline", registry.typeOutline);

/** Renders the "umbrella" icon from https://lucide.dev/icons/umbrella. */
export const UmbrellaIcon = /*#__PURE__*/ createLucideIcon("umbrella", registry.umbrella);

/** Renders the "umbrella-off" icon from https://lucide.dev/icons/umbrella-off. */
export const UmbrellaOffIcon = /*#__PURE__*/ createLucideIcon("umbrella-off", registry.umbrellaOff);

/** Renders the "underline" icon from https://lucide.dev/icons/underline. */
export const UnderlineIcon = /*#__PURE__*/ createLucideIcon("underline", registry.underline);

/** Renders the "undo" icon from https://lucide.dev/icons/undo. */
export const UndoIcon = /*#__PURE__*/ createLucideIcon("undo", registry.undo);

/** Renders the "undo-2" icon from https://lucide.dev/icons/undo-2. */
export const Undo2Icon = /*#__PURE__*/ createLucideIcon("undo-2", registry.undo2);

/** Renders the "undo-dot" icon from https://lucide.dev/icons/undo-dot. */
export const UndoDotIcon = /*#__PURE__*/ createLucideIcon("undo-dot", registry.undoDot);

/** Renders the "unfold-horizontal" icon from https://lucide.dev/icons/unfold-horizontal. */
export const UnfoldHorizontalIcon = /*#__PURE__*/ createLucideIcon(
	"unfold-horizontal",
	registry.unfoldHorizontal,
);

/** Renders the "unfold-vertical" icon from https://lucide.dev/icons/unfold-vertical. */
export const UnfoldVerticalIcon = /*#__PURE__*/ createLucideIcon(
	"unfold-vertical",
	registry.unfoldVertical,
);

/** Renders the "ungroup" icon from https://lucide.dev/icons/ungroup. */
export const UngroupIcon = /*#__PURE__*/ createLucideIcon("ungroup", registry.ungroup);

/** Renders the "university" icon from https://lucide.dev/icons/university. */
export const UniversityIcon = /*#__PURE__*/ createLucideIcon("university", registry.university);

/** Renders the "unlink" icon from https://lucide.dev/icons/unlink. */
export const UnlinkIcon = /*#__PURE__*/ createLucideIcon("unlink", registry.unlink);

/** Renders the "unlink-2" icon from https://lucide.dev/icons/unlink-2. */
export const Unlink2Icon = /*#__PURE__*/ createLucideIcon("unlink-2", registry.unlink2);

/** Renders the "unplug" icon from https://lucide.dev/icons/unplug. */
export const UnplugIcon = /*#__PURE__*/ createLucideIcon("unplug", registry.unplug);

/** Renders the "upload" icon from https://lucide.dev/icons/upload. */
export const UploadIcon = /*#__PURE__*/ createLucideIcon("upload", registry.upload);

/** Renders the "usb" icon from https://lucide.dev/icons/usb. */
export const UsbIcon = /*#__PURE__*/ createLucideIcon("usb", registry.usb);

/** Renders the "usb-c-port" icon from https://lucide.dev/icons/usb-c-port. */
export const UsbCPortIcon = /*#__PURE__*/ createLucideIcon("usb-c-port", registry.usbCPort);

/** Renders the "user" icon from https://lucide.dev/icons/user. */
export const UserIcon = /*#__PURE__*/ createLucideIcon("user", registry.user);

/** Renders the "user-check" icon from https://lucide.dev/icons/user-check. */
export const UserCheckIcon = /*#__PURE__*/ createLucideIcon("user-check", registry.userCheck);

/** Renders the "user-cog" icon from https://lucide.dev/icons/user-cog. */
export const UserCogIcon = /*#__PURE__*/ createLucideIcon("user-cog", registry.userCog);

/** Renders the "user-group" icon from https://lucide.dev/icons/user-group. */
export const UserGroupIcon = /*#__PURE__*/ createLucideIcon("user-group", registry.userGroup);

/** Renders the "user-key" icon from https://lucide.dev/icons/user-key. */
export const UserKeyIcon = /*#__PURE__*/ createLucideIcon("user-key", registry.userKey);

/** Renders the "user-lock" icon from https://lucide.dev/icons/user-lock. */
export const UserLockIcon = /*#__PURE__*/ createLucideIcon("user-lock", registry.userLock);

/** Renders the "user-minus" icon from https://lucide.dev/icons/user-minus. */
export const UserMinusIcon = /*#__PURE__*/ createLucideIcon("user-minus", registry.userMinus);

/** Renders the "user-pen" icon from https://lucide.dev/icons/user-pen. */
export const UserPenIcon = /*#__PURE__*/ createLucideIcon("user-pen", registry.userPen);

/** Renders the "user-plus" icon from https://lucide.dev/icons/user-plus. */
export const UserPlusIcon = /*#__PURE__*/ createLucideIcon("user-plus", registry.userPlus);

/** Renders the "user-round" icon from https://lucide.dev/icons/user-round. */
export const UserRoundIcon = /*#__PURE__*/ createLucideIcon("user-round", registry.userRound);

/** Renders the "user-round-arrow-left" icon from https://lucide.dev/icons/user-round-arrow-left. */
export const UserRoundArrowLeftIcon = /*#__PURE__*/ createLucideIcon(
	"user-round-arrow-left",
	registry.userRoundArrowLeft,
);

/** Renders the "user-round-check" icon from https://lucide.dev/icons/user-round-check. */
export const UserRoundCheckIcon = /*#__PURE__*/ createLucideIcon(
	"user-round-check",
	registry.userRoundCheck,
);

/** Renders the "user-round-cog" icon from https://lucide.dev/icons/user-round-cog. */
export const UserRoundCogIcon = /*#__PURE__*/ createLucideIcon(
	"user-round-cog",
	registry.userRoundCog,
);

/** Renders the "user-round-group" icon from https://lucide.dev/icons/user-round-group. */
export const UserRoundGroupIcon = /*#__PURE__*/ createLucideIcon(
	"user-round-group",
	registry.userRoundGroup,
);

/** Renders the "user-round-key" icon from https://lucide.dev/icons/user-round-key. */
export const UserRoundKeyIcon = /*#__PURE__*/ createLucideIcon(
	"user-round-key",
	registry.userRoundKey,
);

/** Renders the "user-round-minus" icon from https://lucide.dev/icons/user-round-minus. */
export const UserRoundMinusIcon = /*#__PURE__*/ createLucideIcon(
	"user-round-minus",
	registry.userRoundMinus,
);

/** Renders the "user-round-pen" icon from https://lucide.dev/icons/user-round-pen. */
export const UserRoundPenIcon = /*#__PURE__*/ createLucideIcon(
	"user-round-pen",
	registry.userRoundPen,
);

/** Renders the "user-round-plus" icon from https://lucide.dev/icons/user-round-plus. */
export const UserRoundPlusIcon = /*#__PURE__*/ createLucideIcon(
	"user-round-plus",
	registry.userRoundPlus,
);

/** Renders the "user-round-search" icon from https://lucide.dev/icons/user-round-search. */
export const UserRoundSearchIcon = /*#__PURE__*/ createLucideIcon(
	"user-round-search",
	registry.userRoundSearch,
);

/** Renders the "user-round-star" icon from https://lucide.dev/icons/user-round-star. */
export const UserRoundStarIcon = /*#__PURE__*/ createLucideIcon(
	"user-round-star",
	registry.userRoundStar,
);

/** Renders the "user-round-x" icon from https://lucide.dev/icons/user-round-x. */
export const UserRoundXIcon = /*#__PURE__*/ createLucideIcon("user-round-x", registry.userRoundX);

/** Renders the "user-search" icon from https://lucide.dev/icons/user-search. */
export const UserSearchIcon = /*#__PURE__*/ createLucideIcon("user-search", registry.userSearch);

/** Renders the "user-shield" icon from https://lucide.dev/icons/user-shield. */
export const UserShieldIcon = /*#__PURE__*/ createLucideIcon("user-shield", registry.userShield);

/** Renders the "user-star" icon from https://lucide.dev/icons/user-star. */
export const UserStarIcon = /*#__PURE__*/ createLucideIcon("user-star", registry.userStar);

/** Renders the "user-x" icon from https://lucide.dev/icons/user-x. */
export const UserXIcon = /*#__PURE__*/ createLucideIcon("user-x", registry.userX);

/** Renders the "users" icon from https://lucide.dev/icons/users. */
export const UsersIcon = /*#__PURE__*/ createLucideIcon("users", registry.users);

/** Renders the "users-collaboration" icon from https://lucide.dev/icons/users-collaboration. */
export const UsersCollaborationIcon = /*#__PURE__*/ createLucideIcon(
	"users-collaboration",
	registry.usersCollaboration,
);

/** Renders the "users-round" icon from https://lucide.dev/icons/users-round. */
export const UsersRoundIcon = /*#__PURE__*/ createLucideIcon("users-round", registry.usersRound);

/** Renders the "users-round-collaboration" icon from https://lucide.dev/icons/users-round-collaboration. */
export const UsersRoundCollaborationIcon = /*#__PURE__*/ createLucideIcon(
	"users-round-collaboration",
	registry.usersRoundCollaboration,
);

/** Renders the "utensils" icon from https://lucide.dev/icons/utensils. */
export const UtensilsIcon = /*#__PURE__*/ createLucideIcon("utensils", registry.utensils);

/** Renders the "utensils-crossed" icon from https://lucide.dev/icons/utensils-crossed. */
export const UtensilsCrossedIcon = /*#__PURE__*/ createLucideIcon(
	"utensils-crossed",
	registry.utensilsCrossed,
);

/** Renders the "utility-pole" icon from https://lucide.dev/icons/utility-pole. */
export const UtilityPoleIcon = /*#__PURE__*/ createLucideIcon("utility-pole", registry.utilityPole);

/** Renders the "van" icon from https://lucide.dev/icons/van. */
export const VanIcon = /*#__PURE__*/ createLucideIcon("van", registry.van);

/** Renders the "variable" icon from https://lucide.dev/icons/variable. */
export const VariableIcon = /*#__PURE__*/ createLucideIcon("variable", registry.variable);

/** Renders the "vault" icon from https://lucide.dev/icons/vault. */
export const VaultIcon = /*#__PURE__*/ createLucideIcon("vault", registry.vault);

/** Renders the "vector-polygon" icon from https://lucide.dev/icons/vector-polygon. */
export const VectorPolygonIcon = /*#__PURE__*/ createLucideIcon(
	"vector-polygon",
	registry.vectorPolygon,
);

/** Renders the "vector-square" icon from https://lucide.dev/icons/vector-square. */
export const VectorSquareIcon = /*#__PURE__*/ createLucideIcon(
	"vector-square",
	registry.vectorSquare,
);

/** Renders the "vegan" icon from https://lucide.dev/icons/vegan. */
export const VeganIcon = /*#__PURE__*/ createLucideIcon("vegan", registry.vegan);

/** Renders the "venetian-mask" icon from https://lucide.dev/icons/venetian-mask. */
export const VenetianMaskIcon = /*#__PURE__*/ createLucideIcon(
	"venetian-mask",
	registry.venetianMask,
);

/** Renders the "venus" icon from https://lucide.dev/icons/venus. */
export const VenusIcon = /*#__PURE__*/ createLucideIcon("venus", registry.venus);

/** Renders the "venus-and-mars" icon from https://lucide.dev/icons/venus-and-mars. */
export const VenusAndMarsIcon = /*#__PURE__*/ createLucideIcon(
	"venus-and-mars",
	registry.venusAndMars,
);

/** Renders the "vibrate" icon from https://lucide.dev/icons/vibrate. */
export const VibrateIcon = /*#__PURE__*/ createLucideIcon("vibrate", registry.vibrate);

/** Renders the "vibrate-off" icon from https://lucide.dev/icons/vibrate-off. */
export const VibrateOffIcon = /*#__PURE__*/ createLucideIcon("vibrate-off", registry.vibrateOff);

/** Renders the "video" icon from https://lucide.dev/icons/video. */
export const VideoIcon = /*#__PURE__*/ createLucideIcon("video", registry.video);

/** Renders the "video-off" icon from https://lucide.dev/icons/video-off. */
export const VideoOffIcon = /*#__PURE__*/ createLucideIcon("video-off", registry.videoOff);

/** Renders the "videotape" icon from https://lucide.dev/icons/videotape. */
export const VideotapeIcon = /*#__PURE__*/ createLucideIcon("videotape", registry.videotape);

/** Renders the "view" icon from https://lucide.dev/icons/view. */
export const ViewIcon = /*#__PURE__*/ createLucideIcon("view", registry.view);

/** Renders the "virus" icon from https://lucide.dev/icons/virus. */
export const VirusIcon = /*#__PURE__*/ createLucideIcon("virus", registry.virus);

/** Renders the "virus-off" icon from https://lucide.dev/icons/virus-off. */
export const VirusOffIcon = /*#__PURE__*/ createLucideIcon("virus-off", registry.virusOff);

/** Renders the "voicemail" icon from https://lucide.dev/icons/voicemail. */
export const VoicemailIcon = /*#__PURE__*/ createLucideIcon("voicemail", registry.voicemail);

/** Renders the "volleyball" icon from https://lucide.dev/icons/volleyball. */
export const VolleyballIcon = /*#__PURE__*/ createLucideIcon("volleyball", registry.volleyball);

/** Renders the "volume" icon from https://lucide.dev/icons/volume. */
export const VolumeIcon = /*#__PURE__*/ createLucideIcon("volume", registry.volume);

/** Renders the "volume-1" icon from https://lucide.dev/icons/volume-1. */
export const Volume1Icon = /*#__PURE__*/ createLucideIcon("volume-1", registry.volume1);

/** Renders the "volume-2" icon from https://lucide.dev/icons/volume-2. */
export const Volume2Icon = /*#__PURE__*/ createLucideIcon("volume-2", registry.volume2);

/** Renders the "volume-off" icon from https://lucide.dev/icons/volume-off. */
export const VolumeOffIcon = /*#__PURE__*/ createLucideIcon("volume-off", registry.volumeOff);

/** Renders the "volume-x" icon from https://lucide.dev/icons/volume-x. */
export const VolumeXIcon = /*#__PURE__*/ createLucideIcon("volume-x", registry.volumeX);

/** Renders the "vote" icon from https://lucide.dev/icons/vote. */
export const VoteIcon = /*#__PURE__*/ createLucideIcon("vote", registry.vote);

/** Renders the "wallet" icon from https://lucide.dev/icons/wallet. */
export const WalletIcon = /*#__PURE__*/ createLucideIcon("wallet", registry.wallet);

/** Renders the "wallet-cards" icon from https://lucide.dev/icons/wallet-cards. */
export const WalletCardsIcon = /*#__PURE__*/ createLucideIcon("wallet-cards", registry.walletCards);

/** Renders the "wallet-minimal" icon from https://lucide.dev/icons/wallet-minimal. */
export const WalletMinimalIcon = /*#__PURE__*/ createLucideIcon(
	"wallet-minimal",
	registry.walletMinimal,
);

/** Renders the "wallpaper" icon from https://lucide.dev/icons/wallpaper. */
export const WallpaperIcon = /*#__PURE__*/ createLucideIcon("wallpaper", registry.wallpaper);

/** Renders the "wand" icon from https://lucide.dev/icons/wand. */
export const WandIcon = /*#__PURE__*/ createLucideIcon("wand", registry.wand);

/** Renders the "wand-sparkles" icon from https://lucide.dev/icons/wand-sparkles. */
export const WandSparklesIcon = /*#__PURE__*/ createLucideIcon(
	"wand-sparkles",
	registry.wandSparkles,
);

/** Renders the "warehouse" icon from https://lucide.dev/icons/warehouse. */
export const WarehouseIcon = /*#__PURE__*/ createLucideIcon("warehouse", registry.warehouse);

/** Renders the "washing-machine" icon from https://lucide.dev/icons/washing-machine. */
export const WashingMachineIcon = /*#__PURE__*/ createLucideIcon(
	"washing-machine",
	registry.washingMachine,
);

/** Renders the "watch" icon from https://lucide.dev/icons/watch. */
export const WatchIcon = /*#__PURE__*/ createLucideIcon("watch", registry.watch);

/** Renders the "waves-arrow-down" icon from https://lucide.dev/icons/waves-arrow-down. */
export const WavesArrowDownIcon = /*#__PURE__*/ createLucideIcon(
	"waves-arrow-down",
	registry.wavesArrowDown,
);

/** Renders the "waves-arrow-up" icon from https://lucide.dev/icons/waves-arrow-up. */
export const WavesArrowUpIcon = /*#__PURE__*/ createLucideIcon(
	"waves-arrow-up",
	registry.wavesArrowUp,
);

/** Renders the "waves-horizontal" icon from https://lucide.dev/icons/waves-horizontal. */
export const WavesHorizontalIcon = /*#__PURE__*/ createLucideIcon(
	"waves-horizontal",
	registry.wavesHorizontal,
);

/** Renders the "waves-ladder" icon from https://lucide.dev/icons/waves-ladder. */
export const WavesLadderIcon = /*#__PURE__*/ createLucideIcon("waves-ladder", registry.wavesLadder);

/** Renders the "waves-vertical" icon from https://lucide.dev/icons/waves-vertical. */
export const WavesVerticalIcon = /*#__PURE__*/ createLucideIcon(
	"waves-vertical",
	registry.wavesVertical,
);

/** Renders the "waypoints" icon from https://lucide.dev/icons/waypoints. */
export const WaypointsIcon = /*#__PURE__*/ createLucideIcon("waypoints", registry.waypoints);

/** Renders the "webcam" icon from https://lucide.dev/icons/webcam. */
export const WebcamIcon = /*#__PURE__*/ createLucideIcon("webcam", registry.webcam);

/** Renders the "webcam-off" icon from https://lucide.dev/icons/webcam-off. */
export const WebcamOffIcon = /*#__PURE__*/ createLucideIcon("webcam-off", registry.webcamOff);

/** Renders the "webhook" icon from https://lucide.dev/icons/webhook. */
export const WebhookIcon = /*#__PURE__*/ createLucideIcon("webhook", registry.webhook);

/** Renders the "webhook-off" icon from https://lucide.dev/icons/webhook-off. */
export const WebhookOffIcon = /*#__PURE__*/ createLucideIcon("webhook-off", registry.webhookOff);

/** Renders the "weight" icon from https://lucide.dev/icons/weight. */
export const WeightIcon = /*#__PURE__*/ createLucideIcon("weight", registry.weight);

/** Renders the "weight-tilde" icon from https://lucide.dev/icons/weight-tilde. */
export const WeightTildeIcon = /*#__PURE__*/ createLucideIcon("weight-tilde", registry.weightTilde);

/** Renders the "wheat" icon from https://lucide.dev/icons/wheat. */
export const WheatIcon = /*#__PURE__*/ createLucideIcon("wheat", registry.wheat);

/** Renders the "wheat-off" icon from https://lucide.dev/icons/wheat-off. */
export const WheatOffIcon = /*#__PURE__*/ createLucideIcon("wheat-off", registry.wheatOff);

/** Renders the "whistle" icon from https://lucide.dev/icons/whistle. */
export const WhistleIcon = /*#__PURE__*/ createLucideIcon("whistle", registry.whistle);

/** Renders the "whole-word" icon from https://lucide.dev/icons/whole-word. */
export const WholeWordIcon = /*#__PURE__*/ createLucideIcon("whole-word", registry.wholeWord);

/** Renders the "wifi" icon from https://lucide.dev/icons/wifi. */
export const WifiIcon = /*#__PURE__*/ createLucideIcon("wifi", registry.wifi);

/** Renders the "wifi-cog" icon from https://lucide.dev/icons/wifi-cog. */
export const WifiCogIcon = /*#__PURE__*/ createLucideIcon("wifi-cog", registry.wifiCog);

/** Renders the "wifi-high" icon from https://lucide.dev/icons/wifi-high. */
export const WifiHighIcon = /*#__PURE__*/ createLucideIcon("wifi-high", registry.wifiHigh);

/** Renders the "wifi-lock" icon from https://lucide.dev/icons/wifi-lock. */
export const WifiLockIcon = /*#__PURE__*/ createLucideIcon("wifi-lock", registry.wifiLock);

/** Renders the "wifi-low" icon from https://lucide.dev/icons/wifi-low. */
export const WifiLowIcon = /*#__PURE__*/ createLucideIcon("wifi-low", registry.wifiLow);

/** Renders the "wifi-off" icon from https://lucide.dev/icons/wifi-off. */
export const WifiOffIcon = /*#__PURE__*/ createLucideIcon("wifi-off", registry.wifiOff);

/** Renders the "wifi-pen" icon from https://lucide.dev/icons/wifi-pen. */
export const WifiPenIcon = /*#__PURE__*/ createLucideIcon("wifi-pen", registry.wifiPen);

/** Renders the "wifi-sync" icon from https://lucide.dev/icons/wifi-sync. */
export const WifiSyncIcon = /*#__PURE__*/ createLucideIcon("wifi-sync", registry.wifiSync);

/** Renders the "wifi-zero" icon from https://lucide.dev/icons/wifi-zero. */
export const WifiZeroIcon = /*#__PURE__*/ createLucideIcon("wifi-zero", registry.wifiZero);

/** Renders the "wind" icon from https://lucide.dev/icons/wind. */
export const WindIcon = /*#__PURE__*/ createLucideIcon("wind", registry.wind);

/** Renders the "wind-arrow-down" icon from https://lucide.dev/icons/wind-arrow-down. */
export const WindArrowDownIcon = /*#__PURE__*/ createLucideIcon(
	"wind-arrow-down",
	registry.windArrowDown,
);

/** Renders the "wind-arrow-up" icon from https://lucide.dev/icons/wind-arrow-up. */
export const WindArrowUpIcon = /*#__PURE__*/ createLucideIcon(
	"wind-arrow-up",
	registry.windArrowUp,
);

/** Renders the "wine" icon from https://lucide.dev/icons/wine. */
export const WineIcon = /*#__PURE__*/ createLucideIcon("wine", registry.wine);

/** Renders the "wine-off" icon from https://lucide.dev/icons/wine-off. */
export const WineOffIcon = /*#__PURE__*/ createLucideIcon("wine-off", registry.wineOff);

/** Renders the "workflow" icon from https://lucide.dev/icons/workflow. */
export const WorkflowIcon = /*#__PURE__*/ createLucideIcon("workflow", registry.workflow);

/** Renders the "worm" icon from https://lucide.dev/icons/worm. */
export const WormIcon = /*#__PURE__*/ createLucideIcon("worm", registry.worm);

/** Renders the "wrench" icon from https://lucide.dev/icons/wrench. */
export const WrenchIcon = /*#__PURE__*/ createLucideIcon("wrench", registry.wrench);

/** Renders the "wrench-off" icon from https://lucide.dev/icons/wrench-off. */
export const WrenchOffIcon = /*#__PURE__*/ createLucideIcon("wrench-off", registry.wrenchOff);

/** Renders the "x" icon from https://lucide.dev/icons/x. */
export const XIcon = /*#__PURE__*/ createLucideIcon("x", registry.x);

/** Renders the "x-line-top" icon from https://lucide.dev/icons/x-line-top. */
export const XLineTopIcon = /*#__PURE__*/ createLucideIcon("x-line-top", registry.xLineTop);

/** Renders the "zap" icon from https://lucide.dev/icons/zap. */
export const ZapIcon = /*#__PURE__*/ createLucideIcon("zap", registry.zap);

/** Renders the "zap-off" icon from https://lucide.dev/icons/zap-off. */
export const ZapOffIcon = /*#__PURE__*/ createLucideIcon("zap-off", registry.zapOff);

/** Renders the "zodiac-aquarius" icon from https://lucide.dev/icons/zodiac-aquarius. */
export const ZodiacAquariusIcon = /*#__PURE__*/ createLucideIcon(
	"zodiac-aquarius",
	registry.zodiacAquarius,
);

/** Renders the "zodiac-aries" icon from https://lucide.dev/icons/zodiac-aries. */
export const ZodiacAriesIcon = /*#__PURE__*/ createLucideIcon("zodiac-aries", registry.zodiacAries);

/** Renders the "zodiac-cancer" icon from https://lucide.dev/icons/zodiac-cancer. */
export const ZodiacCancerIcon = /*#__PURE__*/ createLucideIcon(
	"zodiac-cancer",
	registry.zodiacCancer,
);

/** Renders the "zodiac-capricorn" icon from https://lucide.dev/icons/zodiac-capricorn. */
export const ZodiacCapricornIcon = /*#__PURE__*/ createLucideIcon(
	"zodiac-capricorn",
	registry.zodiacCapricorn,
);

/** Renders the "zodiac-gemini" icon from https://lucide.dev/icons/zodiac-gemini. */
export const ZodiacGeminiIcon = /*#__PURE__*/ createLucideIcon(
	"zodiac-gemini",
	registry.zodiacGemini,
);

/** Renders the "zodiac-leo" icon from https://lucide.dev/icons/zodiac-leo. */
export const ZodiacLeoIcon = /*#__PURE__*/ createLucideIcon("zodiac-leo", registry.zodiacLeo);

/** Renders the "zodiac-libra" icon from https://lucide.dev/icons/zodiac-libra. */
export const ZodiacLibraIcon = /*#__PURE__*/ createLucideIcon("zodiac-libra", registry.zodiacLibra);

/** Renders the "zodiac-ophiuchus" icon from https://lucide.dev/icons/zodiac-ophiuchus. */
export const ZodiacOphiuchusIcon = /*#__PURE__*/ createLucideIcon(
	"zodiac-ophiuchus",
	registry.zodiacOphiuchus,
);

/** Renders the "zodiac-pisces" icon from https://lucide.dev/icons/zodiac-pisces. */
export const ZodiacPiscesIcon = /*#__PURE__*/ createLucideIcon(
	"zodiac-pisces",
	registry.zodiacPisces,
);

/** Renders the "zodiac-sagittarius" icon from https://lucide.dev/icons/zodiac-sagittarius. */
export const ZodiacSagittariusIcon = /*#__PURE__*/ createLucideIcon(
	"zodiac-sagittarius",
	registry.zodiacSagittarius,
);

/** Renders the "zodiac-scorpio" icon from https://lucide.dev/icons/zodiac-scorpio. */
export const ZodiacScorpioIcon = /*#__PURE__*/ createLucideIcon(
	"zodiac-scorpio",
	registry.zodiacScorpio,
);

/** Renders the "zodiac-taurus" icon from https://lucide.dev/icons/zodiac-taurus. */
export const ZodiacTaurusIcon = /*#__PURE__*/ createLucideIcon(
	"zodiac-taurus",
	registry.zodiacTaurus,
);

/** Renders the "zodiac-virgo" icon from https://lucide.dev/icons/zodiac-virgo. */
export const ZodiacVirgoIcon = /*#__PURE__*/ createLucideIcon("zodiac-virgo", registry.zodiacVirgo);

/** Renders the "zoom-in" icon from https://lucide.dev/icons/zoom-in. */
export const ZoomInIcon = /*#__PURE__*/ createLucideIcon("zoom-in", registry.zoomIn);

/** Renders the "zoom-out" icon from https://lucide.dev/icons/zoom-out. */
export const ZoomOutIcon = /*#__PURE__*/ createLucideIcon("zoom-out", registry.zoomOut);
